import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PETS_BUNDLE_ENABLED, PETS_TITLE } from './companionBuild.js';
import { useCompanionSessions } from './useCompanionSessions.js';
import { usePetSetupHandoff } from './usePetSetupHandoff.js';
import { petSetupReplyCompleted, showsPetSetupReview } from './petSetupHandoff.js';
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { ChatMarkdown as Markdown } from './ChatMarkdown.js';
import { guideAppearance, openUserGuide } from './userGuide.js';
import { clipAtWord, markdownPlainText } from './chatMarkdownParser.js';
import { HeaderOverflowMenu } from './HeaderOverflowMenu.js';
import { measureHeaderOverflow } from './headerOverflow.js';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, version as reactVersion } from 'react';
import AppWindow from 'lucide-react/dist/esm/icons/app-window.mjs';
import AudioLines from 'lucide-react/dist/esm/icons/audio-lines.mjs';
import Bell from 'lucide-react/dist/esm/icons/bell.mjs';
import BellOff from 'lucide-react/dist/esm/icons/bell-off.mjs';
import Bookmark from 'lucide-react/dist/esm/icons/bookmark.mjs';
import Brain from 'lucide-react/dist/esm/icons/brain.mjs';
import Bug from 'lucide-react/dist/esm/icons/bug.mjs';
import Camera from 'lucide-react/dist/esm/icons/camera.mjs';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.mjs';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.mjs';
import Clock from 'lucide-react/dist/esm/icons/clock.mjs';
import Command from 'lucide-react/dist/esm/icons/command.mjs';
import Compass from 'lucide-react/dist/esm/icons/compass.mjs';
import Copy from 'lucide-react/dist/esm/icons/copy.mjs';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link.mjs';
import FileText from 'lucide-react/dist/esm/icons/file-text.mjs';
import GitFork from 'lucide-react/dist/esm/icons/git-fork.mjs';
import LogOut from 'lucide-react/dist/esm/icons/log-out.mjs';
import Maximize2 from 'lucide-react/dist/esm/icons/maximize-2.mjs';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square.mjs';
import Mic from 'lucide-react/dist/esm/icons/mic.mjs';
import Minimize2 from 'lucide-react/dist/esm/icons/minimize-2.mjs';
import Minus from 'lucide-react/dist/esm/icons/minus.mjs';
import Monitor from 'lucide-react/dist/esm/icons/monitor.mjs';
import MoreHorizontal from 'lucide-react/dist/esm/icons/ellipsis.mjs';
import PanelLeft from 'lucide-react/dist/esm/icons/panel-left.mjs';
import Paperclip from 'lucide-react/dist/esm/icons/paperclip.mjs';
import PictureInPicture2 from 'lucide-react/dist/esm/icons/picture-in-picture-2.mjs';
import Plus from 'lucide-react/dist/esm/icons/plus.mjs';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw.mjs';
import Search from 'lucide-react/dist/esm/icons/search.mjs';
import Send from 'lucide-react/dist/esm/icons/send.mjs';
import Shirt from 'lucide-react/dist/esm/icons/shirt.mjs';
import Share2 from 'lucide-react/dist/esm/icons/share-2.mjs';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.mjs';
import Square from 'lucide-react/dist/esm/icons/square.mjs';
import ThumbsDown from 'lucide-react/dist/esm/icons/thumbs-down.mjs';
import ThumbsUp from 'lucide-react/dist/esm/icons/thumbs-up.mjs';
import Undo2 from 'lucide-react/dist/esm/icons/undo-2.mjs';
import Volume2 from 'lucide-react/dist/esm/icons/volume-2.mjs';
import VolumeX from 'lucide-react/dist/esm/icons/volume-x.mjs';
import X from 'lucide-react/dist/esm/icons/x.mjs';
import { playAudioBlob, stopPlayback, primeAudio } from './audio.js';
import { ReplySpeech } from './replySpeech.js';
import { askFailureMessage, errorReplyText, failedAskReply, fileReadFailureMessage } from './askFailure.js';
import { BROWSER_DICTATION_STOP_TIMEOUT_MS, NATIVE_DICTATION_STOP_TIMEOUT_MS, SpeechInput, VOICE_INPUT_OFF, isDesktopShell, isOfficePane, voiceInputFor } from './speechInput.js';
import { canNotify, notificationApi, requestNotificationsFromGesture } from './notifications.js';
import { track } from './telemetry.js';
import { usageCapture } from './usage/featureEvents.js';
import { USAGE_SURFACE_EVENT, USAGE_SURFACE_READY_EVENT, clientEnvironment, parseUsageSurface, usageSurfaceFor } from './usage/environment.js';
import { signOutOfGyroWeb } from './webSignOut.js';
import { WebOutfitsWindow } from './WebOutfitsWindow.js';
import { widgetScreenFor, widgetScreenKey } from './usage/widgetScreens.js';
import { GYRO_REACT_VERSION } from './version.js';
import { GyroActionResponseError, MEMORY_MAX_CHARS } from './client.js';
import { parseHostHookMessage, createHookGate, isTrustedHostHookEvent, parseComposeText, parseSituationPrompt } from './hostHooks.js';
import { keyFromHostField } from './hostFields.js';
import { rankPalette } from './palette.js';
import { GOAL_USAGE, GOAL_USAGE_SHORT, goalResultNote, matchSlashCommand, parseGoalArgs, rankSlashCommands, slashCommandsFor, slashHelpNote, slashInput } from './slashCommands.js';
import { chatModel, chatRequestOptions, defaultThinkingLevel, modelMenuEntries, resolveModelChoice, thinkingLevels } from './modelChoice.js';
import { PAY_DATA_NOT_SHAREABLE_TEXT, shareReducer, SHARE_CONFIRM_TEXT, SHARE_TTL_DAYS } from './share.js';
import { ANONYMOUS_USER_KEY, clearPersistedState, persistedUserKey, readFloatingLayout, readPayChats, rememberPayChat, storageKeys, writeFloatingLayout } from './persistedState.js';
import { DAY_REFRESH_RETRY_MS, dayGreetingFromPersona, dayStatusDue, sameDayGreeting, welcomeHeading } from './dayGreeting.js';
import { PPP_CHIP_LABELS, PPP_EMAIL_TOOL, PPP_STARTER, parsePppMe, pppAvailable, pppChipIds, pppChipSentence, pppCopyBlock, pppMarksAfterTool, pppReportPath, pppShouldContinue } from './ppp.js';
import { usePppMe } from './usePppMe.js';
import { useGyroSkills } from './useGyroSkills.js';
import { SaveSkillBox, SaveSkillLink, saveSkillOffered } from './SaveSkill.js';
import { GyroSkillsPanel, skillsListable } from './GyroSkillsPanel.js';
import { InboxPoller, isLiveActionStatus, jsonEqual, mergeInboxActions } from './inboxPoll.js';
import { CLIENT_CONFIRM_TEXT, CONFIRM_AGAIN_UNUSABLE_TEXT, PREVIEW_CHANGED_TEXT, PREVIEW_EXPIRED_TEXT, TICKET_LAPSED_TEXT, approveSendsFirstConfirm, atConfirmStep, currentApprovalStep, isConfirmAgain, PERSON_SURFACE_APP, personSurfaceVendor, personWriteNotes, previewConnect, previewFields, previewKey, previewSummary, serverConfirmStep, singlePressConfirm, } from './approvalCard.js';
import { mcpActionSummary, mcpServerDisplayName, mcpServerLabel, shownMcpName } from './mcpDisplayNames.js';
import { PreviewFieldList } from './PreviewFieldList.js';
import { PersonConnectControl } from './PersonConnectControl.js';
import { ActionConversations, holdsAction } from './actionConversations.js';
import { usePersonConnect } from './usePersonConnect.js';
import { documentIsVisible, parseHostVisibility, surfaceOnScreen } from './visibility.js';
import { HistorySaveTracker, historyIdentityRetry, historySaveMessage, identityRefusal, newChatHelps } from './historySave.js';
import { ForkError } from './client.js';
import { TranscriptLoadGate, decideTranscriptLoad, savedTurns, transcriptFingerprint } from './transcriptLoad.js';
import { LAB_FILE_TOOL, LAB_UPLOAD_TOOLS, REPORT_MODE_IDLE_TURNS, describeUserAgent, fetchEvidenceUploader, filedReceiptText, formatReportContext, isBugReportRequest, parseAttachImages, parseFiledReport, parseReportContext, parseUploadIntent, selectReportScreenshots, uploadFailureReason, } from './bugReport.js';
import { OPEN_DRAFT_LABEL, draftOpenLink } from './draftLink.js';
import { SCREENSHOT_TARGET_BYTES, canCaptureTab, captureCurrentTab, normalizeForLab } from './screenshot.js';
import { ChatTitleEditor } from './ChatTitleEditor.js';
import { VoiceRecordingBar } from './VoiceRecordingBar.js';
import { startVoiceMeter, VOICE_METER_BARS } from './voiceMeter.js';
import { ConversationTitles, HistoryWriteQueue } from './conversationTitles.js';
import { chatOriginLabel } from './chatOrigin.js';
import { BLUEBEAM_UPLOAD_TOOL, bluebeamUploadLeg, nativeFiles, parseBluebeamUploadResult, parseVaultDownloadResult, relayableVaultUrl } from './bluebeamStudio.js';
import { BluebeamUploadLegCard, VaultDownloadControl } from './BluebeamStudioCards.js';
import { marksLookPicker } from './mascotLooks.js';
import { ModelMenuButton, ThinkingButton } from './ComposerModelControls.js';
import { knowledgeStatus } from './desktopKnowledge.js';
import { desktopWindowChrome, isRoomyWindowMode, parseDesktopWindowMode, windowToggle } from './desktopWindowMode.js';
import { LocalDocumentStore } from './localDocuments.js';
import { ConversationRuns } from './conversationRuns.js';
import LoaderCircle from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_COUNT, fileTypeLabel } from './fileMedia.js';
import { prepareAttachments } from './attachmentIntake.js';
import { appendDroppedText } from './dropContent.js';
import { OPEN_SETTINGS_EVENT, OPEN_SETTINGS_REQUEST_EVENT, parseHostSettingsRequest } from './hostSettingsRequest.js';
import { useGyroLook } from './useGyroLook.js';
import { browserLookStorage, lookStatusDue, outfitToShow, readLookMemory, writeLookMemory } from './gyroLook.js';
import { useSendScroll } from './useSendScroll.js';
import { questionsForReply } from './clarificationQuestions.js';
import { startTask, startTaskTool, finishTaskTool, interruptTask, finishTask, reconcileTaskActions } from './taskActivity.js';
import { toolActivityLabel, toolConnectorLabel } from './toolActivityLabels.js';
import { actionParametersAreFullyDisplayable, formatActionParameters } from './actionParameters.js';
import { ActionParameterDetails, ActionToolLine } from './ActionDetails.js';
import { CompactActivityRow, LiveTokenCounter, SessionUsageLine, compactRowUsage } from './TokenCounter.js';
import { ConversationUsage, EMPTY_SESSION_USAGE, finishedTurnUsage, turnUsageFromLive, withChatUsage } from './tokenUsage.js';
import { DEFAULT_DESKTOP_PREFERENCES, desktopPreferencesKey, normalizeDesktopPreferences, readDesktopPreferences, saveDesktopPreferences } from './desktopPreferences.js';
import { acceptHostSupportCards, applyClaimOutcome, claimOutcomeFromResponse, parseSupportCardsDetail, retainSupportOutcomes } from './supportCards.js';
import { PulseCard, PulseThumbBox } from './pulse/PulseCard.js';
import { PULSE_PANE_SETTLED_EVENT, defaultPulseChips, paneToolSummary, parsePulseToolSettled, pulseCardDecision, pulseCardStore, pulseChipKey, pulseIntentKey, pulseStatus as parsePulseStatus } from './pulse/pulseCards.js';
import { FLOATING_FULL_SCREEN_QUERY, HOST_THEME_WATCHED_ATTRIBUTES, confirmLimitsFor, isLocalProviderAction, isOriginalLocalFileTask, coveredBy, documentOutputsFor, executingLabelFor, floatingSidebarLayout, floatingWorkspaceFor, hostPageTheme, layoutChrome as layoutChromeFor, parseWidgetTheme, workspaceLookFor } from './paneHost.js';
import { useAttentionIntegration } from './attention/useAttentionIntegration.js';
import { AutoSaveContext } from './autoSaveContext.js';
import { previewPayloadSource, useHtmlPreview } from './useHtmlPreview.js';
import { attachmentSourceId, codeSourceId, previewTitle, versionOptions } from './htmlPreview.js';
import { sealedFrameUrl } from './sealedFrameSession.js';
import { PREVIEW_TAB_ID, SIDE_PANEL_MIN_SURFACE_WIDTH, SIDE_PANEL_RESTORE_EVENT, SIDE_PANEL_WIDEN_EVENT, SidePanelContext, sidePanelSurface, sidePanelWindowWidth, useSidePanelHost } from './sidePanel.js';
function parseLabPreview(value) {
    const v = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const text = (x, max = 200) => (typeof x === 'string' && x.trim() ? x.trim().slice(0, max) : undefined);
    const filedAs = v.filedAs && typeof v.filedAs === 'object' ? v.filedAs : undefined;
    const q = v.quota && typeof v.quota === 'object' ? v.quota : undefined;
    const n = (x) => (typeof x === 'number' && Number.isFinite(x) ? x : NaN);
    const quota = q && [q.openUsed, q.openMax, q.dayUsed, q.dayMax].every((x) => Number.isFinite(n(x)))
        ? { openUsed: n(q.openUsed), openMax: n(q.openMax), dayUsed: n(q.dayUsed), dayMax: n(q.dayMax) }
        : undefined;
    const name = text(filedAs?.name);
    const email = text(filedAs?.email);
    return {
        appTitle: text(v.appTitle),
        evidenceAudience: text(v.evidenceAudience, 300),
        filedAs: name && email ? `${name} (${email})` : name ?? email,
        quota,
    };
}
/** The reply patch a tool result adds in the desktop app: the outfit picker mark, for a successful `gyro_list_looks`. */
export function lookPickerReplyPatch(appKey, tool, ok) {
    return appKey === 'desktop' && marksLookPicker(tool, ok) ? { lookPicker: true } : null;
}
/**
 * Whether a chat has anything in it yet (StarScribe's export folder: a page beside an empty chat saves under
 * Documents\Gyro\StarScribe): the welcome greeting and a message still on its way do not count.
 */
export function chatHasMessages(messages) {
    return messages.some((message) => !message.welcome && !message.pending);
}
/** Whether a reply shows the outfit picker: the desktop app, and a finished assistant reply that carries the mark. */
export function showsLookPicker(appKey, message) {
    return appKey === 'desktop' && message.role === 'assistant' && message.lookPicker === true && !message.pending && !message.streaming && !message.error;
}
/**
 * A reply's own key for the state "Create my PPP" keeps per reply (Open PDF's counter, Copy as text's mark): the id its
 * files already go by (`replyId`, the time it was asked), so the state never moves to another reply with a list position.
 */
function pppReplyKey(message, index) {
    return `${message.startedAt ?? index}`;
}
/**
 * Open PDF's fallback (clients contract 6.4) for a reply whose 24-hour PDF answered 404: the path of the stored report's
 * PDF, or null. The export's own `durable` paths travel only in its tool result, which never reaches the widget, and
 * done.artifacts carries the 24-hour store's random task and file ids, so neither names the report. The server names the
 * export's files after the report's file stem (`<fileStem>.pdf`; one stored report per period), so /v1/ppp/me's
 * lastReport is taken only when its stem names this very PDF: a report exported since, for another period, is never saved
 * in its place. A widget host is told no lastReport (`verified: false`), so there it is always null.
 */
function pppDurablePdfPath(me, pdfName) {
    const last = me?.lastReport;
    return last && pdfName === `${last.fileStem}.pdf` ? pppReportPath(last.id, 'pdf') : null;
}
const DEFAULT_GREETING = "Hey, I'm **Gyro**. What are we working on? Bring me a question, a rough idea, or something that's stuck — we'll work through it together.";
const DEFAULT_SUGGESTIONS = ['What needs my attention right now?', 'What looks unhealthy and why?', 'Help me set something up.', 'Explain the safe-action flow.'];
/** Coming back on screen reads the chat list again at most this often, so a quick hide and show reads it once. */
const HISTORY_RESYNC_MS = 30_000;
/**
 * A suggestion that ends in an ellipsis ("Write a formula to…") is a start the person finishes: the composer draft it
 * leaves (the words, then a space). Null for a complete suggestion, which is sent as it is.
 */
export function suggestionDraft(suggestion) {
    const match = /^(.*\S)\s*(?:…|\.\.\.)$/u.exec(suggestion.trim());
    return match ? `${match[1]} ` : null;
}
/**
 * `inert` on any React: React 19 takes a boolean, React 18 passes a string attribute through (and drops `true` with a
 * warning; hosts such as Uplink run React 18). Off: no attribute.
 */
export function inertProps(on, version = reactVersion) {
    if (!on)
        return {};
    return Number.parseInt(version, 10) >= 19 ? { inert: true } : { inert: '' };
}
/** A layout effect in the browser (before paint); a plain effect, which does nothing, in a server render. */
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
function isPageBody(element) {
    return !element || element === document.body || element === document.documentElement;
}
/**
 * Whether the floating panel has the keyboard: focus is in it or on its launcher, or focus fell to the page's body (a click
 * on the panel's text, a control that went away) and the person's last pointer press or focus move was in Gyro.
 */
export function floatingPanelHasKeys(panel, launcher, lastMoveInside) {
    if (!panel)
        return false;
    const active = document.activeElement;
    if (isPageBody(active))
        return lastMoveInside;
    return panel.contains(active) || (launcher !== null && launcher.contains(active));
}
/** Whether focus is on a host control lying wholly under the open floating panel, where the person cannot see it. */
export function focusHiddenUnderPanel(panel, launcher) {
    const active = document.activeElement;
    if (!panel || isPageBody(active) || panel.contains(active) || active === launcher)
        return false;
    return coveredBy(active.getBoundingClientRect(), panel.getBoundingClientRect());
}
// Friendly names for the system a cross-app action runs in (the unified assistant labels actions by system).
const SYSTEM_LABELS = { orbit: 'Orbit', nebula: 'Nebula', bridge: 'The Bridge', graph: 'Microsoft 365', procore: 'Procore', autodesk: 'Autodesk ACC', 'the-lab': 'The Lab', office: 'Microsoft 365', teams: 'Teams', uplink: 'Uplink' };
const systemName = (k) => SYSTEM_LABELS[k] ?? k;
/** A system's label only when the widget knows it (the tool line never shows a raw app key as a name). */
const knownSystemName = (k) => (k && Object.hasOwn(SYSTEM_LABELS, k) ? SYSTEM_LABELS[k] : undefined);
/** The system an action card names: a person-surface action runs in the vendor its tool reaches (approvalCard.ts personSurfaceVendor). */
const actionSystemName = (action) => (action.app === PERSON_SURFACE_APP ? personSurfaceVendor(action.tool) : systemName(action.app ?? ''));
function capturePageContext() {
    try {
        const el = document.querySelector('[data-gyro-page]') || document.querySelector('main') || document.body;
        const text = (el?.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 2500);
        return `Current page: "${document.title}" (${location.pathname})\nVisible content:\n${text}`;
    }
    catch {
        return '';
    }
}
function looksSensitive(text) {
    return /\b(ssn|social security|payroll|salary|restricted|bank account|routing number)\b/i.test(text) || /\b\d{3}-?\d{2}-?\d{4}\b/.test(text);
}
function actionRecord(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
function normalizeActionReceipt(response) {
    const root = actionRecord(response) ?? {};
    const nested = actionRecord(root.result);
    const envelope = actionRecord(root.run) ? root : nested && actionRecord(nested.run) ? nested : null;
    const run = actionRecord(envelope?.run);
    const result = run ? run.result : envelope ? undefined : root.result;
    let text;
    if (run) {
        const durable = {
            status: run.status,
            errorMessage: run.errorMessage ?? root.message,
            result,
        };
        try {
            const serialized = JSON.stringify(durable, null, 2);
            text = serialized.length <= 65_536 ? serialized : `${serialized.slice(0, 65_536)}\n[receipt display truncated]`;
        }
        catch {
            text = '[receipt could not be displayed]';
        }
    }
    else if (result !== undefined) {
        try {
            const serialized = JSON.stringify(result, null, 2);
            text = serialized.length <= 65_536 ? serialized : `${serialized.slice(0, 65_536)}\n[receipt display truncated]`;
        }
        catch {
            text = '[receipt could not be displayed]';
        }
    }
    return {
        runStatus: typeof run?.status === 'string' ? run.status : undefined,
        receipt: text ? {
            text,
            runId: typeof run?.id === 'string' ? run.id : undefined,
            correlationId: typeof run?.correlationId === 'string' ? run.correlationId : undefined,
            implementationRevision: typeof run?.implementationRevision === 'string' ? run.implementationRevision : undefined,
        } : undefined,
        ephemeral: envelope?.ephemeralResult ?? root.ephemeralResult ?? nested?.ephemeralResult,
    };
}
export function classifyActionOutcome(response) {
    const root = actionRecord(response) ?? {};
    const normalized = normalizeActionReceipt(response);
    const reconciliationRequired = root.status === 'reconciliation-required'
        || root.reconciliationRequired === true
        || normalized.runStatus === 'reconciliation-required';
    const completed = root.status === 'done' && (!normalized.runStatus || normalized.runStatus === 'success');
    const submitted = root.status === 'submitted_for_approval';
    const executing = root.status === 'client-executing';
    const cancelled = root.status === 'cancelled';
    // Unknown or malformed success responses must not be re-labelled as a user cancellation.
    const failed = !reconciliationRequired && !completed && !submitted && !executing && !cancelled;
    const state = submitted
        ? 'awaiting-approval'
        : executing
            ? 'executing'
            : completed
                ? 'done'
                : reconciliationRequired
                    ? 'reconciliation-required'
                    : cancelled
                        ? 'cancelled'
                        : 'error';
    return { state, completed, failed, reconciliationRequired };
}
export function beginResultClaim(claimed, actionId, begin) {
    if (claimed.has(actionId) || !begin())
        return false;
    claimed.add(actionId);
    return true;
}
/**
 * Record one action's outcome line in a transcript: added once, replaced when it changed. A failure re-reported without
 * its reason never replaces a failure line that says why: the richer line stays (2026-10-12: the confirm answer's
 * "Failed - <reason>" became a bare "Failed - <summary>" on the inbox's next poll, which listed the action without one).
 */
export function upsertActionOutcomeMessage(previous, actionId, outcome) {
    const existing = previous.findIndex((message) => message.actionOutcomeId === actionId);
    if (existing < 0)
        return [...previous, outcome];
    // The inbox re-reports a terminal outcome on every poll; an identical one must not churn the transcript.
    if (jsonEqual(previous[existing], outcome))
        return previous;
    const current = previous[existing];
    if (outcome.error && !outcome.failureReason && current.error && current.failureReason)
        return previous;
    const next = [...previous];
    next[existing] = outcome;
    return next;
}
/** The card's own state line for a failed action: "Failed", and why when the confirm answer or the inbox said so. */
export function failedStateLine(reason) {
    const text = typeof reason === 'string' ? reason.trim() : '';
    return text ? `Failed: ${text}` : 'Failed';
}
// The confirm card's projection of an action's input (exact and readable views) lives in actionParameters.ts.
export { actionParametersAreFullyDisplayable, formatActionParameters };
export function governedActionFailureRequiresReconciliation(error, decision, requiresPreview) {
    if (decision !== 'confirm' || !requiresPreview)
        return false;
    return error instanceof GyroActionResponseError ? error.reconciliationRequired : true;
}
function vaultDownloadHandoff(value) {
    const result = actionRecord(value);
    if (!result)
        return null;
    const allowed = new Set(['kind', 'id', 'modelName', 'url', 'expiresAt', 'singleUse']);
    const keys = Object.keys(result);
    const now = Math.floor(Date.now() / 1000);
    let validUrl = false;
    if (typeof result.url === 'string') {
        if (/^\/api\/vault\/download\/[A-Za-z0-9_-]{43}$/.test(result.url))
            validUrl = true;
        else {
            try {
                const absolute = new URL(result.url);
                validUrl = absolute.protocol === 'https:'
                    && !absolute.username
                    && !absolute.password
                    && !absolute.search
                    && !absolute.hash
                    && /^\/api\/vault\/download\/[A-Za-z0-9_-]{43}$/.test(absolute.pathname);
            }
            catch {
                validUrl = false;
            }
        }
    }
    if (keys.length !== allowed.size
        || !keys.every((key) => allowed.has(key))
        || result.kind !== 'vault-download'
        || typeof result.id !== 'string'
        || result.id.length === 0
        || result.id.length > 200
        || typeof result.modelName !== 'string'
        || result.modelName.length === 0
        || result.modelName.length > 500
        || typeof result.url !== 'string'
        || !validUrl
        || !Number.isSafeInteger(result.expiresAt)
        || Number(result.expiresAt) <= now
        || Number(result.expiresAt) > now + 120
        || result.singleUse !== true)
        return null;
    return { id: result.id, modelName: result.modelName, url: result.url, expiresAt: Number(result.expiresAt) };
}
function forSpeech(s) {
    return s.replace(/```[\s\S]*?```/g, ' code block ').replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim();
}
function speak(text, onEnded) {
    if (!('speechSynthesis' in window))
        return false;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(forSpeech(text).slice(0, 700));
    const voices = window.speechSynthesis.getVoices();
    const pick = voices.find((v) => /Daniel|Google UK English Male|Microsoft (Ryan|George|Thomas|Guy|David|Mark)|Alex|Aaron/i.test(v.name)) ||
        voices.find((v) => v.lang === 'en-GB' && /male/i.test(v.name)) ||
        voices.find((v) => v.lang === 'en-GB') ||
        voices.find((v) => v.lang.startsWith('en'));
    if (pick)
        u.voice = pick;
    u.rate = 1.0;
    u.pitch = 0.7;
    u.onend = onEnded;
    u.onerror = onEnded;
    window.speechSynthesis.speak(u);
    return true;
}
function stopSpeaking() {
    if ('speechSynthesis' in window)
        window.speechSynthesis.cancel();
}
/**
 * Completion notification (OS toast) for an answer that finished while the tab was hidden or the
 * widget collapsed. This runs from a fetch/SSE completion — NOT a user gesture — so it must never ask
 * for permission (browsers ignore or penalize that, L-27): it notifies only when permission is already
 * granted and stays silent otherwise. Permission is requested solely from the bell control / palette
 * command / first-Send priming (see enableNotifications). Clicking the toast focuses the window + opens Gyro.
 */
function notifyAnswerDone(snippet, icon, onOpen) {
    try {
        if (!canNotify(notificationApi()))
            return;
        const n = new Notification(`Gyro finished: ${snippet}`, { tag: 'gyro-answer-done', icon });
        n.onclick = () => {
            try {
                window.focus();
            }
            catch {
                /* ignore */
            }
            onOpen();
            n.close();
        };
    }
    catch {
        /* Notification API unavailable (sandboxed iframe, embedded webview, …) — the badge still shows */
    }
}
/**
 * The widget, shared by both entries of the vendored package. GyroAssistantFull.tsx (`@exante/gyro-react`) renders it with
 * every part; GyroAssistantWeb.tsx (`@exante/gyro-react/web`) with the web panel's parts only, the rest inert. An instance's
 * parts never change, so neither do the hooks among them.
 */
export function GyroAssistantCore(props) {
    const { client, parts } = props;
    // The entry's parts, under the names of the components, hooks and helpers they stand for, so the code below reads the
    // same for both entries. The web entry's desktop, document and Office-pane parts are inert: it never renders them.
    const { DesktopSettings, DesktopAccountMenu, DesktopTaskPanel, LocalFilesMenu, ComposerAddMenu, DesktopCoworkBar, LocalFileContinueCard, PetsSidebar, PetsWorkspace, ChatMascotLookPicker, DesktopMascotLooks, DesktopKnowledgeBar, DesktopDropOverlay, DesktopNotifications, StreamingActivity, useDesktopDrop, useDesktopMcp, useDesktopSkills, useMascotAnimations, useMascotShareHide, useNotificationCenter, parseHostNotification, currentMcpStatus, LocalFileContinuations, LOCAL_FILE_EVIDENCE_UNSUPPORTED, continuationMessage, evidenceRefusalText, localFileEvidence, localFileEvidenceService, localFileOriginalRequest, parseDesktopActionResult, localActionFolders, localActionFoldersConfirmable, DocumentOutputs, generateDocument, DESKTOP_DOCUMENT_CAPABILITIES, detectDocumentRequest, resolveDocumentRequest, documentOutputContext, paneDocumentContext, shouldOfferExport, mountClientActionRunner, createClientActionRunner, browserClientActionLedger, clientActionLedgerKey, clientActionRefusalMessage, clientExecutingFromInbox, clientExecutingFromResponse, clientExecutingRefusal, ConversationContextMenu, ClarificationCard, SupportCard, GeneratedFiles, GyroSidePanel, HtmlPreviewHeader, HtmlPreviewBody, } = parts;
    // The Office pane's own tools and confirmed actions (paneHost.ts): only an entry that hosts them takes them.
    const clientTools = parts.paneHost ? props.clientTools : undefined;
    const clientActions = parts.paneHost ? props.clientActions : undefined;
    const appKey = props.appKey ?? 'gyro';
    // The workspace layout (navigation drawer, header, welcome, message list and composer): every host unless it asks for
    // desktopLayout="compact" (0.15.0): the desktop app, the Office pane and every web app's floating panel. The desktop's
    // features need the native shell around the page (local files and Cowork, window controls and placement, the agent's
    // notifications and settings, Work and World IQ, the task panel) and its turn runner, so they stay with the desktop app.
    const workspace = workspaceLookFor(props.desktopLayout);
    const desktopWorkspace = parts.desktop && workspace && appKey === 'desktop';
    // Gyro on the web (appKey "web", its own page on gyro.gyro360.ai; docs/PLAN-web-gyro-gyro360.md): the workspace with the
    // outfit gallery in a window of its own (WebOutfitsWindow.tsx) and a way to sign out, from the full entry only.
    const webPage = parts.desktop && workspace && appKey === 'web';
    // On a phone or tablet the web page does not put the cursor in the message box by itself: that raises the keyboard over
    // half the screen before the person asked for it (the iOS simulator showed it at load). Desktop, Office and embed keep it.
    const webTouchScreen = () => webPage && typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    // Bluebeam Studio's native halves (bluebeamStudio.ts): what the desktop shell relays, read once from its announcement. The
    // web and the Office pane relay nothing, so there the vault download stays a link and an upload's card is unchanged.
    const native = useMemo(() => (desktopWorkspace && isDesktopShell(typeof window === 'undefined' ? undefined : window)
        ? nativeFiles(window)
        : { vaultDownload: false, bluebeamUpload: false, autoSave: false }), [desktopWorkspace]);
    const nativeRef = useRef(native);
    nativeRef.current = native;
    // Every file Gyro creates is copied to Documents\Gyro\<chat name> by the desktop agent (owner, 2026-10-22;
    // desktopAutoSave.tsx): the page's side, made only where the desktop shell announces the copies.
    const autoSave = useMemo(() => (native.autoSave && typeof window !== 'undefined' ? parts.createAutoSave(window) : null), [native.autoSave, parts]);
    useEffect(() => () => autoSave?.dispose(), [autoSave]);
    const autoSaveRef = useRef(autoSave);
    autoSaveRef.current = autoSave;
    const [vaultResults, setVaultResults] = useState({});
    const [vaultRequested, setVaultRequested] = useState({});
    const [uploadResults, setUploadResults] = useState({});
    useEffect(() => {
        if (!native.vaultDownload && !native.bluebeamUpload)
            return;
        const onVault = (event) => {
            const result = parseVaultDownloadResult(event.detail);
            if (!result)
                return;
            setVaultResults((current) => ({ ...current, [result.id]: result }));
            setVaultRequested((current) => { if (!(result.id in current))
                return current; const next = { ...current }; delete next[result.id]; return next; });
        };
        const onUpload = (event) => {
            const result = parseBluebeamUploadResult(event.detail);
            if (result)
                setUploadResults((current) => ({ ...current, [result.runId]: result }));
        };
        window.addEventListener('gyro:vault-download-result', onVault);
        window.addEventListener('gyro:bluebeam-upload-result', onUpload);
        return () => {
            window.removeEventListener('gyro:vault-download-result', onVault);
            window.removeEventListener('gyro:bluebeam-upload-result', onUpload);
        };
    }, [native]);
    const requestVaultDownload = (download) => {
        setVaultRequested((current) => ({ ...current, [download.id]: 'download' }));
        window.dispatchEvent(new CustomEvent('gyro:vault-download', { detail: { id: download.id, url: download.url, fileName: download.modelName } }));
    };
    const requestVaultOpen = (id) => {
        setVaultRequested((current) => ({ ...current, [id]: 'open' }));
        window.dispatchEvent(new CustomEvent('gyro:vault-open', { detail: { id } }));
    };
    const requestBluebeamUpload = (leg) => {
        // The step shows "Choosing…" at once; the agent answers `picking` itself, then the outcome.
        setUploadResults((current) => ({ ...current, [leg.runId]: { runId: leg.runId, status: 'picking' } }));
        window.dispatchEvent(new CustomEvent('gyro:bluebeam-upload', { detail: { runId: leg.runId, fileName: leg.fileName, sizeBytes: leg.sizeBytes, sha256: leg.sha256, kind: leg.kind } }));
    };
    // The floating web panel (every web app): the only host that gets .gyro-floating, the full-screen and narrow/docked rules,
    // the drawer reset, the launcher look, the page's own theme, the key focus gate, the remembered Expand and the top-layer chat menu.
    const floatingWorkspace = floatingWorkspaceFor({ workspace, desktopWorkspace, layout: props.layout });
    // Identity key for everything the widget persists in this browser (M-09). Explicit `userKey` wins,
    // else the host's user id / email. A different key IS a different slate: the caches below are all
    // namespaced by it, and an in-place identity switch resets the in-memory conversation (see the
    // identity effect further down). Mirrored into a ref for the once-registered sign-out hook.
    const userKey = persistedUserKey(props.user, props.userKey);
    const notificationCenter = useNotificationCenter(appKey, userKey, desktopWorkspace);
    const [showNotifications, setShowNotifications] = useState(false);
    const [, setInboxSync] = useState({ loading: true, error: false, lastSyncedAt: null });
    const inboxActionsRef = useRef(new Map());
    const [inboxEpoch, setInboxEpoch] = useState(0);
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const receive = (event) => {
            const entry = parseHostNotification(event.detail);
            if (entry)
                notificationCenter.record(entry);
        };
        window.addEventListener('gyro:notification', receive);
        window.dispatchEvent(new CustomEvent('gyro:notification-ready'));
        return () => window.removeEventListener('gyro:notification', receive);
    }, [desktopWorkspace, notificationCenter.record]);
    const userKeyRef = useRef(userKey);
    userKeyRef.current = userKey;
    const preferenceKey = desktopPreferencesKey(appKey, userKey);
    const restoredPreferences = useMemo(() => readDesktopPreferences(desktopWorkspace ? desktopStorage() : null, preferenceKey), [desktopWorkspace, preferenceKey]);
    const [preferenceState, setPreferenceState] = useState({ key: preferenceKey, value: restoredPreferences });
    const preferences = preferenceState.key === preferenceKey ? preferenceState.value : restoredPreferences;
    const preferencesRef = useRef(preferences);
    preferencesRef.current = preferences;
    useEffect(() => {
        if (desktopWorkspace)
            window.dispatchEvent(new CustomEvent('gyro:guide-appearance', { detail: guideAppearance(preferences) }));
    }, [desktopWorkspace, preferences.theme, preferences.accent, preferences.chatFont]);
    const openGuide = useCallback(() => openUserGuide(guideAppearance(preferencesRef.current), '', client.baseUrl), [client]);
    const [preferenceStorageError, setPreferenceStorageError] = useState('');
    useEffect(() => {
        setPreferenceState({ key: preferenceKey, value: restoredPreferences });
        setPreferenceStorageError('');
    }, [preferenceKey, restoredPreferences]);
    const applyPreferences = useCallback((patch, notifyNative) => {
        const value = normalizeDesktopPreferences({ ...preferencesRef.current, ...patch });
        preferencesRef.current = value;
        setPreferenceState({ key: preferenceKey, value });
        setPreferenceStorageError(saveDesktopPreferences(desktopStorage(), preferenceKey, value) ? '' : 'Changes apply now, but could not be saved on this device.');
        if (notifyNative && desktopWorkspace && patch.windowPlacement !== undefined)
            window.dispatchEvent(new CustomEvent('gyro:window-placement-request', { detail: value.windowPlacement }));
    }, [desktopWorkspace, preferenceKey]);
    const updatePreferences = useCallback((patch) => applyPreferences(patch, true), [applyPreferences]);
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const receivePlacement = (event) => {
            const placement = event.detail;
            if (placement !== 'mascot' && placement !== 'left' && placement !== 'right' && placement !== 'free')
                return;
            if (preferencesRef.current.windowPlacement !== placement)
                applyPreferences({ windowPlacement: placement }, false);
        };
        window.addEventListener('gyro:window-placement', receivePlacement);
        window.dispatchEvent(new CustomEvent('gyro:window-placement-ready'));
        return () => window.removeEventListener('gyro:window-placement', receivePlacement);
    }, [desktopWorkspace, applyPreferences]);
    const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    useEffect(() => {
        if (!workspace)
            return;
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const change = () => setSystemDark(query.matches);
        query.addEventListener('change', change);
        return () => query.removeEventListener('change', change);
    }, [workspace]);
    // The desktop's own setting (dark unless the person changes it). The floating web panel follows the host page's own light
    // or dark ('auto': paneHost hostPageTheme, read below while it is open), else the system's; the Office pane follows the
    // page's light or dark scheme, as its own chrome does ('system'). A host's `theme` (its own switch's choice) wins.
    const themeChoice = desktopWorkspace ? preferences.theme : parseWidgetTheme(props.theme) ?? (floatingWorkspace ? 'auto' : 'system');
    const [pageTheme, setPageTheme] = useState(null);
    const systemTheme = systemDark ? 'dark' : 'light';
    const resolvedTheme = themeChoice === 'auto' ? pageTheme ?? systemTheme : themeChoice === 'system' ? systemTheme : themeChoice;
    const [settingsSection, setSettingsSection] = useState(null);
    const [taskPanelOpen, setTaskPanelOpen] = useState(false);
    const [composerWorkspace, setComposerWorkspace] = useState('chat');
    const [composerMenuOpen, setComposerMenuOpen] = useState(false);
    // "Add text-file context" asks the task panel to expand Working folder once: the panel spends the request when it
    // shows it, and closing the panel drops one it never showed. A request left standing would force the section open on
    // every later open of the panel and in every other chat's panel, and a second request could not expand it again.
    const [folderContextRequested, setFolderContextRequested] = useState(false);
    const folderContextShown = useCallback(() => setFolderContextRequested(false), []);
    useEffect(() => { if (!taskPanelOpen)
        setFolderContextRequested(false); }, [taskPanelOpen]);
    const localFilesMenuRef = useRef(null);
    const [localFileState, setLocalFileState] = useState(null);
    const localFilesSnapshot = localFileState?.owner === userKey ? localFileState.value : null;
    const receiveLocalFiles = useCallback((value) => setLocalFileState({ owner: userKey, value }), [userKey]);
    useEffect(() => { setComposerWorkspace('chat'); setFolderContextRequested(false); }, [userKey]);
    const [taskCompact, setTaskCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 1100);
    const [taskActivity, setTaskActivity] = useState(null);
    const [workingFolder, setWorkingFolder] = useState(null);
    const [taskSources, setTaskSources] = useState([]);
    const [taskArtifacts, setTaskArtifacts] = useState([]);
    const [taskRuntime, setTaskRuntime] = useState();
    const [agentStatus, setAgentStatus] = useState();
    const [knowledgeCapabilities, setKnowledgeCapabilities] = useState(() => knowledgeStatus(null));
    const knowledgeMode = desktopWorkspace ? preferences.knowledgeMode : 'work';
    const [taskActions, setTaskActions] = useState([]);
    const [taskContextEpoch, setTaskContextEpoch] = useState(0);
    const sessionIdRef = useRef(null);
    const conversationsRef = useRef(new ConversationRuns());
    // Tokens and cost per chat (client-side only): every chat's total lives in the store, the open chat's is state.
    const conversationUsageRef = useRef(new ConversationUsage());
    const [sessionUsage, setSessionUsage] = useState(EMPTY_SESSION_USAGE);
    /**
     * A turn's tokens join its chat's total exactly once (whichever chat is on screen; the line updates when that chat
     * is open). `recorded` marks the done result's usage, which replaces live figures counted for the same turn earlier
     * (a stream can report an error and still deliver its done result); otherwise the turn's last live figures count.
     * The new total goes to the local chat cache at once: a failed or abandoned turn leaves the transcript unchanged, so
     * the transcript save would never carry it, and the chat must show it when reopened after a reload.
     */
    const countTurnUsage = (conversationId, startedAt, turn, recorded) => {
        if (!turn || startedAt === undefined)
            return;
        const next = conversationUsageRef.current.countTurn(conversationId, `${conversationId}:${startedAt}`, turn, recorded);
        if (sessionIdRef.current === conversationId)
            setSessionUsage(next);
        setSessions(previous => {
            const updated = withChatUsage(previous, conversationId, next);
            if (!updated)
                return previous;
            persistSessions(updated.filter(session => !session.remote));
            return updated;
        });
    };
    /** What a turn that reached its done result adds to its chat (see finishedTurnUsage). */
    const countFinishedTurn = (conversationId, startedAt, res, live) => {
        const counted = finishedTurnUsage(res.usage, live, res.mode !== 'ai');
        if (counted)
            countTurnUsage(conversationId, startedAt, counted.usage, counted.recorded);
    };
    /** A reply cut off before its result (stopped, or left for another chat in the in-page widget) keeps what it spent. */
    const countInterruptedTurn = (conversationId, replies) => {
        const reply = replies.find(message => message.streaming || message.pending);
        if (conversationId && reply?.liveUsage)
            countTurnUsage(conversationId, reply.startedAt, turnUsageFromLive(reply.liveUsage), false);
    };
    const [, refreshConversations] = useReducer((value) => value + 1, 0);
    const documentStore = useMemo(() => {
        const cached = conversationsRef.current.get(sessionIdRef.current ?? '')?.value;
        return cached?.owner === userKey ? cached.documents : new LocalDocumentStore(generateDocument);
    }, [userKey, taskContextEpoch]);
    const documentStoreRef = useRef(documentStore);
    documentStoreRef.current = documentStore;
    useEffect(() => () => {
        for (const chat of conversationsRef.current.values())
            chat.value.documents.clear();
        conversationsRef.current.clear();
        documentStoreRef.current.clear();
    }, []);
    const [taskAttachments, setTaskAttachments] = useState([]);
    const [taskConnectors, setTaskConnectors] = useState([]);
    const desktopMcp = useDesktopMcp(desktopWorkspace);
    // The desktop's server names for the lines below (mcpDisplayNames.ts): read through a ref by callbacks that outlive a render.
    const mcpServersRef = useRef(desktopMcp.snapshot?.servers ?? null);
    mcpServersRef.current = desktopMcp.snapshot?.servers ?? null;
    /** An action's summary as the person reads it: a call to one of the PC's MCP servers names the server by its display name. */
    const actionSummary = (action) => mcpActionSummary(action, mcpServersRef.current);
    const desktopSkills = useDesktopSkills(desktopWorkspace);
    const mascotAnimations = useMascotAnimations(desktopWorkspace);
    const mascotShareHide = useMascotShareHide(desktopWorkspace);
    const [taskConnectorsState, setTaskConnectorsState] = useState('loading');
    const [taskConnectorsRevision, setTaskConnectorsRevision] = useState(0);
    const needsConnectorInventory = taskPanelOpen || composerMenuOpen;
    const taskRunRef = useRef(0);
    const taskPanelRef = useRef(null);
    const taskToggleRef = useRef(null);
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const query = window.matchMedia('(max-width: 1100px)');
        const update = () => { setTaskCompact(query.matches); if (query.matches)
            setTaskPanelOpen(false); };
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, [desktopWorkspace]);
    useEffect(() => {
        if (!desktopWorkspace || !needsConnectorInventory)
            return;
        let cancelled = false;
        setTaskConnectorsState('loading');
        setTaskConnectors([]);
        void client.listConnectors().then(connectors => {
            if (!cancelled) {
                setTaskConnectors(connectors);
                setTaskConnectorsState('ready');
            }
        }).catch(() => { if (!cancelled)
            setTaskConnectorsState('error'); });
        return () => { cancelled = true; };
    }, [client, desktopWorkspace, needsConnectorInventory, userKey, taskConnectorsRevision]);
    // The built-in Gyro. The person's look (#291, below) replaces it wherever Gyro is shown, except where the built-in
    // Gyro is itself a choice (the "Gyro" tile in Settings and in the chat picker).
    const builtInAvatar = props.staticAvatarUrl ?? '/gyro-assistant.png';
    const builtInAnimAvatar = props.animatedAvatarUrl ?? '/gyro-ai-anim.png';
    const [personaGreeting, setPersonaGreeting] = useState();
    const greeting = useMemo(() => ({ role: 'assistant', content: props.greeting ?? personaGreeting ?? DEFAULT_GREETING, welcome: true }), [props.greeting, personaGreeting]);
    const suggestions = props.suggestions ?? DEFAULT_SUGGESTIONS;
    // Per app + identity (M-09). The voice/detailed mode is deliberately NOT identity-scoped: it is a
    // device-level UI preference carrying no user data.
    const { sessions: sessionsKey, model: modelKey, look: lookKey, payChats: payChatsKey } = storageKeys(appKey, userKey);
    /** Remember a chat as holding pay data in this browser (ids only), so a reload never reads one of its answers aloud. */
    const rememberPayChatHere = (conversationId) => {
        try {
            rememberPayChat(localStorage, payChatsKey, conversationId);
        }
        catch { /* no storage: the service's history still says so */ }
    };
    const modeKey = `${appKey}-gyro-mode`;
    // Side-car pane host API (contract §5, paneHost.ts): a pane is always open (nothing the widget does closes it), the
    // host's own tools ride on this widget's own streamed asks only (passed per call in send, never set on the shared
    // client), and its confirmed actions run there (clientActionRunnerRef).
    const layoutChrome = layoutChromeFor(props.layout);
    const [openState, setOpen] = useState(false);
    const open = layoutChrome.alwaysOpen || openState;
    // The Office pane (layout="pane" with appKey 'office'; owner, 2026-10-20: "the window doesnt show a static gyro image so
    // its just a bland window"): a new chat's welcome shows the outfit large (the 300 px rendition, asked for below), and
    // once the welcome is off screen a small mascot sits in the header, so Gyro is never off screen. The desktop overlay,
    // the floating web widget and Gyro on the web keep their sizes.
    const officePane = layoutChrome.alwaysOpen && appKey === 'office';
    // Any floating panel on a host page, in the workspace look or the compact one (not the desktop app, not a pane): it is one
    // part of the host's page (aria-modal false), so its keys act only while it has the keyboard and Tab moves on to the page,
    // and its header moves the buttons that do not fit into a "More actions" menu.
    const floatingPanel = layoutChrome.launcher && !desktopWorkspace;
    // pageShortcuts: read through a ref by the page-wide key listener, which is registered once.
    const pageShortcutsRef = useRef(props.pageShortcuts !== false);
    pageShortcutsRef.current = props.pageShortcuts !== false;
    // 'auto' (the floating panel): read the host page's light or dark when the panel opens, before it paints, and follow the
    // host's own switch while it is open (its theme attributes and classes on <html> and <body>, and the system's scheme, which a
    // page may follow). The closed launcher has no theme, so nothing is read for it.
    useClientLayoutEffect(() => {
        if (themeChoice !== 'auto' || !open)
            return undefined;
        const read = () => setPageTheme(hostPageTheme(document));
        read();
        const Observer = window.MutationObserver;
        const observer = typeof Observer === 'function' ? new Observer(read) : null;
        for (const element of [document.documentElement, document.body]) {
            if (element)
                observer?.observe(element, { attributes: true, attributeFilter: [...HOST_THEME_WATCHED_ATTRIBUTES] });
        }
        const scheme = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
        scheme?.addEventListener?.('change', read);
        return () => { observer?.disconnect(); scheme?.removeEventListener?.('change', read); };
    }, [themeChoice, open]);
    // prefers-reduced-motion: the closed launcher shows the still Gyro, since the animated one loops forever (WCAG 2.2.2). Read
    // after mount, so a server-rendered page and its first render in the browser agree.
    const [reducedMotion, setReducedMotion] = useState(false);
    useEffect(() => {
        if (!layoutChrome.launcher || typeof window.matchMedia !== 'function')
            return undefined;
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const change = () => setReducedMotion(query.matches);
        change();
        query.addEventListener?.('change', change);
        return () => query.removeEventListener?.('change', change);
    }, [layoutChrome.launcher]);
    // Files made in the browser from an answer (PDF, Word, Excel, PowerPoint...): the desktop workspace and a pane.
    const documentOutputs = parts.documents && documentOutputsFor({ layout: props.layout, desktopWorkspace });
    const clientActionsRef = useRef(clientActions);
    clientActionsRef.current = clientActions;
    // A desktop action for Gyro's built-in local providers (file changes, a PowerShell script) shows in the tall view.
    const confirmLimits = (action) => confirmLimitsFor({ override: props.confirmLimits, apps: [client.app, appKey, action.app, action.targetApp], localAction: isLocalProviderAction(action), originalLocalTask: isOriginalLocalFileTask(action) });
    useEffect(() => {
        if (open)
            track('gyro_opened', { app: appKey });
    }, [open, appKey]);
    // Gyro's own usage record (usage/featureEvents.ts): the surface the host answers with ('gyro:surface', answering the
    // widget's 'gyro:surface-ready' the way the desktop shell answers 'gyro:window-mode'), else derived from the app key and layout as a default (a host that configured
    // its own, Gyro Admin's 'admin', keeps it); this client as the sink unless the host installed one first; the widget's
    // version and the browser as the envelope's defaults (the add-in's version wins); the signed-in account as the queue's
    // owner; the page's visibility hooks for session boundaries.
    const [hostSurface, setHostSurface] = useState(undefined);
    const usageSinkOwnedRef = useRef(false); // this widget installed the sink: a new client prop replaces it
    useEffect(() => {
        const receiveSurface = (event) => {
            const surface = parseUsageSurface(event.detail);
            if (surface)
                setHostSurface(surface);
        };
        window.addEventListener(USAGE_SURFACE_EVENT, receiveSurface);
        window.dispatchEvent(new CustomEvent(USAGE_SURFACE_READY_EVENT));
        return () => window.removeEventListener(USAGE_SURFACE_EVENT, receiveSurface);
    }, []);
    useEffect(() => {
        const usage = usageCapture();
        const answered = parseUsageSurface(hostSurface);
        if (answered)
            usage.configure({ surface: answered });
        usage.configureDefaults({ surface: usageSurfaceFor({ appKey, layout: props.layout }), app_version: GYRO_REACT_VERSION, ...clientEnvironment() });
        if (!usage.snapshot().hasSink || usageSinkOwnedRef.current) {
            usage.installSink((envelope, options) => client.postUsageEvents(envelope, options));
            usageSinkOwnedRef.current = true;
        }
        usage.installBrowserHooks();
    }, [client, appKey, props.layout, hostSurface]);
    useEffect(() => {
        usageCapture().setUser(userKey === ANONYMOUS_USER_KEY ? null : userKey);
    }, [userKey]);
    // Is the surface on screen? The host says so when it knows (desktop overlay); otherwise the page's own
    // visibility decides. Background work that only matters to someone looking (the inbox poll, the 1 s clock)
    // runs only while the panel is open AND on screen.
    const hostVisibility = parseHostVisibility(props.hostVisibility);
    const [pageVisible, setPageVisible] = useState(() => documentIsVisible());
    useEffect(() => {
        if (hostVisibility)
            return;
        const onVisibility = () => setPageVisible(documentIsVisible());
        onVisibility();
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [hostVisibility]);
    const onScreen = surfaceOnScreen(open, hostVisibility, pageVisible);
    // "Connect Asana from Gyro" (usePersonConnect): the desktop app only. Every other surface renders no control.
    const personConnect = usePersonConnect(client, onScreen, Boolean(parts.desktop) && appKey === 'desktop');
    // Gyro's look everywhere (#291): the person's outfit wherever Gyro is shown, only where /v1/status says `look`. A
    // picture that will not load drops back to the built-in Gyro until the look changes; never a broken image. It runs
    // while the page is on screen even with the chat closed: the closed launcher is where people see Gyro most (review
    // M1). Until this page has a status answer, what this browser remembers decides (gyroLook.ts LookMemory).
    const lookVisible = hostVisibility ? hostVisibility === 'shown' : pageVisible;
    const [lookStatus, setLookStatus] = useState(null);
    const lookMemoryOn = useMemo(() => readLookMemory(browserLookStorage(), lookKey)?.on === true, [lookKey]);
    const lookAvailable = lookStatus ?? lookMemoryOn;
    /** Remember what /v1/status said about the look, keeping a remembered outfit while it stays on. */
    const rememberLookStatus = useCallback((on) => {
        const storage = browserLookStorage();
        const memory = readLookMemory(storage, lookKey);
        if (on && memory?.on)
            return;
        writeLookMemory(storage, lookKey, { on, atMs: Date.now() });
    }, [lookKey]);
    // The day's greeting (day outfits; dayGreeting.ts): from /v1/status, and from live look answers, whichever came last.
    const [dayGreeting, setDayGreeting] = useState(null);
    const dayGreetingAtRef = useRef(null);
    const dayRefreshRef = useRef({ inFlight: false, retryAtMs: 0 });
    const applyDayGreeting = useCallback((next) => {
        const now = Date.now();
        dayGreetingAtRef.current = now;
        const live = next && next.untilMs > now ? next : null; // an already-ended greeting (clock skew) never loops a refresh
        setDayGreeting(current => sameDayGreeting(current, live) ? current : live);
    }, []);
    const look = useGyroLook({ client, appKey, enabled: lookAvailable, visible: lookVisible, open, memoryKey: lookKey, rememberPictures: userKey !== ANONYMOUS_USER_KEY, xl: officePane, onUnavailable: () => setLookStatus(false), onDay: applyDayGreeting });
    // A page whose chat has not been opened learns whether the look is on with one status read, only when nothing is
    // remembered or a remembered "off" is a day old; never a retry loop (the open chat's own status check does that).
    const lookProbeRef = useRef(null);
    useEffect(() => {
        if (lookStatus !== null || !lookVisible || onScreen || lookProbeRef.current === lookKey)
            return undefined;
        if (!lookStatusDue(readLookMemory(browserLookStorage(), lookKey), Date.now()))
            return undefined;
        lookProbeRef.current = lookKey;
        let cancelled = false;
        client.status().then((s) => {
            if (cancelled)
                return;
            setLookStatus(s.look === true);
            rememberLookStatus(s.look === true);
            applyDayGreeting(dayGreetingFromPersona(s.persona));
        }, () => { });
        return () => { cancelled = true; };
    }, [client, lookKey, lookStatus, lookVisible, onScreen, rememberLookStatus, applyDayGreeting]);
    const [failedLookEtag, setFailedLookEtag] = useState(null);
    const outfitLook = outfitToShow(look, failedLookEtag);
    const staticAvatar = outfitLook?.small ?? builtInAvatar;
    /** The welcome picture: the 192 px rendition, or in the Office pane the 300 px one when the answer carried it (150 px at 2x). */
    const largeAvatar = (officePane ? outfitLook?.xlarge : undefined) ?? outfitLook?.large ?? builtInAvatar;
    const animAvatar = outfitLook?.large ?? builtInAnimAvatar;
    const launcherAvatar = reducedMotion && !outfitLook ? builtInAvatar : animAvatar;
    const lookImg = outfitLook ? { onError: () => setFailedLookEtag(outfitLook.etag) } : {};
    const [expanded, setExpanded] = useState(false);
    const [windowMaximized, setWindowMaximized] = useState(false);
    const [hasNativeWindowControls, setHasNativeWindowControls] = useState(false);
    // The desktop window ("Show as desktop window"): Windows draws the title bar, so the header drops its own window controls.
    const [windowed, setWindowed] = useState(false);
    const expandedRef = useRef(expanded);
    expandedRef.current = expanded;
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 760);
    const [compactDesktop, setCompactDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 760);
    // The floating panel covers the whole screen on a phone, upright or on its side (paneHost FLOATING_FULL_SCREEN_QUERY, the same
    // condition as gyro.css): there its navigation follows the page's width alone, and Expand has nothing to do.
    const [floatingFullScreen, setFloatingFullScreen] = useState(false);
    useEffect(() => {
        if (!floatingWorkspace || typeof window.matchMedia !== 'function')
            return undefined;
        const query = window.matchMedia(FLOATING_FULL_SCREEN_QUERY);
        const change = () => setFloatingFullScreen(query.matches);
        change();
        query.addEventListener?.('change', change);
        return () => query.removeEventListener?.('change', change);
    }, [floatingWorkspace]);
    // The floating panel remembers Expand per app and person (persistedState.ts `layout`); the desktop's host owns its own.
    const layoutKey = storageKeys(appKey, userKey).layout;
    useEffect(() => {
        if (!floatingWorkspace)
            return;
        const saved = readFloatingLayout(desktopStorage(), layoutKey);
        expandedRef.current = saved.expanded;
        setExpanded(saved.expanded);
    }, [floatingWorkspace, layoutKey]);
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const receiveMode = (event) => {
            const mode = parseDesktopWindowMode(event.detail);
            if (!mode)
                return;
            setWindowed(mode === 'window');
            const next = isRoomyWindowMode(mode);
            if (expandedRef.current !== next) {
                expandedRef.current = next;
                setExpanded(next);
                setSidebarOpen(next);
            }
        };
        const receiveMaximized = (event) => {
            const maximized = event.detail;
            if (typeof maximized !== 'boolean')
                return;
            setHasNativeWindowControls(true);
            setWindowMaximized(maximized);
        };
        window.addEventListener('gyro:window-mode', receiveMode);
        window.addEventListener('gyro:window-maximized', receiveMaximized);
        window.dispatchEvent(new CustomEvent('gyro:window-mode-ready'));
        return () => {
            window.removeEventListener('gyro:window-mode', receiveMode);
            window.removeEventListener('gyro:window-maximized', receiveMaximized);
        };
    }, [desktopWorkspace]);
    const toggleExpanded = () => {
        const next = !expandedRef.current;
        // Let the native host capture the current layout before changing the mode. Its acknowledgement
        // applies the new layout behind the transition; browser-only embeddings still update immediately.
        if (desktopWorkspace && !window.dispatchEvent(new CustomEvent('gyro:window-mode-request', {
            detail: next ? 'comfy' : 'vertical', cancelable: true,
        })))
            return;
        expandedRef.current = next;
        setExpanded(next);
        if (floatingWorkspace)
            writeFloatingLayout(desktopStorage(), layoutKey, { expanded: next });
        if (desktopWorkspace)
            setSidebarOpen(next);
    };
    const closeAssistant = useCallback(() => {
        // A native host keeps the page painted until its closing transition ends.
        // Browser embeddings without a host still close the panel normally.
        if (desktopWorkspace && !window.dispatchEvent(new CustomEvent('gyro:window-close', { cancelable: true })))
            return;
        setOpen(false);
    }, [desktopWorkspace]);
    // Which window controls the header draws and whether Esc closes (desktopWindowMode.ts); the switch to the desktop window
    // and back is a request native confirms with the new 'gyro:window-mode'.
    const windowChrome = desktopWindowChrome(desktopWorkspace, windowed, hasNativeWindowControls);
    const escapeCloses = windowChrome.escapeCloses;
    const windowSwitch = windowToggle(windowed);
    const requestWindowSwitch = () => { window.dispatchEvent(new CustomEvent('gyro:window-mode-request', { detail: windowSwitch.request, cancelable: true })); };
    // The navigation is a drawer over the chat in a narrow surface: a window or pane 760 px wide or less (desktop, Office), or the
    // floating panel unless it is expanded on a page wider than 760 px (paneHost floatingSidebarLayout).
    const compactWorkspace = floatingWorkspace ? floatingSidebarLayout({ expanded, pageNarrow: compactDesktop, fullScreen: floatingFullScreen }) === 'drawer' : compactDesktop;
    const desktopDrawerOpen = workspace && open && compactWorkspace && sidebarOpen;
    // The floating panel's navigation starts as the desktop's would: closed in the 400 px panel (Vertical), docked in the expanded
    // panel on a wide page (Comfy) and in a full-screen one wider than 760 px. Set when the panel opens and when that layout
    // changes, before the render commits.
    const floatingLayoutNow = floatingWorkspace && open ? floatingSidebarLayout({ expanded, pageNarrow: compactDesktop, fullScreen: floatingFullScreen }) : null;
    const [floatingLayoutSeen, setFloatingLayoutSeen] = useState(null);
    if (floatingLayoutNow !== floatingLayoutSeen) {
        setFloatingLayoutSeen(floatingLayoutNow);
        if (floatingLayoutNow)
            setSidebarOpen(floatingLayoutNow === 'docked');
    }
    const taskDrawerOpen = desktopWorkspace && open && taskCompact && taskPanelOpen && !settingsSection;
    const closeTaskPanel = useCallback(() => {
        setTaskPanelOpen(false);
        requestAnimationFrame(() => taskToggleRef.current?.focus());
    }, []);
    useEffect(() => {
        if (taskDrawerOpen)
            taskPanelRef.current?.querySelector('button')?.focus();
    }, [taskDrawerOpen]);
    useEffect(() => {
        if (!workspace)
            return;
        const query = window.matchMedia('(max-width: 760px)');
        const update = () => setCompactDesktop(query.matches);
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, [workspace]);
    const [showSessions, setShowSessions] = useState(false);
    const [showCapabilities, setShowCapabilities] = useState(false);
    const [showMemory, setShowMemory] = useState(false);
    const [petSessionEpoch, setPetSessionEpoch] = useState(0);
    // Hosts may recreate equivalent clients during render. Availability belongs to the
    // service/app and signed-in identity, not the transport object's allocation.
    const petServiceKey = JSON.stringify([client.baseUrl, client.app, appKey]);
    const [petsCapability, setPetsCapability] = useState(null);
    const petsCapabilityCurrent = petsCapability?.serviceKey === petServiceKey && petsCapability.userKey === userKey && petsCapability.epoch === petSessionEpoch;
    const petsEnabled = PETS_BUNDLE_ENABLED && desktopWorkspace && petsCapabilityCurrent && petsCapability.enabled === true;
    const petMissionSession = useMemo(() => ({ client, userKey, petSessionEpoch, petsEnabled, petServiceKey }), [client, userKey, petSessionEpoch, petsEnabled, petServiceKey]);
    const petSetup = usePetSetupHandoff(petMissionSession, client, petServiceKey, Boolean(petsEnabled), () => sessionIdRef.current);
    const petDecisionLifetime = useRef(null);
    useClientLayoutEffect(() => {
        const previous = petDecisionLifetime.current;
        // A committed identity change retires the old API before any promise can
        // deliver. StrictMode's synchronous effect replay keeps the same owner.
        if (previous && previous.owner !== petMissionSession)
            previous.clear(previous.owner);
        const current = { owner: petMissionSession, clear: parts.clearPetDecisionSession };
        petDecisionLifetime.current = current;
        return () => queueMicrotask(() => {
            if (petDecisionLifetime.current !== current)
                return;
            petDecisionLifetime.current = null;
            current.clear(current.owner);
        });
        // The parts object's allocation is not an authenticated identity change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petMissionSession]);
    const petBriefRemovalSession = useMemo(() => PETS_BUNDLE_ENABLED && petsEnabled
        ? { owner: petMissionSession, transport: client, operation: null, epoch: 0 } : undefined, [petMissionSession, client, petsEnabled]);
    useClientLayoutEffect(() => () => { if (petBriefRemovalSession) {
        petBriefRemovalSession.operation = null;
        petBriefRemovalSession.epoch++;
    } }, [petBriefRemovalSession]);
    const petLifecycleSession = useMemo(() => PETS_BUNDLE_ENABLED && petsEnabled
        ? { owner: petMissionSession, transport: client, operation: null, epoch: 0 } : undefined, [petMissionSession, client, petsEnabled]);
    useClientLayoutEffect(() => () => { if (petLifecycleSession) {
        petLifecycleSession.operation = null;
        petLifecycleSession.epoch++;
    } }, [petLifecycleSession]);
    const petResolutionSession = useMemo(() => PETS_BUNDLE_ENABLED && petsEnabled
        ? { owner: petMissionSession, transport: client, operation: null, epoch: 0 } : undefined, [petMissionSession, client, petsEnabled]);
    useClientLayoutEffect(() => () => { if (petResolutionSession) {
        petResolutionSession.operation = null;
        petResolutionSession.epoch++;
    } }, [petResolutionSession]);
    const petViewZoneSession = useMemo(() => PETS_BUNDLE_ENABLED && petsEnabled
        ? { owner: petMissionSession, transport: client, operation: null, epoch: 0 } : undefined, [petMissionSession, client, petsEnabled]);
    useClientLayoutEffect(() => () => { if (petViewZoneSession) {
        petViewZoneSession.operation = null;
        petViewZoneSession.epoch++;
    } }, [petViewZoneSession]);
    const [petsViewRequested, setShowPets] = useState(false);
    const showPets = petsEnabled && petsViewRequested;
    const [petRoute, setPetRoute] = useState(() => props.petContext ? { view: 'pet', ...props.petContext } : { view: 'crew' });
    useEffect(() => {
        setShowPets(Boolean(petsEnabled && props.petContext));
        setPetRoute(petsEnabled && props.petContext ? { view: 'pet', ...props.petContext } : { view: 'crew' });
    }, [userKey, petsEnabled, props.petContext?.petId, props.petContext?.tab, props.petContext?.missionId, props.petContext?.decisionId]); // eslint-disable-line react-hooks/exhaustive-deps
    const disablePets = useCallback(() => {
        setPetsCapability(previous => previous?.serviceKey === petServiceKey && previous.userKey === userKey && previous.epoch === petSessionEpoch ? { ...previous, enabled: false } : previous);
    }, [petServiceKey, userKey, petSessionEpoch]);
    const companionSessions = useCompanionSessions(PETS_BUNDLE_ENABLED && petsEnabled, petMissionSession, client, parts, disablePets);
    const petActionReviewSession = companionSessions.action, petCheckStatusSessions = companionSessions.status;
    useClientLayoutEffect(() => {
        petCheckStatusSessions?.availability(open && hostVisibility !== 'hidden' && petsEnabled);
        return () => petCheckStatusSessions?.availability(false);
    }, [petCheckStatusSessions, open, hostVisibility, petsEnabled]);
    const previousPetsEnabled = useRef(false);
    useEffect(() => {
        if (previousPetsEnabled.current && !petsEnabled)
            parts.clearNativePets();
        previousPetsEnabled.current = petsEnabled;
    }, [petsEnabled, parts]);
    useEffect(() => { if (showNotifications || showSessions || showCapabilities || showMemory)
        setShowPets(false); }, [showNotifications, showSessions, showCapabilities, showMemory]);
    const [memoryText, setMemoryText] = useState('');
    const [memoryLoading, setMemoryLoading] = useState(false);
    const [memoryLoadFailed, setMemoryLoadFailed] = useState(false);
    const [memoryError, setMemoryError] = useState('');
    const [memorySaving, setMemorySaving] = useState(false);
    const [memorySaved, setMemorySaved] = useState(false);
    const [capabilities, setCapabilities] = useState([]);
    // Caller-visible model catalog (server-filtered by role) and the chat's two choices: its model and its thinking level
    // (the composer's two buttons, ComposerModelControls; owner, 2026-10-19).
    const [models, setModels] = useState([]);
    /** The saved per-app model pick ('' = the default), kept in localStorage as the dropdown kept it; never the High model. */
    const [modelChoice, setModelChoice] = useState('');
    /**
     * Whether the open chat runs at High (modelChoice.ts; 'high' or 'balanced'). In-memory state of this chat only: every
     * new chat and every chat opened from the list starts at Balanced (newSession, loadSession), and nothing writes it to
     * localStorage, the desktop preferences or the history, so High can never become a default (owner, 2026-10-17).
     */
    const [modelTier, setModelTier] = useState('balanced');
    /** The thinking level the chat sends ('' = the model's own default): in-memory, reset for every new or reopened chat. */
    const [effort, setEffort] = useState('');
    /** The service's note when the chat ran at a shallower depth than promised (the admin depth allow-list); under the buttons. */
    const [tierNote, setTierNote] = useState('');
    const menuEntries = useMemo(() => modelMenuEntries(models), [models]);
    const currentModel = useMemo(() => chatModel(models, modelTier, modelChoice), [models, modelTier, modelChoice]);
    const levels = useMemo(() => thinkingLevels(currentModel), [currentModel]);
    /** The model button's pick: High is this chat's alone; any other model is saved per app, as the dropdown saved it. */
    const pickModel = (entry) => {
        setTierNote('');
        setEffort('');
        if (entry.high) {
            setModelTier('high');
            track('gyro_model_changed', { app: appKey, model: 'high' });
            return;
        }
        setModelTier('balanced');
        const id = entry.model.default ? '' : entry.id;
        setModelChoice(id);
        track('gyro_model_changed', { app: appKey, model: id || 'default' });
        try {
            if (id)
                localStorage.setItem(modelKey, id);
            else
                localStorage.removeItem(modelKey);
        }
        catch {
            /* storage unavailable: the pick still applies for this session */
        }
    };
    /** The hint line under the message box shows only while it is focused and empty (owner, 2026-10-17: "tighten this up"). */
    const [composerFocused, setComposerFocused] = useState(false);
    const [configured, setConfigured] = useState(null);
    const [unreachable, setUnreachable] = useState(false);
    /** The reason the status check failed (e.g. "status 401: app_auth_required") — shown in the banner so a
     *  "was working → unavailable" flip is diagnosable without opening DevTools. Null while reachable. */
    const [unreachableReason, setUnreachableReason] = useState(null);
    /** Retry counter for the status check — bumped on a capped backoff after a failed check so a transient
     *  auth/network blip recovers itself instead of latching on `configured === null` until a reload (#123). */
    const [statusAttempt, setStatusAttempt] = useState(0);
    const statusRetryRef = useRef(undefined);
    const [input, setInput] = useState('');
    const [attachments, setAttachments] = useState([]);
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;
    const attachmentEpochRef = useRef(0);
    const attachmentQueueRef = useRef(Promise.resolve());
    const attachmentPendingRef = useRef(0);
    const [attachmentReads, setAttachmentReads] = useState(0);
    const [attachError, setAttachError] = useState('');
    const [fileAttachmentsSupported, setFileAttachmentsSupported] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    // Messages typed while Gyro is responding are QUEUED here (steer the conversation): they drain in
    // order when the current turn finishes, or the user can "Send now" to stop + release them immediately.
    const [queued, setQueued] = useState([]);
    const [messages, setRenderedMessages] = useState([greeting]);
    // Approval cards, receipts, and stream deltas share the same conversation snapshot. Mutate that
    // snapshot synchronously so a batched stream update cannot erase an intervening action result.
    const setMessages = useCallback((update) => {
        const chat = conversationsRef.current.get(sessionIdRef.current ?? '');
        if (chat?.value.owner === userKeyRef.current) {
            const next = typeof update === 'function' ? update(chat.value.messages) : update;
            chat.value.messages = next;
            setRenderedMessages(next);
        }
        else
            setRenderedMessages(update);
    }, []);
    // The open chat's transcript as last rendered: whether a result the inbox lists can be shown here (outcomeShowable).
    const renderedMessagesRef = useRef(messages);
    renderedMessagesRef.current = messages;
    // IT-support request cards from the host ('gyro:support-card' hook; the host owns the list and re-sends it on
    // every change). What THIS person's Claim click learned is layered on top until the host confirms the claim.
    const [supportCards, setSupportCards] = useState([]);
    const [supportOutcomes, setSupportOutcomes] = useState({});
    const [supportClaiming, setSupportClaiming] = useState(null);
    const openSupportIdsRef = useRef(new Set());
    const [resolved, setResolved] = useState({});
    const [previews, setPreviews] = useState({});
    // Where each approval card is (approvalCard.ts): Approve pressed, gyro-svc's second confirmation, or back at Approve.
    const [approvalSteps, setApprovalSteps] = useState({});
    const [actionOps, setActionOps] = useState({});
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [feedbackGiven, setFeedbackGiven] = useState({});
    // Pulse (docs/CONTRACT-pulse.md section 10): the status frame's switch, the device's card memory (the same storage the
    // desktop's preferences live in), the cards this session showed by key, the ones closed, each card's cause chips, the
    // thumbs comment box open under a reply, the pane edits that settled in the running turn, and each action's ask id.
    const [pulseOn, setPulseOn] = useState(null);
    const pulseOnRef = useRef(pulseOn);
    pulseOnRef.current = pulseOn;
    const pulseStore = useMemo(() => pulseCardStore(desktopStorage()), []);
    const pulseCardedRef = useRef(new Set());
    const [pulseClosed, setPulseClosed] = useState({});
    const [pulseChips, setPulseChips] = useState({});
    const [pulseThumbBox, setPulseThumbBox] = useState(null);
    const paneSettledRef = useRef([]);
    const pulseAsksRef = useRef(new Map());
    // Share flow (L-26): confirm → creating → ready/failed, for at most one answer at a time.
    // Gyro on the web never shares: a share link opens for anyone who has it, and that page is for employees only (gyro-svc
    // refuses the share routes on its host as well, webHost.ts).
    const allowShare = props.allowShare !== false && appKey !== 'web' && (!desktopWorkspace || preferences.allowSharing);
    const [share, dispatchShare] = useReducer(shareReducer, null);
    const [sessions, setSessions] = useState([]);
    const sessionsRef = useRef([]);
    sessionsRef.current = sessions;
    const [chatMenu, setChatMenu] = useState(null);
    const [renameRequest, setRenameRequest] = useState(null);
    // Spent once the title editor has opened for it, so leaving the chat view and coming back cannot reopen the editor.
    const renameRequestHandled = useCallback(() => setRenameRequest(null), []);
    const compactRequestRef = useRef(null);
    useEffect(() => () => { compactRequestRef.current?.abort(); }, [userKey, taskContextEpoch]);
    useEffect(() => { setChatMenu(null); setRenameRequest(null); }, [userKey]);
    const [loadedSessionRevision, setLoadedSessionRevision] = useState(0);
    const [historySearch, setHistorySearch] = useState('');
    // Command palette (Ctrl/Cmd+K) + the launcher's unread-answers badge.
    const [paletteOpen, setPaletteOpen] = useState(false);
    useEffect(() => { if (!open || hostVisibility === 'hidden' || settingsSection || paletteOpen)
        setChatMenu(null); }, [open, hostVisibility, settingsSection, paletteOpen]);
    const [paletteQuery, setPaletteQuery] = useState('');
    const [paletteIndex, setPaletteIndex] = useState(0);
    // Slash commands (slashCommands.ts): the menu above the message box while the composer starts with "/". Esc hides it
    // until the text changes; the highlighted row follows the arrow keys.
    const [slashDismissed, setSlashDismissed] = useState(false);
    const [slashIndex, setSlashIndex] = useState(0);
    const [unseen, setUnseen] = useState(0);
    // Desktop notifications (L-27): mirror the browser permission in state so the opt-in control can
    // render its three looks; it is only ever changed from a user gesture (enableNotifications).
    const notificationsMode = props.notifications ?? true;
    const notificationsEnabled = notificationsMode !== false && (!desktopWorkspace || preferences.responseNotifications);
    const notificationsEnabledRef = useRef(notificationsEnabled);
    notificationsEnabledRef.current = notificationsEnabled;
    const [notifPerm, setNotifPerm] = useState(() => notificationApi()?.permission ?? 'unsupported');
    const primedNotificationsRef = useRef(false);
    const [mode, setMode] = useState(() => {
        try {
            const saved = localStorage.getItem(modeKey);
            return saved === 'voice' || saved === 'detailed' ? saved : 'detailed';
        }
        catch {
            return 'detailed';
        }
    });
    const voiceOn = mode === 'voice';
    const voiceOnRef = useRef(voiceOn);
    voiceOnRef.current = voiceOn;
    const speechSuppressedRunRef = useRef(null);
    const [speechState, setSpeechState] = useState('idle');
    const speech = useMemo(() => new ReplySpeech({
        // The chat on screen goes with the text: gyro-svc never voices an answer from a chat that holds pay data.
        requestAudio: (text, signal) => client.tts(text, { signal, conversationId: sessionIdRef.current }),
        playAudio: playAudioBlob,
        speakFallback: speak,
        stopAudio: stopPlayback,
        stopFallback: stopSpeaking,
        onState: setSpeechState,
    }), [client]);
    useEffect(() => () => speech.stop(), [speech]);
    // Voice input (speechInput.ts voiceInputFor, 0.16.3): the desktop shell dictates through Windows, the Office pane keeps the
    // browser's dictation, and a web page offers none, by the owner's decision: no mic, no dictation keys or palette command,
    // and no recognizer is ever made there. The web entry, without the desktop app's and the Office pane's parts, is a web page
    // wherever it runs. The page-wide listeners, registered once, read it through the ref.
    const voiceInput = voiceInputFor({
        desktopShell: parts.desktop && isDesktopShell(typeof window === 'undefined' ? undefined : window),
        officePane: isOfficePane({ paneHost: parts.paneHost, layout: props.layout, appKey }),
    });
    const voiceInputRef = useRef(voiceInput);
    voiceInputRef.current = voiceInput;
    const [speechInputState, setSpeechInputState] = useState('idle');
    // What Pulse's card rules read at the moment an action settles (section 10.1: never during a running task or dictation).
    const pulseConditionsRef = useRef({ busy: false, dictating: false });
    pulseConditionsRef.current = { busy, dictating: speechInputState !== 'idle' };
    const [voiceError, setVoiceError] = useState('');
    // A web page's "not available here" (VOICE_INPUT_OFF, its only voice message) answers one ask, so it goes with the person's
    // next move: typing in the composer, or a message sent or queued. A floating panel's also goes when it closes (below); a
    // pane never closes. The desktop shell's and the Office pane's dictation messages stay, as before, until their next dictation.
    const clearVoiceInputOff = () => { if (voiceInputRef.current === 'off')
        setVoiceError(''); };
    const [voiceMeter, setVoiceMeter] = useState();
    const voiceMeterRef = useRef(null);
    const loadingRef = useRef(false);
    /** What gyro-svc confirmed (2xx) it holds, so an unchanged transcript is not PUT again and a failed save is retried. */
    const historySaveRef = useRef(new HistorySaveTracker());
    // The last history save that did not go through for the conversation on screen; shown next to the composer.
    const [historySync, setHistorySync] = useState(null);
    /** Per chat: the identity-refused saves retried quietly so far, and the retry armed (historySave.ts identityRefusal). */
    const historyIdentityRetryRef = useRef(new Map());
    /** Per chat: the fingerprint of the transcript last sent, so a quiet retry never re-sends an older one over a newer save. */
    const historyLastAttemptRef = useRef(new Map());
    const clearHistoryIdentityRetry = (conversationId) => {
        const entry = historyIdentityRetryRef.current.get(conversationId);
        if (entry?.timer !== undefined)
            clearTimeout(entry.timer);
        historyIdentityRetryRef.current.delete(conversationId);
    };
    /** Why the last Fork chat / Fork from here did not happen, shown above the composer until the next change. */
    const [forkNotice, setForkNotice] = useState(null);
    const [forking, setForking] = useState(false);
    // Load outcome of the conversation on screen: send and autosave are refused until its transcript is in hand.
    const transcriptLoadRef = useRef(new TranscriptLoadGate());
    const [transcriptLoad, setTranscriptLoad] = useState({ sessionId: null, state: 'ready' });
    const historyWritesRef = useRef(new HistoryWriteQueue());
    const conversationTitlesRef = useRef(new ConversationTitles());
    const recognitionRef = useRef(null);
    useEffect(() => {
        setVoiceError('');
        return () => { recognitionRef.current?.cancel(); voiceMeterRef.current?.stop(); };
    }, [userKey, taskContextEpoch]);
    useEffect(() => {
        if (!open || hostVisibility === 'hidden') {
            recognitionRef.current?.cancel();
            voiceMeterRef.current?.stop();
        }
        // A web page's "not available here" (VOICE_INPUT_OFF, its only voice message) goes when its panel closes, if the person's
        // next move (clearVoiceInputOff) has not already taken it.
        if (!open && voiceInputRef.current === 'off')
            setVoiceError('');
    }, [open, hostVisibility]);
    useEffect(() => {
        const cancelWhenHidden = () => { if (document.hidden) {
            recognitionRef.current?.cancel();
            voiceMeterRef.current?.stop();
        } };
        document.addEventListener('visibilitychange', cancelWhenHidden);
        return () => document.removeEventListener('visibilitychange', cancelWhenHidden);
    }, []);
    const triggerVoiceRef = useRef(() => undefined);
    const toggleVoiceHookRef = useRef(() => undefined);
    // `send` is re-created every render (it closes over `busy`, `messages`, `mode`, …). Listeners that are
    // registered ONCE (the deps-[] host-hook effect) must call through this ref, never the closure they
    // captured on the first render — that closure has `busy === false` baked in and would start a second
    // stream on top of a running one (L-28). Same pattern as triggerVoiceRef / toggleVoiceHookRef.
    const sendRef = useRef(async () => undefined);
    // The StarScribe page's own ask (its selection or shortcut) for the next send only: send takes it before anything can return.
    const starscribeAskRef = useRef(undefined);
    // "Create my PPP" in one step (owner, 2026-10-25): the next send is the widget's own "Continue my PPP", and how many
    // it has sent in a row (reset by anything the person sends).
    const pppAutoRef = useRef(false);
    const pppContinuedRef = useRef(0);
    const localFileContinuationsRef = useRef(new LocalFileContinuations());
    useEffect(() => { localFileContinuationsRef.current.clear(); }, [userKey]);
    // The chat each action belongs to (actionConversations.ts): where an outcome that arrives on its own is recorded.
    const actionChatsRef = useRef(new ActionConversations());
    useEffect(() => { actionChatsRef.current.clear(); }, [userKey]);
    // Set only while continueFileTask sends a continuation the service has just said it reads (send() refuses any other);
    // and whether a Continue click is being handled now.
    const localFileEvidenceClearedRef = useRef(false);
    const localFileServiceCheckRef = useRef(false);
    const openSituationRef = useRef(() => undefined);
    const signOutRef = useRef(() => undefined);
    // The host-hook listener is registered once (deps-[] effect), so it reads the CURRENT origin
    // allowlist through this ref rather than the first render's prop value (L-29).
    const hostOriginsRef = useRef(props.hostOrigins);
    hostOriginsRef.current = props.hostOrigins;
    const memoryTextRef = useRef(null);
    const cancelRef = useRef(null);
    const listRef = useRef(null);
    const requestSendScroll = useSendScroll({ targetRef: listRef, enabled: workspace, scope: `${userKey}:${taskContextEpoch}` });
    const panelRef = useRef(null);
    // The panel beside the chat (sidePanel.ts, GyroSidePanel.tsx), with tabs; the web page preview is its first (owner,
    // 2026-10-22). The desktop app and the floating web panel, only while Gyro's window or panel is wide enough (never a pane,
    // never the compact widget, never a narrow window). Gyro's own width is measured, so a window made narrower hides it again.
    const sidePanelPossible = sidePanelSurface({ workspace, layout: props.layout });
    const [surfaceWidth, setSurfaceWidth] = useState(0);
    useEffect(() => {
        const element = panelRef.current;
        if (!sidePanelPossible || !open || !element || typeof ResizeObserver !== 'function')
            return undefined;
        // The last entry's width, or the element's own when a caller reports no entries.
        const observer = new ResizeObserver((entries) => {
            const last = entries?.[entries.length - 1];
            setSurfaceWidth(Math.round(last ? last.contentRect.width : element.clientWidth));
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [sidePanelPossible, open]);
    const sidePanelFits = sidePanelPossible && open && surfaceWidth >= SIDE_PANEL_MIN_SURFACE_WIDTH;
    // The panel's state and the API every tab uses (provided to everything below as SidePanelContext; useSidePanel()).
    const sidePanel = useSidePanelHost();
    const sidePanelApi = sidePanel.api;
    const htmlPreview = useHtmlPreview({
        enabled: sidePanelFits,
        messages,
        client,
        resolveLocal: documentStore.resolve,
        conversationKey: `${userKey}:${taskContextEpoch}`,
        open: sidePanelApi.activeTabId === PREVIEW_TAB_ID,
        selectedId: previewPayloadSource(sidePanelApi.payloadFor(PREVIEW_TAB_ID)),
        show: (id) => sidePanelApi.open(PREVIEW_TAB_ID, { sourceId: id }),
        close: sidePanelApi.close,
    });
    // Another person: the panel closes, whatever tab it shows.
    const sidePanelOwnerRef = useRef(userKey);
    useEffect(() => {
        if (sidePanelOwnerRef.current === userKey)
            return;
        sidePanelOwnerRef.current = userKey;
        sidePanelApi.close();
    }, [userKey, sidePanelApi]);
    // The Preview tab, registered through the same API as any other tab. Its header and body read the latest model.
    const previewModel = htmlPreview.selected ? {
        title: previewTitle(htmlPreview.selected.name),
        versions: versionOptions(htmlPreview.sources),
        selectedId: htmlPreview.selected.id,
        page: htmlPreview.page,
        renderKey: htmlPreview.renderKey,
        frameUrl: sealedFrameUrl({ desktopShell: desktopWorkspace && isDesktopShell(typeof window === 'undefined' ? undefined : window), baseUrl: client.baseUrl }),
        unavailableText: desktopWorkspace ? "Preview isn't available right now." : "Preview isn't available in this app.",
        onSelect: htmlPreview.openSource,
        onRefresh: htmlPreview.refresh,
    } : null;
    const previewModelRef = useRef(previewModel);
    previewModelRef.current = previewModel;
    useEffect(() => {
        if (!sidePanelPossible)
            return undefined;
        return sidePanelApi.register({
            id: PREVIEW_TAB_ID,
            label: 'Preview',
            headerExtras: () => _jsx(HtmlPreviewHeader, { model: previewModelRef.current }),
            render: () => _jsx(HtmlPreviewBody, { model: previewModelRef.current }),
        });
    }, [sidePanelPossible, sidePanelApi.register, HtmlPreviewHeader, HtmlPreviewBody]);
    const sidePanelShown = sidePanelFits && sidePanel.activeTab !== null && !showPets && !showNotifications;
    const sidePanelFullScreen = sidePanelShown && sidePanel.fullScreen;
    // One panel at the right at a time: the side panel closes the desktop's task panel (its toggle waits meanwhile).
    useEffect(() => { if (sidePanelShown)
        setTaskPanelOpen(false); }, [sidePanelShown]);
    // The desktop app makes room for the panel, whatever its tab (sidePanel.ts): a window too narrow for the chat and the panel
    // is widened by the native host, once per opening, and put back when the panel closes (unless the person moved it).
    const sidePanelWidenedRef = useRef(false);
    const sidebarDocked = sidebarOpen && !compactWorkspace;
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        if (sidePanelShown) {
            const needed = sidePanelWindowWidth({ sidebarDocked });
            if (sidePanelWidenedRef.current || surfaceWidth <= 0 || surfaceWidth >= needed)
                return;
            sidePanelWidenedRef.current = true;
            window.dispatchEvent(new CustomEvent(SIDE_PANEL_WIDEN_EVENT, { detail: { minWidthDip: needed } }));
        }
        else if (sidePanelWidenedRef.current) {
            sidePanelWidenedRef.current = false;
            window.dispatchEvent(new CustomEvent(SIDE_PANEL_RESTORE_EVENT));
        }
    }, [desktopWorkspace, sidePanelShown, sidebarDocked, surfaceWidth]);
    // StarScribe (docs/CONTRACT-starscribe-clients.md): its menu item and list, the answer button, the page cards, the page
    // beside the chat (the panel's tab, or tabs over the chat where the panel cannot show) and the ask field; only in the
    // desktop app, Gyro on the web and the Office pane while GET /v1/starscribe/me says it is on (the web entry's is off).
    const starscribe = parts.starscribe.useStarscribe({
        client, appKey, desktop: desktopWorkspace, layout: props.layout, userKey, onScreen, sidePanel: sidePanelApi, wide: sidePanelFits,
        theme: resolvedTheme, you: { name: preferences.displayName || props.user?.name || '', email: props.user?.email ?? '' },
        ask: (input) => { starscribeAskRef.current = input.starscribe; void sendRef.current(input.question); }, host: props.starscribeHost,
        mascotSrc: staticAvatar,
    });
    const settingsReturnFocusRef = useRef(null);
    const sidebarRef = useRef(null);
    const sidebarCloseRef = useRef(null);
    const sidebarToggleRef = useRef(null);
    const drawerWasOpenRef = useRef(false);
    const launcherRef = useRef(null);
    const inputRef = useRef(null);
    // Gyro on the web's Outfits window and sign-out (webPage above).
    const [outfitsOpen, setOutfitsOpen] = useState(false);
    const outfitsButtonRef = useRef(null);
    // As Settings closes (closeSettings): after a frame, focus goes back to Outfits while it can take it, else to the navigation
    // toggle, never the message box (on a phone that would raise the keyboard inside the tap that closed the window).
    const closeOutfits = useCallback(() => {
        setOutfitsOpen(false);
        requestAnimationFrame(() => {
            const opener = outfitsButtonRef.current;
            if (opener?.isConnected && opener.getClientRects().length && !opener.closest('[inert], [hidden]'))
                opener.focus();
            else
                sidebarToggleRef.current?.focus();
        });
    }, []);
    const [webSignOut, setWebSignOut] = useState('idle');
    const signOutOfWeb = async () => {
        setWebSignOut('busy');
        const result = await signOutOfGyroWeb({ baseUrl: client.baseUrl, assign: (url) => window.location.assign(url) });
        if (result === 'failed')
            setWebSignOut('failed');
    };
    const fileInputRef = useRef(null);
    useEffect(() => {
        if (!desktopWorkspace || !open || hostVisibility === 'hidden')
            return;
        const attachShortcut = (event) => {
            if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'u')
                return;
            // M-10: Ctrl/Cmd+U in a field of the page outside Gyro (underline, in an editor) is that field's.
            if (keyFromHostField(event, [panelRef.current, launcherRef.current]))
                return;
            // So is it in a StarScribe page (starscribe/tab.tsx): there it underlines.
            if (event.target instanceof Element && event.target.closest('[data-starscribe-page]'))
                return;
            if (settingsSection || paletteOpen || showPets || showNotifications || showSessions || showCapabilities || showMemory
                || busy || speechInputState !== 'idle' || attachmentReads > 0 || attachments.length >= ATTACHMENT_MAX_COUNT
                || panelRef.current?.querySelector('dialog[open]'))
                return;
            event.preventDefault();
            fileInputRef.current?.click();
        };
        document.addEventListener('keydown', attachShortcut);
        return () => document.removeEventListener('keydown', attachShortcut);
    }, [desktopWorkspace, open, hostVisibility, settingsSection, paletteOpen, showPets, showNotifications, showSessions, showCapabilities, showMemory, busy, speechInputState, attachmentReads, attachments.length]);
    const paletteInputRef = useRef(null);
    const historySearchRef = useRef(null);
    // `open` mirrored into a ref so async completion callbacks read the CURRENT collapsed state.
    const openRef = useRef(false);
    // True once the panel has been opened at least once. Gates the "return focus to the launcher on
    // close" effect so it never fires on the initial mount (where `open` is already false).
    const hasOpenedRef = useRef(false);
    const actionOpsRef = useRef(new Set());
    const claimedResultsRef = useRef(new Set());
    /** The requester-inbox poller (see the inbox effects below); null between client changes. */
    const inboxPollerRef = useRef(null);
    // ---- "Gyro, report this bug" -> The Lab (docs/HANDOFF-lab-bug-reporting.md) ----
    // Offered only when gyro-svc lists The Lab's filing tool for this person (the receiver is live and enabled).
    const labAvailable = capabilities.some((t) => t.name === LAB_FILE_TOOL);
    // ---- "Create my PPP" (ppp.ts, clients contract section 6) ----
    // The starter, the palette entry and the due line wait for GET /v1/ppp/me to say enabled and eligible; the read itself
    // is skipped when /v1/status says the feature is off for this person (a service without the field is asked anyway).
    const [pppStatus, setPppStatus] = useState(undefined);
    const [pppRefresh, setPppRefresh] = useState(0);
    // Per reply (pppReplyKey), never per list position: Open PDF's counter, and the reply whose block Copy as text copied.
    const [pppPdfOpen, setPppPdfOpen] = useState({});
    const [pppCopied, setPppCopied] = useState(null);
    // `enabled` is the service's answer only; `onScreen` decides when to read. A hidden panel keeps its answer, so the
    // starter and the due line do not vanish and reappear on every open (usePppMe drops the answer only when PPP is off).
    const ppp = usePppMe({ client, owner: userKey, enabled: configured === true && pppStatus !== false, onScreen, refreshKey: pppRefresh });
    const pppOn = pppAvailable(ppp);
    // ---- Saved skills (userSkillRoutes.ts; the plan's section d) ----
    // /v1/status's `skills` field is kept as it answered: without it nothing of the feature renders (the labAvailable
    // rule). The lists, and whether this identity may save here, come from GET /v1/skills through the hook; "Save as
    // skill" renders only when the service verified the identity on that read (a web host that proves only its app key
    // reads, inserts "run <name>", and never sees a Save it cannot complete). The same field and hook serve every surface.
    const [skillsStatus, setSkillsStatus] = useState(undefined);
    const gyroSkills = useGyroSkills({ client, status: skillsStatus, configured: configured === true, onScreen });
    const saveSkillOn = saveSkillOffered(skillsStatus, gyroSkills);
    // The open "Save as skill" box: under one reply (its key) or above the composer (the + menu's "Save a skill"). It is
    // rendered on this state alone, so a save refused for custody (which turns saving off) keeps the box and its reason.
    const [skillBox, setSkillBox] = useState(null);
    // The web widget's and the Office pane's skills list (GyroSkillsPanel.tsx); the desktop app lists them in its + menu.
    const [skillsPanelOpen, setSkillsPanelOpen] = useState(false);
    const captureMode = props.captureMode === 'host' ? 'host' : 'tab';
    const evidenceUploader = useMemo(() => props.evidenceUploader ?? fetchEvidenceUploader(), [props.evidenceUploader]);
    const evidenceUploaderRef = useRef(evidenceUploader);
    evidenceUploaderRef.current = evidenceUploader;
    // Report mode: set by "report this bug" (or the Report a bug control) and cleared once a report is filed or the
    // chat is reset. Only report-mode turns carry the where-it-happened context (owner decision D8).
    const [reportMode, setReportModeState] = useState(false);
    const reportModeRef = useRef(false);
    /** Turns sent in report mode since the last Lab proposal; report mode ends by itself after REPORT_MODE_IDLE_TURNS. */
    const reportIdleTurnsRef = useRef(0);
    const setReportMode = (on) => {
        reportModeRef.current = on;
        reportIdleTurnsRef.current = 0;
        setReportModeState(on);
    };
    /** A finished turn: a Lab proposal keeps report mode alive; a run of turns without one ends it (D8). */
    const noteReportTurn = (proposedLab) => {
        if (!reportModeRef.current)
            return;
        if (proposedLab) {
            reportIdleTurnsRef.current = 0;
            return;
        }
        if (++reportIdleTurnsRef.current >= REPORT_MODE_IDLE_TURNS)
            setReportMode(false);
    };
    /** The desktop agent's record of the window that was in front before the overlay opened (gyro:report-context). */
    const hostReportContextRef = useRef(null);
    /** Images snapshotted when a Lab write was confirmed, waiting for its upload link. In memory only. */
    const pendingUploadsRef = useRef(new Map());
    /** The Lab's answer per filed action, so a late (inbox) result claim still has the report id for its receipt. */
    const filedReportsRef = useRef(new Map());
    const [labPreviews, setLabPreviews] = useState({});
    const labPreviewRequestedRef = useRef(new Set());
    const [capturing, setCapturing] = useState(false);
    const captureTimerRef = useRef(undefined);
    const openSettings = useCallback((section = 'general', returnFocus) => {
        if (!desktopWorkspace)
            return;
        settingsReturnFocusRef.current = returnFocus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        setPaletteOpen(false);
        setSidebarOpen(current => window.innerWidth <= 760 ? false : current);
        setSettingsSection(section);
    }, [desktopWorkspace]);
    const closeSettings = useCallback(() => {
        setSettingsSection(null);
        requestAnimationFrame(() => {
            const target = settingsReturnFocusRef.current;
            if (target?.isConnected && target.getClientRects().length && !target.closest('[inert]'))
                target.focus();
            else
                panelRef.current?.querySelector('.gyro-account-header-button, .gyro-desktop-sidebar-toggle')?.focus();
        });
    }, []);
    useEffect(() => { setSettingsSection(null); }, [userKey]);
    // "Change outfit…" in the desktop's right-click menu (owner, 2026-10-05; hostSettingsRequest.ts): the shell asks for a
    // Settings section, which opens even when Settings is already open on another one (the count remounts it there). The
    // event is cancelled once handled, so the shell stops holding it; one that came before this listener is asked for.
    const [hostSettingsRequests, setHostSettingsRequests] = useState(0);
    useEffect(() => {
        if (!desktopWorkspace)
            return undefined;
        const onOpenSettings = (event) => {
            const section = parseHostSettingsRequest(event.detail);
            if (!section)
                return;
            event.preventDefault();
            setOpen(true);
            setHostSettingsRequests(count => count + 1);
            openSettings(section);
        };
        window.addEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
        try {
            window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_REQUEST_EVENT));
        }
        catch { /* ignore */ }
        return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    }, [desktopWorkspace, openSettings]);
    useEffect(() => {
        const wasOpen = drawerWasOpenRef.current;
        drawerWasOpenRef.current = desktopDrawerOpen;
        if (desktopDrawerOpen)
            sidebarCloseRef.current?.focus();
        else if (wasOpen && open && workspace)
            sidebarToggleRef.current?.focus();
    }, [desktopDrawerOpen, open, workspace]);
    const beginActionOp = (id, operation) => {
        if (actionOpsRef.current.has(id))
            return false;
        actionOpsRef.current.add(id);
        setActionOps((current) => ({ ...current, [id]: operation }));
        return true;
    };
    const endActionOp = (id) => {
        actionOpsRef.current.delete(id);
        setActionOps((current) => {
            const next = { ...current };
            delete next[id];
            return next;
        });
    };
    useEffect(() => {
        if (!onScreen)
            return;
        setNowMs(Date.now());
        const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [onScreen]);
    // The day's greeting is read again from /v1/status when it ends or the local date changes (dayGreeting.ts dayStatusDue),
    // on the 1 s clock above. One read at a time; a failed one waits DAY_REFRESH_RETRY_MS. Never cancelled by this effect,
    // which runs every second: its answer is applied only for the identity that asked.
    useEffect(() => {
        if (!onScreen || configured === null)
            return; // the status check below owns the first answer
        const refresh = dayRefreshRef.current;
        if (refresh.inFlight || nowMs < refresh.retryAtMs || !dayStatusDue(dayGreeting, dayGreetingAtRef.current, nowMs))
            return;
        refresh.inFlight = true;
        const owner = userKeyRef.current;
        client.status().then((s) => {
            if (userKeyRef.current === owner) {
                applyDayGreeting(dayGreetingFromPersona(s.persona));
                setPetsCapability(previous => previous?.serviceKey === petServiceKey && previous.userKey === owner && previous.epoch === petSessionEpoch ? { ...previous, enabled: s.petsEnabled === true } : previous);
            }
        }, () => { refresh.retryAtMs = Date.now() + DAY_REFRESH_RETRY_MS; }).finally(() => { refresh.inFlight = false; });
    }, [client, onScreen, configured, nowMs, dayGreeting, applyDayGreeting, petSessionEpoch, petServiceKey]);
    // ---- Attachments: supported files, capped to mirror the service limits. ----
    useEffect(() => {
        const field = inputRef.current;
        if (!field)
            return;
        field.style.height = 'auto';
        field.style.height = `${Math.min(160, field.scrollHeight)}px`;
    }, [input, open, showNotifications, showSessions, showMemory, showCapabilities, expanded, sidebarOpen, taskPanelOpen, compactDesktop]);
    const addFiles = async (files) => {
        if (busy || !files.length)
            return;
        track('gyro_file_attached', { app: appKey, count: files.length });
        const epoch = attachmentEpochRef.current;
        attachmentPendingRef.current++;
        setAttachmentReads(attachmentPendingRef.current);
        attachmentQueueRef.current = attachmentQueueRef.current.then(async () => {
            if (epoch !== attachmentEpochRef.current)
                return;
            setAttachError('');
            const { accepted, errors } = await prepareAttachments(files, attachmentsRef.current, { fileAttachments: fileAttachmentsSupported });
            if (epoch !== attachmentEpochRef.current)
                return;
            const next = [...attachmentsRef.current, ...accepted];
            attachmentsRef.current = next;
            setAttachments(previous => epoch === attachmentEpochRef.current ? next : previous);
            setAttachError(errors.join(' · '));
        }).catch(() => {
            if (epoch === attachmentEpochRef.current)
                setAttachError('These files could not be added. Try again.');
        }).finally(() => {
            if (epoch !== attachmentEpochRef.current)
                return;
            attachmentPendingRef.current = Math.max(0, attachmentPendingRef.current - 1);
            setAttachmentReads(attachmentPendingRef.current);
        });
        await attachmentQueueRef.current;
    };
    const removeAttachment = (idx) => {
        attachmentsRef.current = attachmentsRef.current.filter((_, i) => i !== idx);
        setAttachments(attachmentsRef.current);
    };
    const clearComposer = () => {
        attachmentEpochRef.current++;
        attachmentQueueRef.current = Promise.resolve();
        attachmentPendingRef.current = 0;
        attachmentsRef.current = [];
        setAttachmentReads(0);
        setAttachments([]);
        setAttachError('');
        setInput('');
    };
    const desktopDrop = useDesktopDrop({
        enabled: desktopWorkspace && onScreen,
        scope: `${userKey}:${taskContextEpoch}`,
        blockedReason: settingsSection || paletteOpen ? 'Close this dialog to add content to your message.'
            : showPets || showNotifications || showSessions || showMemory || showCapabilities ? 'Open a conversation to add content.'
                : busy ? 'Finish or stop the current response before adding content.' : undefined,
        filesFull: attachments.length >= ATTACHMENT_MAX_COUNT,
        onFiles: files => { void addFiles(files); requestAnimationFrame(() => inputRef.current?.focus()); },
        onText: text => {
            const next = appendDroppedText(input, text);
            setInput(next);
            setAttachError('');
            requestAnimationFrame(() => inputRef.current?.focus());
        },
        onError: setAttachError,
    });
    const loadSessions = () => {
        try {
            const raw = localStorage.getItem(sessionsKey);
            return raw ? JSON.parse(raw) : [];
        }
        catch {
            return [];
        }
    };
    const persistSessions = (next) => {
        try {
            localStorage.setItem(sessionsKey, JSON.stringify(next.slice(0, 50)));
        }
        catch {
            /* ignore quota */
        }
    };
    const deriveTitle = (msgs) => (msgs.find((m) => m.role === 'user')?.content ?? 'New chat').slice(0, 60);
    /**
     * Mirror a conversation to gyro-svc and say how it went. A refusal of the transcript itself (413 too large, 400, 507
     * storage full) is remembered so the same bytes are not PUT again on every re-render, and every failure is shown next
     * to the composer (historySync): a save that did not happen is never silent.
     */
    const saveChatHistory = async (conversation) => {
        const fingerprint = transcriptFingerprint(conversation.id, conversation.title, conversation.turns);
        const tracker = historySaveRef.current;
        const owner = userKey;
        historyLastAttemptRef.current.set(conversation.id, fingerprint);
        let outcome;
        const ok = await tracker.save(conversation.id, fingerprint, () => historyWritesRef.current.run(async () => {
            outcome = await client.saveHistoryOutcome(conversation);
            return outcome.ok;
        }, conversation.id));
        if (userKeyRef.current !== owner)
            return { ok, outcome };
        if (ok) {
            clearHistoryIdentityRetry(conversation.id);
            setHistorySync(current => (current?.sessionId === conversation.id ? null : current));
        }
        else if (outcome) {
            if (!outcome.retryable)
                tracker.markRefused(conversation.id, fingerprint);
            // A refusal for want of identity is tried again quietly a few times before the person is told (historySave.ts
            // identityRefusal); a retry re-sends this transcript only while it is still the latest one tried for the chat.
            if (identityRefusal(outcome)) {
                const attempts = historyIdentityRetryRef.current.get(conversation.id)?.attempts ?? 0;
                const delay = historyIdentityRetry.delaysMs[attempts];
                if (delay !== undefined) {
                    clearHistoryIdentityRetry(conversation.id);
                    const timer = setTimeout(() => {
                        historyIdentityRetryRef.current.set(conversation.id, { attempts: attempts + 1 });
                        if (userKeyRef.current === owner && historyLastAttemptRef.current.get(conversation.id) === fingerprint)
                            void saveChatHistory(conversation);
                    }, delay);
                    historyIdentityRetryRef.current.set(conversation.id, { attempts, timer });
                    return { ok, outcome };
                }
            }
            clearHistoryIdentityRetry(conversation.id);
            setHistorySync({ sessionId: conversation.id, state: outcome.retryable ? 'failed' : 'refused', message: historySaveMessage(outcome, desktopWorkspace), reason: outcome.error });
        }
        return { ok, outcome };
    };
    const renameCurrentChat = async (title) => {
        const owner = userKey;
        const now = Date.now();
        const id = sessionIdRef.current ?? `s-${now}-${Math.floor(Math.random() * 100000)}`;
        // Rename goes through the same gate as send and autosave: a transcript that is loading or failed to load is not
        // written, not even its title, because the PUT carries the whole conversation.
        const refusal = transcriptLoadRef.current.refusalMessage(id);
        if (refusal)
            return { error: refusal };
        const original = sessions.find(session => session.id === id);
        const currentRun = taskRunRef.current;
        let turns = savedTurns(messages);
        // A remote transcript may still be loading (or its load may have failed). Never rename it by
        // writing an empty/partial transcript over the saved conversation: the screen is trusted only when this widget
        // holds the conversation and shows at least one of its turns.
        if (original?.remote && (!conversationsRef.current.get(id) || turns.length === 0)) {
            const full = await client.getHistory(id);
            if (!full || userKeyRef.current !== owner || taskRunRef.current !== currentRun || sessionIdRef.current !== id)
                return false;
            turns = full.turns;
        }
        if (userKeyRef.current !== owner)
            return false;
        const meta = conversationTitlesRef.current.describe(id, title, original?.createdAt ?? now);
        const renamed = conversationTitlesRef.current.rename(id, title, meta.createdAt, now);
        sessionIdRef.current = id;
        const saved = { id, title: renamed, messages: turns, createdAt: meta.createdAt, updatedAt: now, usage: conversationUsageRef.current.saved(id) ?? original?.usage };
        setSessions(previous => {
            const next = [saved, ...previous.filter(session => session.id !== id)];
            persistSessions(next.filter(session => !session.remote));
            return next;
        });
        // Local naming works offline; the inline editor shows why a sync was refused and offers retry.
        const sync = await saveChatHistory({ id, title: renamed, turns, createdAt: meta.createdAt, updatedAt: now, ...(original?.forkedFrom ? { forkedFrom: original.forkedFrom } : {}) });
        if (sync.ok)
            return true;
        return sync.outcome ? { error: historySaveMessage(sync.outcome, desktopWorkspace) } : false;
    };
    /** When the chat list was last read from gyro-svc (Date.now()); see HISTORY_RESYNC_MS. */
    const historySyncAtRef = useRef(0);
    /**
     * Merge local cached sessions with server-synced ones (server fills in chats from other devices). A chat saved later on
     * another device than here takes the server's copy, listed as remote so opening it reads that transcript; a copy this
     * desktop holds of it is let go, unless it is on screen or a reply is still being written into it.
     */
    const syncRemoteHistory = async (search) => {
        historySyncAtRef.current = Date.now();
        try {
            const remote = (await client.listHistory(search)).filter(session => !historyWritesRef.current.isRemoved(session.id));
            if (userKeyRef.current !== userKey)
                return;
            for (const session of remote)
                conversationTitlesRef.current.remember(session);
            setSessions((local) => {
                const byId = new Map(local.filter((s) => !s.remote).map((s) => [s.id, { ...s, title: conversationTitlesRef.current.describe(s.id, s.title, s.createdAt).title }]));
                const merged = [...byId.values()];
                for (const r of remote) {
                    const mine = byId.get(r.id);
                    if (mine && mine.updatedAt >= r.updatedAt)
                        continue;
                    const entry = { id: r.id, title: r.title, messages: [], createdAt: r.createdAt, updatedAt: r.updatedAt, remote: true, turnCount: r.turnCount, ...(r.app ? { app: r.app } : {}) };
                    if (!mine) {
                        merged.push(entry);
                        continue;
                    }
                    merged[merged.indexOf(mine)] = entry;
                    const held = conversationsRef.current.get(r.id);
                    if (held && held.state !== 'running' && r.id !== sessionIdRef.current)
                        conversationsRef.current.remove(r.id);
                }
                persistSessions(merged.filter((s) => !s.remote));
                return merged.sort((a, b) => b.updatedAt - a.updatedAt);
            });
        }
        catch {
            /* server history optional — local cache still works */
        }
    };
    const stopAudio = () => {
        speech.stop();
    };
    const stopReplySpeech = () => {
        // If the reply is still streaming, its final text must not restart speech the user just stopped.
        speechSuppressedRunRef.current = conversationsRef.current.get(sessionIdRef.current ?? '')?.value.runId ?? taskRunRef.current;
        stopAudio();
    };
    const speakReply = async (text) => {
        await speech.speak(text);
    };
    const switchMode = (next) => {
        if (next === mode)
            return;
        try {
            localStorage.setItem(modeKey, next);
        }
        catch {
            /* ignore */
        }
        setMode(next);
        if (next === 'voice') {
            speechSuppressedRunRef.current = null;
            const last = [...messages].reverse().find((m) => m.role === 'assistant' && !m.pending && !m.error && !m.note);
            if (last && !looksSensitive(last.content) && !last.classified)
                void speakReply(last.content);
        }
        else {
            stopAudio();
        }
    };
    /** Forget every bug-report draft state (a new chat, another identity). */
    const resetBugReport = () => {
        setReportMode(false);
        pendingUploadsRef.current.clear();
        filedReportsRef.current.clear();
        labPreviewRequestedRef.current.clear();
        setLabPreviews({});
    };
    const rememberConversation = () => {
        const id = sessionIdRef.current;
        if (!desktopWorkspace || !id)
            return;
        // A conversation whose transcript is still loading, or failed to load, shows only the greeting, which is not its
        // content. Cached, it would reopen as an empty chat with the gate reset to ready, and the next send, autosave or
        // rename would replace the saved conversation (audit C review, round 2).
        if (!transcriptLoadRef.current.canWrite(id))
            return;
        const previous = conversationsRef.current.get(id);
        const snapshot = { owner: userKey, messages, documents: documentStore, activity: taskActivity,
            sources: taskSources, artifacts: taskArtifacts, runtime: taskRuntime, actions: taskActions,
            attachments: taskAttachments, folder: workingFolder, queued, runId: previous?.value.runId ?? taskRunRef.current, resolved };
        // Stream callbacks update their record synchronously, ahead of React's next paint.
        conversationsRef.current.remember(id, previous?.state === 'running'
            ? { ...previous.value, documents: documentStore, folder: workingFolder, queued, resolved } : snapshot);
    };
    const newSession = () => {
        setShowPets(false);
        rememberConversation();
        setRenameRequest(null);
        clearComposer();
        setModelTier('balanced'); // a new chat never inherits High (owner, 2026-10-17)
        setEffort(''); // nor the thinking level: the model's own default again
        setTierNote('');
        setShowNotifications(false);
        setTaskContextEpoch(epoch => epoch + 1);
        taskRunRef.current++;
        // The in-page widget cancels a reply still streaming; what it spent stays with its chat. (The desktop keeps it running.)
        if (!desktopWorkspace)
            countInterruptedTurn(sessionIdRef.current, messages);
        if (!desktopWorkspace)
            cancelRef.current?.();
        cancelRef.current = null;
        setBusy(false);
        setQueued([]);
        stopAudio();
        setTaskActivity(null);
        setWorkingFolder(null);
        setTaskSources([]);
        setTaskArtifacts([]);
        setTaskRuntime(undefined);
        setTaskAttachments([]);
        setTaskActions([]);
        sessionIdRef.current = null;
        setTranscriptLoad(transcriptLoadRef.current.reset());
        setSessionUsage(EMPTY_SESSION_USAGE); // a new chat's total starts at zero
        loadingRef.current = true;
        setMessages([greeting]);
        setResolved({});
        setPreviews({});
        setApprovalSteps({});
        resetBugReport();
        dispatchShare({ type: 'cancel' }); // share keys are per-message positions — never carry one across chats
        setShowSessions(false);
    };
    /** `fresh`: fetch the transcript again even if this widget holds a copy (the failed-load banner's Retry). */
    const loadSession = async (s, options = {}) => {
        setShowPets(false);
        setRenameRequest(null);
        conversationTitlesRef.current.remember(s);
        rememberConversation();
        clearComposer();
        setModelTier('balanced'); // a chat opened from the list starts at Balanced too, whatever the last chat ran at
        setEffort('');
        setTierNote('');
        setShowNotifications(false);
        setTaskContextEpoch(epoch => epoch + 1);
        const sessionRun = ++taskRunRef.current;
        if (!desktopWorkspace)
            countInterruptedTurn(sessionIdRef.current, messages);
        if (!desktopWorkspace)
            cancelRef.current?.();
        cancelRef.current = null;
        setBusy(false);
        setQueued([]);
        stopAudio();
        setTaskActivity(null);
        setWorkingFolder(null);
        setTaskSources([]);
        setTaskArtifacts([]);
        setTaskRuntime(undefined);
        setTaskAttachments([]);
        setTaskActions([]);
        sessionIdRef.current = s.id;
        // That chat's own total: what this session counted, else what the local cache kept for it, else zero.
        setSessionUsage(conversationUsageRef.current.seed(s.id, s.usage));
        loadingRef.current = true;
        setTranscriptLoad(transcriptLoadRef.current.reset());
        const held = desktopWorkspace ? conversationsRef.current.get(s.id) : undefined;
        // Retry fetches again: a held copy is dropped, unless a reply is still being written into it.
        const refetch = options.fresh === true && held?.state !== 'running';
        if (refetch && held)
            conversationsRef.current.remove(s.id);
        const cached = refetch ? undefined : held;
        if (cached?.value.owner === userKey) {
            const saved = cached.value;
            setMessages(saved.messages);
            setBusy(cached.state === 'running');
            cancelRef.current = cached.cancel;
            streamBufRef.current = saved.messages.find(message => message.streaming)?.content ?? '';
            setTaskActivity(saved.activity);
            setTaskSources(saved.sources);
            setTaskArtifacts(saved.artifacts);
            setTaskRuntime(saved.runtime);
            setTaskActions(saved.actions);
            setTaskAttachments(saved.attachments);
            setWorkingFolder(saved.folder);
            setQueued(saved.queued);
            setResolved(saved.resolved ?? {});
            setPreviews({});
            setApprovalSteps({});
            dispatchShare({ type: 'cancel' });
            setShowSessions(false);
            return;
        }
        // Retire old reply components before remote history awaits; they must not export into a new store.
        setMessages([greeting]);
        let turns = s.messages;
        let fromServer = false;
        let serverPay = false;
        if (s.remote || turns.length === 0) {
            // Read-only until the fetch settles: a send or an autosave now would replace the saved conversation.
            setTranscriptLoad(transcriptLoadRef.current.begin(s.id));
            const load = await client.loadHistory(s.id);
            if (taskRunRef.current !== sessionRun)
                return;
            const decision = decideTranscriptLoad(s, load);
            if (!decision.writable) {
                // Failed, or a listed chat the server now says it does not hold: keep the greeting, keep the session marked
                // remote, write nothing. The banner offers Retry and New chat.
                setTranscriptLoad(transcriptLoadRef.current.fail(s.id));
                setShowSessions(false);
                return;
            }
            turns = decision.turns;
            fromServer = decision.fromServer;
            setTranscriptLoad(transcriptLoadRef.current.succeed(s.id));
            // A chat forked on the server remembers its origin; a listing that did not carry it learns it from the transcript.
            const forkedFrom = load.status === 'loaded' ? load.conversation.forkedFrom : undefined;
            if (forkedFrom && !s.forkedFrom)
                setSessions(previous => previous.map(item => (item.id === s.id ? { ...item, forkedFrom } : item)));
            // The service says the chat holds pay data: remembered here too, for a copy this browser opens later from its cache.
            serverPay = load.status === 'loaded' && load.conversation.classified === true;
            if (serverPay)
                rememberPayChatHere(s.id);
        }
        // A chat that holds pay data (the service said so, or this browser saw a pay answer in it) keeps every answer marked
        // after a reload: none is read aloud, shared by link or saved as a skill (owner, 2026-10-23: "Keep off").
        let payChat = serverPay;
        try {
            payChat ||= readPayChats(localStorage, payChatsKey).has(s.id);
        }
        catch { /* no storage */ }
        const markPay = (m) => (payChat && m.role === 'assistant' ? { ...m, classified: true } : m);
        const loaded = [greeting, ...turns.filter((m) => m.content !== greeting.content).map(markPay)];
        if (fromServer) {
            // gyro-svc already holds exactly this transcript: viewing it is not a change, so it is not saved back. The
            // fingerprint is derived exactly as the autosave derives it (savedTurns, the same title).
            const title = conversationTitlesRef.current.describe(s.id, deriveTitle(loaded), s.createdAt).title;
            historySaveRef.current.markSaved(s.id, transcriptFingerprint(s.id, title, savedTurns(loaded)));
        }
        // The autosave effect skips the render a load causes; re-arm it for this one (the greeting render above used it).
        loadingRef.current = true;
        setMessages(loaded);
        setLoadedSessionRevision((revision) => revision + 1);
        setResolved({});
        setPreviews({});
        setApprovalSteps({});
        dispatchShare({ type: 'cancel' });
        setShowSessions(false);
    };
    const removeSession = async (id) => {
        const owner = userKey;
        const deleted = await historyWritesRef.current.remove(id, () => client.deleteHistory(id));
        if (!deleted || userKeyRef.current !== owner)
            return false;
        petSetup.handoffs.forgetConversation(id);
        conversationTitlesRef.current.remove(id);
        conversationUsageRef.current.remove(id);
        actionChatsRef.current.forgetConversation(id);
        conversationsRef.current.get(id)?.value.documents.clear();
        conversationsRef.current.remove(id);
        setSessions((prev) => {
            const next = prev.filter((s) => s.id !== id);
            persistSessions(next.filter((s) => !s.remote));
            return next;
        });
        if (sessionIdRef.current === id) {
            sessionIdRef.current = null;
            newSession();
        }
        return true;
    };
    // A chat deleted from its options menu takes its row, and the menu's own way back to it, along: focus moves to the next chat
    // in the list, else New chat, else the composer, never to the page's body (where Tab would leave Gyro for the page).
    const refocusAfterChatRemovalRef = useRef(false);
    const removeChatFromMenu = async (id) => {
        refocusAfterChatRemovalRef.current = true;
        const removed = await removeSession(id);
        if (!removed)
            refocusAfterChatRemovalRef.current = false;
        return removed;
    };
    useEffect(() => {
        if (!refocusAfterChatRemovalRef.current)
            return;
        refocusAfterChatRemovalRef.current = false;
        if (!isPageBody(document.activeElement))
            return;
        const panel = panelRef.current;
        (panel?.querySelector('.gyro-desktop-recent .gyro-desktop-session, .gyro-sessions .gyro-session-open')
            ?? panel?.querySelector('.gyro-desktop-new') ?? inputRef.current)?.focus({ preventScroll: true });
    }, [sessions]);
    const openChatMenu = (session, opener, point) => {
        const rect = opener.getBoundingClientRect();
        setChatMenu({ session, opener, x: point?.x ?? rect.right - 12, y: point?.y ?? rect.bottom + 4, key: Date.now() });
    };
    const chatMenuKey = (event, session) => {
        if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
            event.preventDefault();
            event.stopPropagation();
            openChatMenu(session, event.currentTarget);
        }
    };
    const renameSessionFromMenu = async (session) => {
        const owner = userKey;
        showChat();
        if (sessionIdRef.current !== session.id)
            await loadSession(session);
        if (userKeyRef.current !== owner || sessionIdRef.current !== session.id)
            return false;
        closeMobileSidebar();
        setRenameRequest({ id: session.id, revision: Date.now() });
        return true;
    };
    const openFreshSession = async (source) => {
        const owner = userKey;
        const currentRun = taskRunRef.current;
        const cached = conversationsRef.current.get(source.id);
        const liveMessages = source.id === sessionIdRef.current ? messages : cached?.value.owner === owner ? cached.value.messages : undefined;
        let turns = liveMessages ? liveMessages.filter(message => !message.welcome && !message.note && !message.pending && !message.streaming && !message.error) : source.messages;
        if ((source.remote && !cached) || (!liveMessages && source.messages.length === 0)) {
            const full = await client.getHistory(source.id);
            if (!full)
                return false;
            turns = full.turns;
        }
        if (userKeyRef.current !== owner || taskRunRef.current !== currentRun)
            return false;
        turns = turns.filter(turn => turn.content !== greeting.content).map(turn => ({ role: turn.role, content: turn.content }));
        compactRequestRef.current?.abort();
        const controller = new AbortController();
        compactRequestRef.current = controller;
        // Compaction runs on the service's default model: no pick travels with it (the chat's tier is for its asks alone).
        const result = turns.length ? await client.compactConversation({ conversationId: source.id, turns }, { signal: controller.signal }) : undefined;
        if (userKeyRef.current !== owner || taskRunRef.current !== currentRun || historyWritesRef.current.isRemoved(source.id))
            return false;
        const now = Date.now();
        const id = `s-${now}-${crypto.randomUUID()}`;
        const title = conversationTitlesRef.current.rename(id, `${source.title} (continued)`, now);
        const summaryTurns = result ? [{ role: 'assistant', content: `## Conversation handoff\n\n${result.summary}\n\n*Continued in a fresh session from a compacted summary. Original messages and files remain in the previous chat.*` }] : [];
        if (!(await saveChatHistory({ id, title, turns: summaryTurns, createdAt: now, updatedAt: now })).ok) {
            conversationTitlesRef.current.remove(id);
            return false;
        }
        if (userKeyRef.current !== owner || taskRunRef.current !== currentRun)
            return false;
        const session = { id, title, messages: summaryTurns, createdAt: now, updatedAt: now };
        setSessions(previous => { const next = [session, ...previous]; persistSessions(next.filter(item => !item.remote)); return next; });
        showChat();
        await loadSession(session);
        closeMobileSidebar();
        requestAnimationFrame(() => inputRef.current?.focus());
        return true;
    };
    /**
     * Fork a chat (owner ask 2026-10-17): a new chat that holds the source's turns, every one ("Fork chat") or up to and
     * including `upToTurn` ("Fork from here" on a turn), opened as the active chat with the title "Fork of <source>". The
     * source chat is not changed. A chat whose latest turns are not saved yet is saved first through the same path as the
     * autosave; the server then copies what it holds. Refused while a reply is still streaming (the transcript would move
     * under the copy), and when the transcript is not writable (loading, or its load failed).
     */
    const forkSession = async (source, upToTurn) => {
        /** The fork's answer or error, handed out of the boolean HistoryWriteQueue (one fork runs at a time). */
        let forkedResult;
        let forkedError;
        const owner = userKey;
        const currentRun = taskRunRef.current;
        const live = source.id === sessionIdRef.current;
        const held = conversationsRef.current.get(source.id);
        if ((live && busy) || held?.state === 'running') {
            setForkNotice({ sessionId: source.id, message: 'Wait for Gyro to finish this reply before forking the chat.' });
            return false;
        }
        if (live && !transcriptLoadRef.current.canWrite(source.id)) {
            setForkNotice({ sessionId: source.id, message: 'This chat has not finished loading, so it cannot be forked yet.' });
            return false;
        }
        const liveMessages = live ? messages : held?.value.owner === owner ? held.value.messages : undefined;
        if (liveMessages) {
            // What is on screen is what gets forked: save it first (a no-op when gyro-svc already holds exactly this).
            const turns = savedTurns(liveMessages);
            if (turns.length === 0) {
                setForkNotice({ sessionId: source.id, message: 'Send a message first; an empty chat has nothing to fork.' });
                return false;
            }
            const now = Date.now();
            const { title, createdAt } = conversationTitlesRef.current.describe(source.id, deriveTitle(liveMessages), source.createdAt ?? now);
            const sync = await saveChatHistory({ id: source.id, title, turns, createdAt, updatedAt: now, ...(source.forkedFrom ? { forkedFrom: source.forkedFrom } : {}) });
            if (userKeyRef.current !== owner || taskRunRef.current !== currentRun)
                return false;
            if (!sync.ok) {
                setForkNotice({ sessionId: source.id, message: sync.outcome ? `The chat could not be saved, so it was not forked. ${historySaveMessage(sync.outcome, desktopWorkspace)}` : 'The chat could not be saved, so it was not forked. Try again in a moment.' });
                return false;
            }
        }
        let forked;
        try {
            setForking(true);
            forked = await historyWritesRef.current.run(() => client.forkHistory({ id: source.id, ...(upToTurn !== undefined ? { upToTurn } : {}) })
                .then((result) => { forkedResult = result; return true; }, (error) => { forkedError = error; return false; }))
                .then((ok) => { if (!ok || !forkedResult)
                throw forkedError ?? new ForkError('fork_unavailable', 0); return forkedResult; });
        }
        catch (error) {
            if (userKeyRef.current === owner)
                setForkNotice({ sessionId: source.id, message: error instanceof ForkError ? error.message : new ForkError('fork_unavailable', 0).message });
            return false;
        }
        finally {
            forkedResult = undefined;
            forkedError = undefined;
            setForking(false);
        }
        if (userKeyRef.current !== owner || taskRunRef.current !== currentRun)
            return false;
        const now = forked.forkedFrom?.at ?? Date.now();
        conversationTitlesRef.current.remember({ id: forked.id, title: forked.title, createdAt: now, updatedAt: now });
        const session = { id: forked.id, title: forked.title, messages: [], createdAt: now, updatedAt: now, remote: true, turnCount: forked.turnCount, ...(forked.forkedFrom ? { forkedFrom: forked.forkedFrom } : {}) };
        setForkNotice(null);
        setSessions(previous => [session, ...previous.filter(item => item.id !== session.id)]);
        showChat();
        await loadSession(session);
        closeMobileSidebar();
        requestAnimationFrame(() => inputRef.current?.focus());
        return true;
    };
    /** The chat on screen, as a Session for forkSession (it may not be in the list yet when nothing has been saved). */
    const currentSessionForFork = () => {
        const id = sessionIdRef.current;
        if (!id)
            return null;
        return sessions.find(session => session.id === id) ?? { id, title: deriveTitle(messages), messages: [], createdAt: Date.now(), updatedAt: Date.now() };
    };
    /** "Fork chat" on the header: the whole chat on screen. Nothing happens on a chat with no sent message yet. */
    const forkCurrentChat = async () => {
        const current = currentSessionForFork();
        if (!current) {
            setForkNotice({ sessionId: '', message: 'Send a message first; an empty chat has nothing to fork.' });
            return false;
        }
        return forkSession(current);
    };
    /** "Fork from here" on an assistant turn: the chat up to and including that turn (`index` counts saved turns, greeting excluded). */
    const forkFromTurn = async (messageIndex) => {
        const current = currentSessionForFork();
        if (!current)
            return false;
        // The saved transcript leaves out the greeting and anything pending, streaming or failed; the turn's index in it
        // is the number of saved turns before it.
        const saved = savedTurns(messages.slice(0, messageIndex));
        const turn = messages[messageIndex];
        if (!turn || turn.pending || turn.streaming || turn.error || turn.welcome || turn.note)
            return false;
        return forkSession(current, saved.length);
    };
    /** (Re)load the memory note. A failed load blocks Save so a blank editor can't wipe the real note. */
    const loadMemory = async () => {
        setMemorySaved(false);
        setMemoryError('');
        setMemoryLoadFailed(false);
        setMemoryLoading(true);
        const m = await client.getMemory();
        setMemoryLoading(false);
        if (m) {
            setMemoryText(m.text);
            setTimeout(() => memoryTextRef.current?.focus(), 50);
        }
        else {
            setMemoryText('');
            setMemoryLoadFailed(true);
            setMemoryError("Couldn't load your memory — check your connection and try again.");
        }
    };
    const toggleMemory = async () => {
        const opening = !showMemory;
        setShowMemory(opening);
        setShowSessions(false);
        setShowCapabilities(false);
        if (opening)
            await loadMemory();
    };
    const saveMemory = async () => {
        if (memorySaving)
            return;
        setMemorySaving(true);
        setMemoryError('');
        const saved = await client.saveMemory(memoryText.slice(0, MEMORY_MAX_CHARS));
        setMemorySaving(false);
        if (saved === null) {
            setMemoryError("Couldn't save — please try again.");
            return;
        }
        setMemoryText(saved);
        setMemorySaved(true);
        track('gyro_memory_saved', { app: appKey });
    };
    /** Finalize the streaming assistant message in place (or append if missing). */
    const finalizeStreaming = (patch) => {
        setMessages((prev) => {
            const next = [...prev];
            const idx = next.findIndex((m) => m.streaming || m.pending);
            if (idx >= 0)
                next[idx] = { ...next[idx], pending: false, streaming: false, ...patch };
            return next;
        });
    };
    const stopTurn = () => {
        track('gyro_stop', { app: appKey });
        const current = conversationsRef.current.get(sessionIdRef.current ?? '');
        if (desktopWorkspace && current) {
            // What the stopped turn had spent so far still counts toward the chat.
            countInterruptedTurn(current.id, current.value.messages);
            conversationsRef.current.stop(current.id);
            current.value = { ...current.value, activity: current.value.activity ? interruptTask(current.value.activity, 'stopped') : null,
                messages: current.value.messages.map(message => message.streaming || message.pending ? { ...message, content: message.content || 'Stopped.', pending: false, streaming: false, documentReady: false, liveUsage: undefined } : message) };
            setMessages(current.value.messages);
            setTaskActivity(current.value.activity);
            setBusy(false);
            cancelRef.current = null;
            refreshConversations();
            return;
        }
        taskRunRef.current++;
        setTaskActivity(current => current ? interruptTask(current, 'stopped') : null);
        cancelRef.current?.();
        cancelRef.current = null;
        countInterruptedTurn(sessionIdRef.current, messages);
        finalizeStreaming({ content: messagesRefContent() || 'Stopped.', streaming: false, liveUsage: undefined });
        setBusy(false);
    };
    // Read the current streaming buffer synchronously for stop/finalize.
    const streamBufRef = useRef('');
    const messagesRefContent = () => streamBufRef.current;
    /**
     * Compact web integrations retain their opted-in completion alerts. Desktop completions remain
     * in their conversations; its notifications center is reserved for organization and monitoring events.
     */
    const answerFinished = (text, _failed = false) => {
        if (desktopWorkspace)
            return;
        const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        if (!hidden && openRef.current)
            return; // visible + open — the user saw it land
        setUnseen((n) => n + 1);
        if (!notificationsEnabledRef.current)
            return;
        const clean = text.replace(/\s+/g, ' ').trim();
        const snippet = (looksSensitive(clean) || !clean ? 'your answer is ready.' : clean).slice(0, 60);
        try {
            window.postMessage({ type: 'gyro:notify', text: `Gyro finished: ${snippet}` }, '*');
        }
        catch {
            /* ignore */
        }
        // Only notifies if permission is ALREADY granted — never requests from this completion path (L-27).
        if (!props.nativeNotifications)
            notifyAnswerDone(snippet, staticAvatar, () => setOpen(true));
    };
    /**
     * Opt in to desktop notifications. MUST only be invoked from a user gesture — the bell control, the
     * palette command, or the first Send when the host chose `notifications: 'prompt-on-send'` — because
     * browsers ignore/penalize permission prompts that are not gesture-initiated (L-27).
     */
    const enableNotifications = () => {
        const api = notificationApi();
        if (!api || api.permission !== 'default') {
            setNotifPerm(api?.permission ?? 'unsupported');
            return;
        }
        track('gyro_notifications_opt_in', { app: appKey });
        void requestNotificationsFromGesture(api).then((perm) => setNotifPerm(perm));
    };
    /**
     * Drop everything belonging to the previous identity from MEMORY (M-09): the open conversation and
     * its per-action state, the loaded chat list, the memory editor, the steer queue, an in-flight turn
     * and any audio. Storage is not touched here — the caches are namespaced by userKey, so the incoming
     * identity simply reads its own (possibly empty) slate. `configured` is reset so the status check
     * re-runs for the new identity (the model list + persona are role-scoped).
     */
    const resetForIdentity = () => {
        if (desktopWorkspace)
            parts.clearNativePets();
        setShowPets(false);
        setPetRoute({ view: 'crew' });
        setPetSessionEpoch(value => value + 1);
        localFileContinuationsRef.current.clear();
        actionChatsRef.current.clear();
        for (const chat of conversationsRef.current.values())
            chat.value.documents.clear();
        conversationsRef.current.clear();
        conversationUsageRef.current.clear();
        setSessionUsage(EMPTY_SESSION_USAGE);
        setTaskContextEpoch(epoch => epoch + 1);
        documentStoreRef.current.clear();
        clearComposer();
        inboxPollerRef.current?.dispose();
        inboxPollerRef.current = null;
        setShowNotifications(false);
        inboxActionsRef.current.clear();
        setInboxSync({ loading: true, error: false, lastSyncedAt: null });
        taskRunRef.current++;
        setTaskActivity(null);
        setWorkingFolder(null);
        setTaskSources([]);
        setTaskArtifacts([]);
        setTaskRuntime(undefined);
        setAgentStatus(undefined);
        setKnowledgeCapabilities(knowledgeStatus(null));
        setFileAttachmentsSupported(false);
        setTaskAttachments([]);
        setTaskConnectors([]);
        cancelRef.current?.();
        cancelRef.current = null;
        stopAudio();
        setBusy(false);
        setQueued([]);
        sessionIdRef.current = null;
        setTranscriptLoad(transcriptLoadRef.current.reset());
        setHistorySync(null);
        loadingRef.current = true;
        setMessages([greeting]);
        setResolved({});
        setPreviews({});
        setApprovalSteps({});
        resetBugReport();
        dispatchShare({ type: 'cancel' });
        setSkillBox(null);
        setSkillsPanelOpen(false);
        setSessions([]);
        setFeedbackGiven({});
        setMemoryText('');
        setMemorySaved(false);
        setMemoryError('');
        setUnseen(0);
        setConfigured(null);
        setModels([]);
        setDayGreeting(null);
        dayGreetingAtRef.current = null;
        dayRefreshRef.current = { inFlight: false, retryAtMs: 0 };
        historySaveRef.current.reset();
        historyWritesRef.current.reset();
        conversationTitlesRef.current.clear();
    };
    // An identity switch WITHOUT a remount (the host swaps `user` / `userKey` in place) must not carry
    // the previous person's conversation across (M-09). Skips the mount run — a fresh mount already
    // starts clean. Declared BEFORE the open/userKey session-load effect so that, when the panel is
    // open, the reload of the new identity's chat list wins over the clear.
    const previousUserKeyRef = useRef(userKey);
    useEffect(() => {
        if (previousUserKeyRef.current === userKey)
            return;
        previousUserKeyRef.current = userKey;
        resetForIdentity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userKey]);
    /**
     * Host sign-out hook (`gyro:sign-out` CustomEvent, or the same-window postMessage form): wipe THIS
     * identity's persisted caches from the browser and reset the in-memory state, so the next person on
     * this browser — even one the host mounts as 'anon' — cannot read the previous user's chats (M-09).
     * Reached through signOutRef because the window listeners are registered once.
     */
    const signOut = () => {
        void usageCapture().signOut(); // the usage record: flush this account's queue, then drop it
        attention.clear();
        notificationCenter.clear();
        try {
            clearPersistedState(localStorage, appKey, userKeyRef.current);
            localStorage.removeItem(desktopPreferencesKey(appKey, userKeyRef.current));
        }
        catch {
            /* storage unavailable — nothing was persisted there in the first place */
        }
        resetForIdentity();
        setInboxEpoch(epoch => epoch + 1);
        setPreferenceState({ key: preferenceKey, value: { ...DEFAULT_DESKTOP_PREFERENCES } });
        setSettingsSection(null);
        setOpen(false);
    };
    signOutRef.current = signOut;
    // Dictation is cancelled only by the composer's own submit. Queued sends, suggestion chips, palette and host-hook
    // asks leave a dictation running (cancelling it would throw away the words heard so far).
    const send = async (q, atts = [], pendingDocuments, localFile) => {
        const starscribeAsk = starscribeAskRef.current;
        starscribeAskRef.current = undefined;
        const pppAuto = pppAutoRef.current;
        pppAutoRef.current = false;
        if (!pppAuto)
            pppContinuedRef.current = 0;
        // A message sent from anywhere (the composer, a chip, the palette, the host, the queue) takes a web page's "not available
        // here" with it.
        clearVoiceInputOff();
        if (knowledgeMode === 'world' && !knowledgeCapabilities.worldEnabled) {
            setAttachError('World IQ needs an updated service. Choose Work IQ to continue, or retry after the service update.');
            return;
        }
        if (attachmentPendingRef.current > 0) {
            setAttachError('Wait for your files to finish loading before sending.');
            return;
        }
        const question = q.trim();
        if ((!question && atts.length === 0) || busy)
            return;
        const loadRefusal = transcriptLoadRef.current.refusalMessage(sessionIdRef.current);
        if (loadRefusal) {
            setAttachError(loadRefusal);
            return;
        }
        // A continuation requires a live native result bound to this user's currently viewed conversation. It is checked
        // after the load gate, because dropping a stale card below changes the transcript on screen.
        const continuation = localFile && localFileContinuationsRef.current.get(localFile.actionId, userKeyRef.current, sessionIdRef.current ?? '');
        if (localFile && !continuation) {
            // Evicted (32 entries), cleared on an identity reset, or settled as failed since the card rendered: say so and
            // drop the card instead of doing nothing (the repo rule is never to do nothing silently).
            setAttachError('That file result is no longer available in this session. Ask for the file again; Gyro does not repeat an action on its own.');
            setMessages(previous => previous.map(message => message.localFileResult?.actionId === localFile.actionId ? { ...message, localFileResult: undefined } : message));
            return;
        }
        if (localFile && (knowledgeMode !== 'work' || inputRef.current?.value.trim() || attachmentsRef.current.length || speechInputState !== 'idle'))
            return;
        // Every path to a continuation goes through continueFileTask, which has just asked the service whether it reads the
        // evidence field; one that does not would answer without the file result, so nothing is sent to it.
        if (localFile && !localFileEvidenceClearedRef.current) {
            setAttachError(LOCAL_FILE_EVIDENCE_UNSUPPORTED);
            return;
        }
        // The evidence is a dedicated, server-fenced field; the question stays the short visible message.
        const requestQuestion = question;
        const localFileEvidenceField = continuation ? { localFileEvidence: localFileEvidence(continuation) } : {};
        // The StarScribe page open beside this chat, or the page's own ask (its selection or shortcut): `starscribe` on the ask.
        const starscribeField = starscribe.askField(starscribeAsk);
        if (desktopWorkspace && conversationsRef.current.values().filter(chat => chat.state === 'running').length >= 2) {
            setAttachError('Two chats are already working. Wait for one to finish, or open a running chat and stop it before sending.');
            return;
        }
        stopAudio();
        track('gyro_ask', { app: appKey, kind: mode, count: atts.length });
        if (mode === 'voice')
            primeAudio();
        // A running dictation owns the composer draft; it is not this send's text, so it is left in place.
        if (!recognitionRef.current?.isActive)
            setInput('');
        const attachments = atts.length ? atts : undefined;
        // File-only turns have empty text; give history a placeholder so the model never sees an empty
        // user message (the API rejects those) — attachments themselves are not replayed in history.
        const history = messages
            .filter((m) => !m.welcome && !m.note && !m.pending && !m.error)
            .map((m) => ({ role: m.role, content: m.content || (m.attachments && m.attachments.length ? '[file attachment]' : m.content) }))
            .filter((m) => m.content.length > 0);
        const startedAt = Date.now();
        const latest = messages.at(-1);
        const continuingDocument = latest?.role === 'assistant' && !latest.error && !latest.streaming
            && questionsForReply(latest.content, latest.followups, latest.questions)?.length ? latest : undefined;
        const inheritedDocuments = pendingDocuments ?? continuingDocument?.requestedDocuments ?? [];
        const requestedDocuments = documentOutputs ? resolveDocumentRequest(question, inheritedDocuments) : [];
        const documentPrompt = inheritedDocuments.length && !detectDocumentRequest(question).length
            ? continuingDocument?.documentPrompt ?? question : question;
        // Give completion notifications a stable destination before the first response is saved.
        if (!sessionIdRef.current)
            sessionIdRef.current = `s-${startedAt}-${Math.floor(Math.random() * 100000)}`;
        const conversationId = sessionIdRef.current;
        // An MCP call's result, once the chat has continued from it, is spent: its card goes (a file task's stays, so the person
        // can take another step from the same file result).
        const spent = (message) => (continuation?.kind === 'mcp' && message.localFileResult?.actionId === continuation.actionId ? { ...message, localFileResult: undefined } : message);
        const initialMessages = [...messages.map(spent), { role: 'user', content: question, attachments, ...(continuation ? { localFileTask: continuation.originalRequest } : {}), ...(pppAuto ? { auto: true } : {}) }, { role: 'assistant', content: '', pending: true, streaming: true, toolActivity: [], question, startedAt, requestedDocuments, documentPrompt }];
        setMessages(initialMessages);
        requestSendScroll();
        setBusy(true);
        const taskRun = ++taskRunRef.current;
        paneSettledRef.current = []; // a pane edit that settled for an earlier turn is not this turn's
        const run = desktopWorkspace ? conversationsRef.current.begin(conversationId, {
            owner: userKey, messages: initialMessages, documents: documentStore, activity: startTask(question || 'Review attached files', startedAt),
            sources: [], artifacts: [], actions: [], attachments: atts.map(attachment => attachment.name || (attachment.kind === 'image' ? 'Image attachment' : 'File attachment')),
            folder: workingFolder, queued: [], runId: taskRun, resolved,
        }) : undefined;
        const isCurrentRun = () => run ? conversationsRef.current.active(run) && userKeyRef.current === userKey : taskRunRef.current === taskRun;
        const isViewed = () => sessionIdRef.current === conversationId && userKeyRef.current === userKey;
        // The chat's name when its files appear: the folder the desktop's copies of them go in (desktopAutoSave.tsx).
        const chatNameNow = () => conversationTitlesRef.current.describe(conversationId, deriveTitle(run ? run.value.messages : initialMessages), startedAt).title;
        const restoreUnreadFiles = (failure) => {
            const message = failure instanceof Error ? failure.message : typeof failure === 'string' ? failure : '';
            if (!atts.length || !fileReadFailureMessage(message)
                || !isViewed() || inputRef.current?.value || attachmentsRef.current.length || attachmentPendingRef.current)
                return;
            attachmentsRef.current = atts;
            setAttachments(atts);
            setInput(question);
        };
        if (run) {
            const meta = conversationTitlesRef.current.describe(conversationId, deriveTitle(initialMessages), startedAt);
            conversationTitlesRef.current.remember({ id: conversationId, ...meta });
            setSessions(previous => [{ id: conversationId, ...meta, messages: history, updatedAt: startedAt, usage: conversationUsageRef.current.saved(conversationId) }, ...previous.filter(session => session.id !== conversationId)]);
            refreshConversations();
        }
        if (desktopWorkspace) {
            setTaskActivity(startTask(question || 'Review attached files', startedAt));
            setTaskActions([]);
            setTaskSources([]);
            setTaskArtifacts([]);
            setTaskRuntime(undefined);
            setTaskAttachments(atts.map(attachment => attachment.name || (attachment.kind === 'image' ? 'Image attachment' : 'File attachment')));
        }
        const updateTask = (update) => {
            if (!isCurrentRun() || !run)
                return;
            run.value.activity = run.value.activity ? update(run.value.activity) : null;
            if (isViewed())
                setTaskActivity(run.value.activity);
        };
        const replyPatch = (patch) => {
            if (!isCurrentRun())
                return;
            if (!run) {
                setMessages(previous => previous.map(message => message.streaming || message.pending ? { ...message, ...patch } : message));
                return;
            }
            run.value.messages = run.value.messages.map(message => message.startedAt === startedAt && message.role === 'assistant' ? { ...message, ...patch } : message);
            if (isViewed())
                setMessages(run.value.messages);
        };
        const finalizeReply = (patch) => replyPatch({ pending: false, streaming: false, ...patch });
        // Pulse (section 10.1): the turn's ask id goes on the reply (a thumb posts against it) and is remembered for the turn's
        // actions (their cards post against it too); a Pay data chat's is not. The turn's one higher-profile auto write, else
        // the one pane edit that settled while it ran, earns one card under the reply, when the device's rules say so.
        const pulseTurnFields = (res) => {
            const paneSettled = paneSettledRef.current.splice(0);
            if (!res.askId || res.classified === true)
                return {};
            for (const action of res.pendingActions ?? [])
                pulseAsksRef.current.set(action.id, res.askId);
            const auto = res.autoActions?.find((action) => action.highProfile === true && action.result === 'success');
            const pane = paneSettled.find((event) => event.ok);
            const subject = auto ? { tool: auto.tool, summary: auto.summary } : pane ? { tool: pane.tool, summary: paneToolSummary(pane.tool) } : null;
            const pulse = subject ? offerPulseCard({ askId: res.askId, actionKey: res.askId, highProfile: true, classified: false, chipKey: pulseChipKey(subject.tool), ...subject }, { busy: false }) : null;
            return { askId: res.askId, ...(pulse ? { pulse } : {}) };
        };
        const resultDetails = (res) => {
            if (!run)
                return;
            Object.assign(run.value, { sources: res.sources ?? [], actions: res.pendingActions ?? [], artifacts: res.artifacts ?? [], runtime: res.runtime });
            if (isViewed()) {
                setTaskSources(run.value.sources);
                setTaskActions(run.value.actions);
                setTaskArtifacts(run.value.artifacts);
                setTaskRuntime(run.value.runtime);
            }
        };
        const settleTurn = () => {
            if (!isCurrentRun())
                return;
            if (isViewed()) {
                setBusy(false);
                cancelRef.current = null;
            }
            if (!run)
                return;
            conversationsRef.current.finish(run);
            const slim = run.value.messages.filter(message => !message.welcome && !message.note && !message.pending && !message.streaming && !message.error).map(message => ({ role: message.role, content: message.content }));
            const meta = conversationTitlesRef.current.describe(conversationId, deriveTitle(run.value.messages), startedAt);
            // The chat's token total rides the local cache entry only; the server copy (saveChatHistory) carries no usage.
            const saved = { id: conversationId, ...meta, messages: slim, updatedAt: Date.now(), usage: conversationUsageRef.current.saved(conversationId) };
            setSessions(previous => { const next = [saved, ...previous.filter(session => session.id !== conversationId)]; persistSessions(next.filter(session => !session.remote)); return next; });
            void saveChatHistory({ id: conversationId, title: saved.title, turns: slim, createdAt: saved.createdAt, updatedAt: saved.updatedAt });
            refreshConversations();
        };
        streamBufRef.current = '';
        let streamText = '';
        let toolActivity = [];
        let pppMarks;
        let gotText = false;
        // The last live figures: what a turn spent when it ends without a recorded usage (stopped, errored, older server).
        let lastLiveUsage;
        let hostContext;
        try {
            hostContext = knowledgeMode === 'world' ? undefined : props.pageAware === false ? props.pageContext : props.getPageContext ? await props.getPageContext() : (props.pageContext ?? capturePageContext());
        }
        catch {
            if (isCurrentRun()) {
                updateTask(current => interruptTask(current, 'error'));
                finalizeReply({ content: 'Gyro could not load the context for this message. Please try again.', error: true, documentReady: false });
                settleTurn();
            }
            return;
        }
        if (!isCurrentRun())
            return;
        // Selected file snapshots are ephemeral and sent only with a user-initiated turn.
        // The service fences this untrusted context and caps it at 4,000 characters.
        const taskPageContext = knowledgeMode === 'work' && desktopWorkspace && workingFolder
            ? [workingFolder.context, hostContext?.slice(0, 900)].filter(Boolean).join('\n\n').slice(0, 4000)
            : hostContext;
        // This is the user's desktop interaction preference, also understood by services using answer chips.
        const basePageContext = knowledgeMode === 'world' ? undefined : desktopWorkspace ? [
            documentOutputContext(requestedDocuments) || DESKTOP_DOCUMENT_CAPABILITIES,
            'User preference: When you need clarification to continue, ask a concise question on its own line and offer 2–4 short, mutually exclusive answer choices through your follow-up suggestions. The user can also type another answer. Ask only when the answer matters; do not ask for a choice when you can already complete the request. An answer to a question never substitutes for required action approval.',
            taskPageContext,
        ].filter(Boolean).join('\n\n') : documentOutputs ? paneDocumentContext(requestedDocuments, taskPageContext) : taskPageContext;
        // "Report this bug": from here until the report is filed, each turn also carries where the bug happened —
        // and only these turns do (owner decision D8).
        // World IQ turns carry no host context and offer no tools, so they never start a report or carry its block.
        if (labAvailable && knowledgeMode !== 'world' && isBugReportRequest(question))
            setReportMode(true);
        const reportBlock = labAvailable && knowledgeMode !== 'world' && reportModeRef.current ? formatReportContext(reportContextFor(), captureMode === 'host' ? 'desktop' : 'in-app') : '';
        const pageContext = [basePageContext, reportBlock].filter(Boolean).join('\n\n') || undefined;
        // The chat's tier when it is Fast or High (`modelTier`, resolved by the service: High = Fable 5.1 at its deepest
        // thinking). Balanced sends nothing: the service's default model and depth, never a model id or depth from the client.
        const requestModel = chatRequestOptions(modelTier, modelChoice, effort);
        // The StarScribe pages this turn made: their cards under this reply, and (D12) the last one marked open beside the chat.
        const starscribePages = (made) => starscribe.answerPages(`${conversationId}:${startedAt}`, made?.created ?? [], isViewed());
        const updateStreaming = replyPatch;
        const fallbackAsk = async () => {
            if (!isCurrentRun())
                return;
            try {
                const res = await client.ask({ question: requestQuestion, history, conversationId, pageContext, style: mode, attachments, knowledgeMode, ...requestModel, ...localFileEvidenceField, ...starscribeField });
                if (!isCurrentRun())
                    return;
                if (res.mode === 'error') {
                    restoreUnreadFiles(res.text);
                    res.text = errorReplyText(res.text);
                }
                noteReportTurn(Boolean(res.pendingActions?.some((a) => a.app === 'the-lab')));
                updateTask(current => finishTask(current, res));
                resultDetails(res);
                starscribePages(res.starscribe);
                autoSaveRef.current?.saveArtifacts(chatNameNow(), res.artifacts);
                countFinishedTurn(conversationId, startedAt, res, lastLiveUsage);
                const pulseFields = pulseTurnFields(res);
                finalizeReply({
                    liveUsage: undefined,
                    content: res.text,
                    documentReady: res.mode === 'ai',
                    error: res.mode === 'error' || res.mode === 'unconfigured',
                    actions: res.pendingActions?.length ? res.pendingActions : undefined,
                    autoActions: res.autoActions?.length ? res.autoActions : undefined,
                    sources: res.sources,
                    artifacts: res.artifacts,
                    runtime: res.runtime,
                    followups: res.followups,
                    questions: res.questions,
                    ...attention.metadata(res),
                    grounded: res.grounded,
                    advisory: res.advisory,
                    traceId: res.traceId,
                    model: res.mode === 'ai' ? res.model : undefined,
                    classified: res.classified === true ? true : undefined,
                    ...pulseFields,
                });
                setTierNote(res.tierNote ?? '');
                for (const action of res.pendingActions ?? []) {
                    inboxActionsRef.current.set(action.id, action);
                    actionChatsRef.current.shown(action.id, userKey, conversationId);
                }
                attention.refreshAfterAnswer(res);
                if (res.classified === true)
                    rememberPayChatHere(conversationId);
                // A reply from a chat that holds pay data names nothing in a notification: no text, so only "your answer is ready.".
                answerFinished(res.classified === true ? '' : res.text, res.mode === 'error' || res.mode === 'unconfigured');
                if (isViewed() && voiceOnRef.current && speechSuppressedRunRef.current !== taskRun && !looksSensitive(res.text) && res.classified !== true)
                    void speakReply(res.text);
            }
            catch (e) {
                if (!isCurrentRun())
                    return;
                restoreUnreadFiles(e);
                updateTask(current => interruptTask(current, 'error'));
                const failure = failedAskReply(e);
                countTurnUsage(conversationId, startedAt, lastLiveUsage && turnUsageFromLive(lastLiveUsage), false);
                finalizeReply({ ...failure, liveUsage: undefined });
                answerFinished(failure.content, true);
                void client.reportError({ message: e instanceof Error ? e.message : 'ask failed', stack: e instanceof Error ? e.stack : undefined, where: 'ask' });
            }
            finally {
                settleTurn();
            }
        };
        const cancel = client.askStream({ question: requestQuestion, history, conversationId, pageContext, style: mode, attachments, knowledgeMode, ...requestModel, ...localFileEvidenceField, ...starscribeField }, {
            clientTools,
            onText: (delta) => {
                if (!isCurrentRun())
                    return;
                updateTask(current => current.phase === 'responding' ? current : { ...current, phase: 'responding' });
                gotText = true;
                streamText += delta;
                if (isViewed())
                    streamBufRef.current = streamText;
                updateStreaming({ content: streamText, pending: false, latestTextAt: Date.now() });
            },
            onToolStart: (tool, summary, _riskTier, meta) => {
                if (!isCurrentRun())
                    return;
                updateTask(current => startTaskTool(current, tool, summary, Date.now(), meta));
                // A readable label and the connector name; never the raw tool name or its arguments.
                const connector = toolConnectorLabel(tool, meta?.connector);
                const label = toolActivityLabel(tool, meta?.connector, summary);
                toolActivity = [...toolActivity, `${label}${connector && !label.includes(connector) ? ` (${connector})` : ''}…`].slice(-4);
                updateStreaming({ toolActivity, pending: false });
            },
            onToolResult: (tool, ok, meta, setupMarker) => {
                if (!isCurrentRun())
                    return;
                if (petsEnabled && setupMarker) {
                    const token = petSetup.handoffs.offer(conversationId, q, atts.length + (localFile || starscribeAsk ? 1 : 0), setupMarker, String(startedAt));
                    if (token)
                        updateStreaming({ petSetupReview: token });
                }
                updateTask(current => finishTaskTool(current, tool, ok, Date.now(), meta));
                // "Change your outfit": the reply gets the outfit picker once it finishes (desktop only).
                const lookPicker = lookPickerReplyPatch(appKey, tool, ok);
                if (lookPicker)
                    updateStreaming(lookPicker);
                // "Create my PPP": what ran decides the chips under the reply; an export moves the due date, so /v1/ppp/me is read again.
                const marks = pppMarksAfterTool(pppMarks, tool, ok, meta?.progress);
                if (marks) {
                    if (marks.exported && !pppMarks?.exported)
                        setPppRefresh(count => count + 1);
                    pppMarks = marks;
                    updateStreaming({ ppp: marks });
                }
            },
            onUsage: (usage) => {
                if (!isCurrentRun())
                    return;
                // Live state on the streaming reply only (never persisted); the session line adds it on top while it runs.
                lastLiveUsage = usage;
                updateStreaming({ liveUsage: usage });
            },
            onStatus: (elapsedMs) => {
                if (!isCurrentRun())
                    return;
                // Only a bridging host sends this; it lets the bubble say "still working (Ns)" instead of a mute "Thinking…".
                updateStreaming({ workingForMs: elapsedMs });
            },
            onDone: (res) => {
                if (!isCurrentRun())
                    return;
                if (res.mode === 'error') {
                    restoreUnreadFiles(res.text);
                    res.text = errorReplyText(res.text);
                }
                noteReportTurn(Boolean(res.pendingActions?.some((a) => a.app === 'the-lab')));
                updateTask(current => finishTask(current, res));
                resultDetails(res);
                starscribePages(res.starscribe);
                // The answer's files are known: the desktop keeps a copy of each in Documents\Gyro\<chat name> (desktopAutoSave.tsx).
                autoSaveRef.current?.saveArtifacts(chatNameNow(), res.artifacts);
                // The recorded usage counts (it replaces live figures an earlier error counted for this turn). A failed turn
                // keeps at least what its counter showed; an older server without a recorded usage leaves the live figures.
                countFinishedTurn(conversationId, startedAt, res, lastLiveUsage);
                const pulseFields = pulseTurnFields(res);
                finalizeReply({
                    petSetupCompleted: petSetupReplyCompleted(res.mode),
                    workingForMs: undefined,
                    liveUsage: undefined,
                    documentReady: res.mode === 'ai',
                    content: res.text || streamText,
                    error: res.mode === 'error' || res.mode === 'unconfigured',
                    actions: res.pendingActions?.length ? res.pendingActions : undefined,
                    autoActions: res.autoActions?.length ? res.autoActions : undefined,
                    sources: res.sources,
                    artifacts: res.artifacts,
                    runtime: res.runtime,
                    followups: res.followups,
                    questions: res.questions,
                    ...attention.metadata(res),
                    grounded: res.grounded,
                    advisory: res.advisory,
                    traceId: res.traceId,
                    model: res.mode === 'ai' ? res.model : undefined,
                    toolActivity: undefined,
                    classified: res.classified === true ? true : undefined,
                    ...pulseFields,
                });
                attention.refreshAfterAnswer(res);
                setTierNote(res.tierNote ?? '');
                track('gyro_answer', {
                    app: appKey,
                    kind: res.advisory ? 'advisory' : res.grounded ? 'grounded' : 'plain',
                    count: (res.pendingActions?.length ?? 0) + (res.autoActions?.length ?? 0),
                });
                // The cards belong to this turn's chat, whichever chat is open when their outcomes arrive.
                for (const action of res.pendingActions ?? []) {
                    inboxActionsRef.current.set(action.id, action);
                    actionChatsRef.current.shown(action.id, userKey, conversationId);
                }
                if (res.classified === true)
                    rememberPayChatHere(conversationId);
                // A reply from a chat that holds pay data names nothing in a notification: no text, so only "your answer is ready.".
                answerFinished(res.classified === true ? '' : res.text || streamText, res.mode === 'error' || res.mode === 'unconfigured');
                if (isViewed() && voiceOnRef.current && speechSuppressedRunRef.current !== taskRun && res.text && !looksSensitive(res.text) && res.classified !== true)
                    void speakReply(res.text);
                settleTurn();
                // "Create my PPP" in one step (owner, 2026-10-25): while steps remain and the turn's calls are spent, the widget
                // sends "Continue my PPP" itself, exactly as the person would, until the run is done or a reply asks something.
                if (pppShouldContinue(pppMarks, { error: res.mode === 'error' || res.mode === 'unconfigured', questions: res.questions?.length ?? 0 }, pppContinuedRef.current)) {
                    pppContinuedRef.current += 1;
                    pppAutoRef.current = true;
                    setTimeout(() => { if (isViewed())
                        void sendRef.current(pppChipSentence('continue'));
                    else
                        pppAutoRef.current = false; }, 50);
                }
            },
            onError: (message, options) => {
                if (!isCurrentRun())
                    return;
                restoreUnreadFiles(message);
                if (isViewed())
                    cancelRef.current = null;
                // Do not expose raw provider bodies or device details in the renderer's diagnostics.
                if (message)
                    console.warn(`[gyro] ask stream error: ${askFailureMessage(message)}`);
                // A dropped stream can hide a tool that already ran. Only an unsupported endpoint permits
                // buffered fallback; an uncertain outcome must never silently start a second task.
                if (options?.allowBufferedFallback && !gotText && toolActivity.length === 0)
                    void fallbackAsk();
                else {
                    updateTask(current => interruptTask(current, 'error'));
                    const failure = failedAskReply(message, streamText);
                    // A turn that failed after spending tokens still counts toward the chat (provisionally: a done result that
                    // follows the error settles the figure, never below these).
                    countTurnUsage(conversationId, startedAt, lastLiveUsage && turnUsageFromLive(lastLiveUsage), false);
                    finalizeReply({ ...failure, liveUsage: undefined });
                    answerFinished(failure.content, true);
                    settleTurn();
                }
            },
        });
        if (isCurrentRun()) {
            if (run)
                run.cancel = cancel;
            if (isViewed())
                cancelRef.current = cancel;
        }
    };
    sendRef.current = send;
    /** "Continue file task" (and the chat's own continuation after any other confirmed MCP call): the service is asked first
     * whether it reads the evidence field for this kind of result (one older than this widget, reached first or rolled back
     * to, would ignore it and answer without the result). Only a yes sends; otherwise the person is told why nothing was
     * sent, and the card stays for another try. */
    const continueFileTask = async (result) => {
        if (localFileServiceCheckRef.current)
            return 'busy'; // this click's check (or its send) is already under way
        localFileServiceCheckRef.current = true;
        try {
            const service = await localFileEvidenceService(() => client.status(), result.tool, result.kind);
            if (service !== 'supported') {
                setAttachError(evidenceRefusalText(result.kind, service));
                return service;
            }
            localFileEvidenceClearedRef.current = true;
            try {
                await sendRef.current(continuationMessage(result, mcpServerDisplayName(result.server, mcpServersRef.current)), [], undefined, result);
            }
            finally {
                localFileEvidenceClearedRef.current = false;
            }
            return service;
        }
        finally {
            localFileServiceCheckRef.current = false;
        }
    };
    // Any other MCP server's result (a confirmed mcp_call on Gyro AI for Revit, Revu or a configured server) continues the
    // open chat on its own, once per action, as soon as nothing holds it up: the call was proposed to answer the person's
    // question, and until 2026-10-18 the chat stopped at "Done - Run revit_status on revit-ai" with no answer (owner, "can
    // you see my revit model?"). The same conditions the card enforces hold it (a running answer, World IQ, dictation, a
    // draft, attachments); while they do, the card stays so the person can continue by hand, and a continuation that was
    // held is sent when they clear. A local file task keeps its explicit "Continue file task" step (PR #220 review item 5).
    // Nothing is run again: this is a new ask carrying the result, never a second action.
    // An action is marked before its check so a render during the check cannot send it twice; a service that could not be
    // reached unmarks it, so the next render (the inbox poll re-renders every few seconds) tries again. One the service
    // refused stays marked: the card says why, and the person decides.
    const autoContinuedRef = useRef(new Set());
    // While this chat waits for a confirmed call on the PC to come back with its result (the time between the confirm and
    // the continuation above), the composer offers "Stop waiting", and the wait ends on its own after the clocks in
    // localFileContinuation.ts (WAIT_AFTER_SETTLE_MS once the inbox lists the action done or failed without a result,
    // WAIT_OVERALL_MS from the confirm). Owner, 2026-10-18: a confirmed revit_status whose result never came back left the
    // chat at "Done - Run revit_status on revit-ai" with the composer locked. Ending a wait drops the binding (a late result
    // continues nothing and nothing runs again), says so in that chat, and leaves the card with the state the inbox gave it.
    // Read every render; nowMs ticks each second while the chat is on screen, so a wait that ended by its clock is re-read then.
    const waitingOn = desktopWorkspace && !busy ? localFileContinuationsRef.current.waiting(userKeyRef.current, sessionIdRef.current ?? '') : [];
    const stopWaiting = (actionId, why) => {
        const ended = localFileContinuationsRef.current.forget(actionId);
        if (!ended)
            return;
        autoContinuedRef.current.add(actionId);
        const call = `${shownMcpName(ended.tool)} on ${mcpServerLabel(ended.server, mcpServersRef.current)}`;
        updateConversationMessages({ id: ended.conversation, owner: ended.owner }, (previous) => [...previous, {
                role: 'assistant',
                content: why === 'timeout'
                    ? `Gyro stopped waiting for the result of ${call}: it did not arrive in time. Nothing was run again; ask again if you still need it.`
                    : `Stopped waiting for the result of ${call}. Nothing was run again; ask again if you still need it.`,
            }]);
    };
    const stopWaitingRef = useRef(stopWaiting);
    stopWaitingRef.current = stopWaiting;
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        for (const expired of localFileContinuationsRef.current.expiredWaits(nowMs))
            stopWaitingRef.current(expired.actionId, 'timeout');
    }, [desktopWorkspace, nowMs]);
    useEffect(() => {
        if (!desktopWorkspace || busy || knowledgeMode !== 'work' || speechInputState !== 'idle' || input.trim() || attachments.length > 0 || attachmentReads > 0)
            return;
        const next = messages.find((message) => message.localFileResult?.kind === 'mcp' && !autoContinuedRef.current.has(message.localFileResult.actionId));
        if (!next?.localFileResult)
            return;
        const { actionId } = next.localFileResult;
        autoContinuedRef.current.add(actionId);
        void continueFileTask(next.localFileResult).then((outcome) => { if (outcome === 'unreachable' || outcome === 'busy')
            autoContinuedRef.current.delete(actionId); });
    });
    /** Where the bug happened: the desktop agent's record on the desktop, else this page (path only, no query). */
    const reportContextFor = () => {
        if (captureMode === 'host')
            return hostReportContextRef.current;
        try {
            const version = document.querySelector('meta[name="app-version"]')?.getAttribute('content') ?? document.documentElement.dataset.appVersion;
            return parseReportContext({ route: location.pathname, pageTitle: document.title, appVersion: version ?? undefined, ...describeUserAgent(navigator.userAgent) });
        }
        catch {
            return null;
        }
    };
    /** The Report a bug control (chip, palette, composer button): enter report mode and let Gyro ask what happened. */
    const startBugReport = () => {
        setShowPets(false);
        setOpen(true);
        // The Lab's tools exist only in Work IQ (a World IQ turn gets no tools and no host context).
        if (knowledgeMode === 'world') {
            setAttachError('Bug reports go to The Lab through Work IQ. Choose Work IQ, then report the bug.');
            return;
        }
        setShowSessions(false);
        setShowCapabilities(false);
        setShowMemory(false);
        setReportMode(true);
        track('gyro_bug_report', { app: appKey, kind: 'start' });
        const prompt = 'I want to report a bug.';
        if (busy) {
            setQueued((q) => [...q, prompt]);
            return;
        }
        const tray = attachments;
        setAttachments([]);
        setAttachError('');
        void send(prompt, tray);
    };
    /**
     * Take a screenshot for the report. In a browser: one frame of the tab the person picks (the browser asks). On the
     * desktop: the agent captures the whole screen with the overlay hidden and answers with `gyro:attach`.
     * Either way the picture lands in the attachment tray and rides the next message, so Gyro sees it (D2).
     */
    const captureScreenshot = async () => {
        setAttachError('');
        setReportMode(true);
        if (attachments.length >= ATTACHMENT_MAX_COUNT) {
            setAttachError(`max ${ATTACHMENT_MAX_COUNT} images`);
            return;
        }
        if (captureMode === 'host') {
            setCapturing(true);
            if (captureTimerRef.current)
                clearTimeout(captureTimerRef.current);
            captureTimerRef.current = setTimeout(() => {
                setCapturing(false);
                setAttachError('Screenshot not taken: the desktop agent did not answer.');
            }, 15_000);
            window.postMessage({ type: 'gyro:capture-screen' }, '*');
            return;
        }
        setCapturing(true);
        try {
            const shot = await captureCurrentTab();
            setAttachments((prev) => (prev.length >= ATTACHMENT_MAX_COUNT ? prev : [...prev, shot]));
            track('gyro_bug_report', { app: appKey, kind: 'capture' });
        }
        catch (e) {
            const name = e instanceof DOMException ? e.name : '';
            setAttachError(name === 'NotAllowedError' ? 'Screenshot not taken (cancelled or blocked by the browser).' : `Screenshot not taken: ${e instanceof Error ? e.message : 'capture failed'}.`);
        }
        finally {
            setCapturing(false);
        }
    };
    /** Replace the text of an action's outcome message in place (the receipt changes as the upload progresses). */
    const patchOutcome = (actionId, patch) => {
        setMessages((previous) => previous.map((m) => (m.actionOutcomeId === actionId ? { ...m, ...patch } : m)));
    };
    /**
     * After a confirmed Lab write came back: upload the images snapshotted at confirm to the one-time link gyro-svc
     * handed over, and say exactly what happened. The report already exists at this point, so every failure reads
     * "filed WITHOUT the screenshot" with its reason — never silence (§1.6, §4.5).
     */
    const finishLabWrite = async (action, response) => {
        if (action.app !== 'the-lab' || !LAB_UPLOAD_TOOLS.has(action.tool))
            return;
        const root = actionRecord(response) ?? {};
        const outcome = classifyActionOutcome(response);
        const images = pendingUploadsRef.current.get(action.id);
        if (!outcome.completed) {
            if (outcome.failed || outcome.state === 'cancelled')
                pendingUploadsRef.current.delete(action.id);
            return;
        }
        if (action.tool === LAB_FILE_TOOL)
            setReportMode(false);
        if (!images || images.length === 0)
            return;
        pendingUploadsRef.current.delete(action.id);
        const report = action.tool === LAB_FILE_TOOL
            ? parseFiledReport(root.result) ?? filedReportsRef.current.get(action.id) ?? null
            : typeof action.input.reportId === 'string' ? parseFiledReport({ reportId: action.input.reportId }) : null;
        const describe = (state) => (report
            ? filedReceiptText(report, action.input.appId, state)
            : state.state === 'failed'
                ? `**Updated the report WITHOUT the screenshot:** ${state.reason} Say *attach it again* to retry.`
                : state.state === 'attached' ? `Report updated, ${state.count} screenshot${state.count === 1 ? '' : 's'} attached.` : 'Report updated.');
        const fail = (reason) => {
            patchOutcome(action.id, { content: describe({ state: 'failed', reason }), error: true });
            void client.reportError({ message: `lab screenshot not attached: ${reason}`, where: 'lab-upload' });
        };
        const intent = parseUploadIntent(root.ephemeralResult);
        if (!intent) {
            fail(typeof root.handoffRefused === 'string'
                ? `The Lab's upload link was not usable (${root.handoffRefused.slice(0, 120)}).`
                : typeof root.handoffUnavailable === 'string'
                    ? root.handoffUnavailable.slice(0, 200)
                    : 'no upload link came back from The Lab (it expired or was not issued).');
            return;
        }
        const batch = images.slice(0, intent.maxFiles);
        patchOutcome(action.id, { content: describe({ state: 'uploading', count: batch.length }) });
        let dataUris;
        try {
            dataUris = await Promise.all(batch.map((image) => normalizeForLab(image, Math.min(intent.maxBytes, SCREENSHOT_TARGET_BYTES))));
        }
        catch {
            fail('the screenshot could not be prepared for upload.');
            return;
        }
        const uploaded = await evidenceUploaderRef.current(intent, dataUris);
        if (uploaded.ok) {
            patchOutcome(action.id, { content: describe({ state: 'attached', count: batch.length }), error: false });
            track('gyro_bug_report', { app: appKey, kind: 'upload', result: 'ok', count: batch.length });
        }
        else {
            fail(uploadFailureReason(uploaded.status, uploaded.error));
            track('gyro_bug_report', { app: appKey, kind: 'upload', result: 'failed' });
        }
    };
    const finishLabWriteRef = useRef(finishLabWrite);
    finishLabWriteRef.current = finishLabWrite;
    /**
     * Target of the `gyro:situation` host hook ("open Gyro on this issue"). Runs the LATEST closure via
     * openSituationRef (L-28), so it sees the current `busy`: while a turn is streaming the prompt is
     * QUEUED (steer semantics, same as the palette) instead of a fresh session being started under the
     * running stream — which is what the stale first-render `send` used to do, bypassing the busy guard.
     */
    const openSituation = (prompt) => {
        setShowPets(false);
        setOpen(true);
        setShowSessions(false);
        setShowCapabilities(false);
        setShowMemory(false);
        if (busy) {
            setQueued((q) => [...q, prompt]);
            return;
        }
        newSession();
        // Deferred so the reset conversation has rendered; the ref guarantees the send that runs is the
        // one belonging to that render (its history is the fresh session, its busy guard is live).
        setTimeout(() => void sendRef.current(prompt), 50);
    };
    openSituationRef.current = openSituation;
    // Drain the queue: when a turn finishes and messages are queued, send the next one (in order).
    useEffect(() => {
        if (busy || attachmentReads > 0 || queued.length === 0)
            return;
        const [next, ...rest] = queued;
        setQueued(rest);
        void send(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busy, queued, attachmentReads]);
    /** Apply `update` to a chat's transcript: the open chat, or one this widget holds. False when that chat is not loaded here. */
    const updateConversationMessages = useCallback((destination, update) => {
        if (destination.owner !== userKeyRef.current)
            return false;
        if (destination.id === sessionIdRef.current) {
            setMessages(update);
            return true;
        }
        const chat = conversationsRef.current.get(destination.id ?? '');
        if (chat?.value.owner !== destination.owner)
            return false;
        chat.value.messages = update(chat.value.messages);
        return true;
    }, [setMessages]);
    const resolveInConversation = useCallback((destination, actionId, status) => {
        if (destination.owner !== userKeyRef.current)
            return;
        const chat = conversationsRef.current.get(destination.id ?? '');
        if (chat?.value.owner === destination.owner)
            chat.value.resolved = { ...chat.value.resolved, [actionId]: status };
        if (destination.id === sessionIdRef.current)
            setResolved(previous => (previous[actionId] === status ? previous : { ...previous, [actionId]: status }));
    }, []);
    /** The chat an action belongs to (actionConversations.ts): a local file task's own chat, else the one bound to it. */
    const boundChat = useCallback((actionId, owner) => localFileContinuationsRef.current.conversationFor(actionId, owner) ?? actionChatsRef.current.conversationFor(actionId, owner), []);
    /** The chats this widget holds, other than the open one, whose own transcripts show the action's card. */
    const heldChatsShowing = useCallback((actionId, owner) => conversationsRef.current.values()
        .filter(chat => chat.id !== sessionIdRef.current && chat.value.owner === owner && holdsAction(chat.value.messages, actionId)), []);
    /** Whether chat `id` is loaded here and may be written to: the open chat once its transcript is in hand, or a held chat. */
    const chatLoaded = useCallback((id, owner) => (id === sessionIdRef.current
        ? transcriptLoadRef.current.canWrite(id)
        : conversationsRef.current.get(id)?.value.owner === owner), []);
    /** Whether an action's card belongs to a chat other than the open one: the inbox then does not surface it in the open chat. */
    const belongsElsewhere = (actionId) => {
        const owner = userKeyRef.current;
        const bound = boundChat(actionId, owner);
        return bound !== null ? bound !== sessionIdRef.current : heldChatsShowing(actionId, owner).length > 0;
    };
    /**
     * Whether an action's outcome can be shown here now: the chat it belongs to is loaded, or, with no chat bound to it, a
     * loaded chat's own transcript shows its card. The inbox's result for it is claimed only then (applyInbox).
     */
    const outcomeShowable = (actionId) => {
        const owner = userKeyRef.current;
        const bound = boundChat(actionId, owner);
        if (bound !== null)
            return chatLoaded(bound, owner);
        const openChat = sessionIdRef.current;
        const held = conversationsRef.current.get(openChat ?? '');
        const transcript = held?.value.owner === owner ? held.value.messages : renderedMessagesRef.current;
        return (transcriptLoadRef.current.canWrite(openChat) && holdsAction(transcript, actionId)) || heldChatsShowing(actionId, owner).length > 0;
    };
    /**
     * Save a chat this widget holds whose transcript changed while another chat is open (an action's outcome was recorded in
     * it), as the autosave saves the open chat. A chat still running a turn is saved when that turn settles (send).
     */
    const saveHeldChat = (id) => {
        const chat = conversationsRef.current.get(id);
        if (!chat || chat.value.owner !== userKey || chat.state === 'running' || id === sessionIdRef.current || !transcriptLoadRef.current.canWrite(id))
            return;
        const real = chat.value.messages.filter(message => !message.pending && !message.streaming);
        const slim = savedTurns(real);
        if (slim.length === 0)
            return;
        const now = Date.now();
        const { title, createdAt } = conversationTitlesRef.current.describe(id, deriveTitle(real), now);
        if (!historySaveRef.current.needsSave(id, transcriptFingerprint(id, title, slim)))
            return;
        setSessions(previous => {
            const next = previous.map(session => (session.id === id
                ? { ...session, messages: slim, title, updatedAt: now, remote: false, turnCount: undefined, usage: conversationUsageRef.current.saved(id) ?? session.usage }
                : session));
            persistSessions(next.filter(session => !session.remote));
            return next;
        });
        void saveChatHistory({ id, title, turns: slim, createdAt, updatedAt: now });
    };
    const saveHeldChatRef = useRef(saveHeldChat);
    saveHeldChatRef.current = saveHeldChat;
    useEffect(() => {
        if (!desktopWorkspace)
            return;
        const receive = (event) => {
            const result = parseDesktopActionResult(event.detail);
            if (!result)
                return;
            const ready = localFileContinuationsRef.current.receive(result, userKeyRef.current);
            if (!ready)
                return;
            updateConversationMessages({ id: ready.conversation, owner: userKeyRef.current }, previous => previous.map(message => message.actionOutcomeId === result.actionId ? { ...message, localFileResult: ready.continuation ?? undefined } : message));
        };
        window.addEventListener('gyro:desktop-action-result', receive);
        return () => window.removeEventListener('gyro:desktop-action-result', receive);
    }, [desktopWorkspace, updateConversationMessages]);
    /**
     * Pulse (section 10.1): whether a quick feedback card follows this settled action, decided once, here, against the device's
     * memory; a yes counts against the day, remembers the intent and asks for the tool's chips. The mark for the message, or null.
     * `conditions` overrides what is read live: the turn's own done frame is not "a running task" for its own card.
     */
    const offerPulseCard = useCallback((candidate, conditions = {}) => {
        const status = pulseOnRef.current;
        const intentKey = pulseIntentKey(candidate.tool, candidate.summary);
        const decision = pulseCardDecision({ askId: candidate.askId, actionKey: candidate.actionKey, highProfile: candidate.highProfile, intentKey }, 
        // The pane announces an edit only once it has applied (apps/office-addin pulse.ts), so none is applying when a card is offered.
        { status, busy: conditions.busy ?? pulseConditionsRef.current.busy, dictating: pulseConditionsRef.current.dictating, classified: candidate.classified, paneEditApplying: false }, pulseStore.read(), pulseCardedRef.current);
        if (!decision.show || !candidate.askId || !status)
            return null;
        pulseCardedRef.current.add(candidate.actionKey);
        const state = pulseStore.shown(intentKey);
        const chipKey = candidate.chipKey;
        void client.pulseChips(chipKey).then((chips) => setPulseChips((current) => (current[chipKey] ? current : { ...current, [chipKey]: chips })), () => undefined);
        return { key: candidate.actionKey, askId: candidate.askId, ...(candidate.actionId ? { actionId: candidate.actionId } : {}), tool: candidate.tool, chipKey, summary: candidate.summary, perDayLeft: Math.max(0, status.perDay - state.shown) };
    }, [client, pulseStore]);
    // The Office pane's hook (section 10.2): a settled higher-profile edit in the running turn, validated field by field.
    useEffect(() => {
        const receive = (event) => {
            const settled = parsePulseToolSettled(event.detail);
            if (settled)
                paneSettledRef.current.push(settled);
        };
        window.addEventListener(PULSE_PANE_SETTLED_EVENT, receive);
        return () => window.removeEventListener(PULSE_PANE_SETTLED_EVENT, receive);
    }, []);
    /**
     * Record an action's outcome in the chat it belongs to. `destination` is the chat the person decided in (resolveAction: the
     * confirm answer, an answer the Office pane will not run). An outcome that arrives on its own names none: the requester
     * inbox re-reports every finished action on each poll, and the Office pane's runner settles an action whenever its
     * acknowledgement lands, whichever chat is open then. It goes to the chat the action belongs to, once that chat is loaded
     * here (the inbox lists it again, so a chat opened later still gets it), else to the chats whose own transcripts show the
     * card; never to the open chat by default. Owner report 2026-10-09: "Done - Create the event ..." from one chat appeared
     * at the bottom of a new chat while his PPP was drafting. False when it was left for its chat to take once that chat is
     * open here (or for another identity).
     */
    const recordActionOutcome = useCallback((action, response, destination) => {
        const owner = userKeyRef.current;
        if (destination && destination.owner !== owner)
            return false;
        const fileConversation = localFileContinuationsRef.current.conversationFor(action.id, owner);
        // Where it goes: a local file task's own chat, else the chat the person decided in, else the chat bound to the action
        // (null when none is: then only chats whose own transcripts show the card take it).
        const decided = Boolean(fileConversation) || destination !== undefined;
        const home = fileConversation ?? (destination ? destination.id : actionChatsRef.current.conversationFor(action.id, owner));
        const root = actionRecord(response) ?? {};
        const normalized = normalizeActionReceipt(response);
        const outcomeState = classifyActionOutcome(response);
        // The done frame's `highProfile` mark stays with the action when the inbox re-reports it without one.
        const known = inboxActionsRef.current.get(action.id);
        const updatedAction = { ...(known?.highProfile === true ? { highProfile: true } : {}), ...action, status: outcomeState.state === 'error' ? 'failed' : outcomeState.state };
        inboxActionsRef.current.set(action.id, updatedAction);
        // A chat bound to it that is not loaded here records it once it is open: the inbox lists the action again (its result
        // is claimed only then), so nothing is lost and nothing lands in the open chat meanwhile.
        if (!decided && home !== null && !chatLoaded(home, owner))
            return false;
        // Why it failed, when the answer says so: the outcome line names it, keeps it when a later re-report has none
        // (upsertActionOutcomeMessage), and the card's own state line shows it (failedStateLine).
        const reason = typeof root.message === 'string' && root.message.trim() ? root.message.trim() : undefined;
        const summary = actionSummary(action);
        const outcome = root.status === 'submitted_for_approval'
            ? `Submitted for approval - ${summary} (a separate approver must confirm)`
            : root.status === 'client-executing'
                ? `${executingLabelFor(clientActionsRef.current)} - ${summary}`
                : outcomeState.completed
                    ? `Done - ${summary}`
                    : outcomeState.reconciliationRequired
                        ? `Outcome unknown - do not retry; administrator reconciliation required - ${summary}`
                        : root.status === 'cancelled'
                            ? `Cancelled - ${summary}`
                            : `Failed - ${reason ?? summary}`;
        // A filed Lab report reads as a receipt with its id and link (§1.6), not a generic "Done".
        const labReport = outcomeState.completed && action.app === 'the-lab' && action.tool === LAB_FILE_TOOL ? parseFiledReport(root.result) : null;
        if (labReport)
            filedReportsRef.current.set(action.id, labReport);
        const pendingShots = labReport ? pendingUploadsRef.current.get(action.id)?.length ?? 0 : 0;
        // A confirmed Bluebeam Studio upload answers with the Bridge's run in `applying`: the bytes are owed from this PC, so in the
        // desktop shell the card reads "Confirmed" and offers the content leg (bluebeamStudio.ts) instead of "Outcome unknown".
        const uploadLeg = nativeRef.current.bluebeamUpload ? bluebeamUploadLeg(action, response) : null;
        // The e-mail draft a confirmed create_draft saved: its Outlook link, offered on the Done card as "Open the draft" so
        // the person need not search Drafts for it (owner, 2026-10-20). It opens the way every link in a message does.
        const draftLink = outcomeState.completed ? draftOpenLink(action.tool, root.result) : undefined;
        // Pulse (section 10.1): a card follows the Done or Failed line of a higher-profile action, once, when the device's rules
        // say so. A Lab receipt and an owed Bluebeam content leg are not settled actions in that sense.
        const settled = (outcomeState.completed || outcomeState.failed) && !outcomeState.reconciliationRequired && !labReport && !uploadLeg;
        const pulse = settled ? offerPulseCard({
            askId: pulseAsksRef.current.get(action.id), actionKey: action.id, actionId: action.id, highProfile: updatedAction.highProfile === true,
            tool: action.tool, chipKey: pulseChipKey(action.tool, typeof action.input?.server === 'string' ? action.input.server : undefined), summary, classified: false,
        }) : null;
        const outcomeMessage = {
            role: 'assistant',
            content: labReport
                ? filedReceiptText(labReport, action.input.appId, pendingShots ? { state: 'uploading', count: pendingShots } : { state: 'none' })
                : uploadLeg ? `Confirmed - ${summary}` : outcome,
            error: outcomeState.failed && !uploadLeg,
            ...(outcomeState.failed && !uploadLeg && reason ? { failureReason: reason } : {}),
            actionReceipt: labReport ? undefined : normalized.receipt,
            vaultDownload: outcomeState.completed ? vaultDownloadHandoff(normalized.ephemeral) ?? undefined : undefined,
            ...(uploadLeg ? { bluebeamUpload: uploadLeg } : {}),
            actions: root.undoable ? [{ ...action, summary }] : undefined,
            ...(root.undoable ? { undoable: true } : {}),
            ...(labReport?.url ? { link: { href: labReport.url, label: 'Open it in The Lab' } } : {}),
            ...(draftLink ? { link: { href: draftLink, label: OPEN_DRAFT_LABEL } } : {}),
            actionOutcomeId: action.id,
            localFileResult: localFileContinuationsRef.current.settle(action.id, owner, home ?? '', outcomeState.completed && !outcomeState.reconciliationRequired, outcomeState.failed && !outcomeState.reconciliationRequired) ?? undefined,
            ...(pulse ? { pulse } : {}),
        };
        const labFiling = action.app === 'the-lab' && action.tool === LAB_FILE_TOOL && outcomeState.completed;
        // The inbox re-reports a Bluebeam upload whose run is still `applying` as reconciliation-required (gyro-svc settles it so
        // until the content leg lands); the card already offering the leg, or showing its result, is the better one and stays.
        const legOpen = !uploadLeg && action.tool === BLUEBEAM_UPLOAD_TOOL && outcomeState.reconciliationRequired;
        const record = (previous) => {
            if ((labFiling && !labReport && previous.some((m) => m.actionOutcomeId === action.id))
                || (legOpen && previous.some((m) => m.actionOutcomeId === action.id && m.bluebeamUpload)))
                return previous; // the inbox's copy has no result body; the receipt already shown is the better one
            // A re-report of the same action keeps the card the first report earned (the rules say once per action).
            const kept = previous.find((m) => m.actionOutcomeId === action.id)?.pulse;
            return upsertActionOutcomeMessage(previous, action.id, kept && !outcomeMessage.pulse ? { ...outcomeMessage, pulse: kept } : outcomeMessage);
        };
        // The card's own state line: an owed content leg reads "Confirmed" (the leg card under it carries the rest), not the
        // reconciliation words gyro-svc's answer would otherwise earn it.
        const cardState = uploadLeg ? 'done' : outcomeState.state;
        const recordIn = (chat) => {
            resolveInConversation(chat, action.id, cardState);
            const held = chat.id !== sessionIdRef.current ? conversationsRef.current.get(chat.id ?? '') : undefined;
            const before = held?.value.messages;
            // A held chat's transcript changed while another chat is open: it is saved now, as the open chat's autosave would.
            if (updateConversationMessages(chat, record) && held && held.value.messages !== before)
                saveHeldChatRef.current(held.id);
        };
        if (decided || home !== null) {
            recordIn({ id: home, owner });
            return true;
        }
        // No chat is bound to it (another device's action, or one from before a reload): only a chat whose own transcript shows
        // the card records it, the open chat as its transcript stands when the update applies. The state is kept (by action id,
        // so a chat without the card never shows it); no line is added to a chat that does not show the card.
        setResolved(previous => (previous[action.id] === cardState ? previous : { ...previous, [action.id]: cardState }));
        setMessages(previous => (holdsAction(previous, action.id) ? record(previous) : previous));
        for (const chat of heldChatsShowing(action.id, owner))
            recordIn({ id: chat.id, owner });
        return true;
    }, [chatLoaded, heldChatsShowing, offerPulseCard, resolveInConversation, setMessages, updateConversationMessages]);
    /** Set or drop one card's approval step (approvalCard.ts). */
    const setApprovalStep = (actionId, step) => {
        setApprovalSteps((current) => {
            if (step === undefined) {
                if (!(actionId in current))
                    return current;
                const next = { ...current };
                delete next[actionId];
                return next;
            }
            return { ...current, [actionId]: step };
        });
    };
    /** A preview the card may confirm with: it carries the exact phrase and has not expired. */
    const previewCurrent = (preview) => Boolean(preview?.confirmationPhrase) && (preview?.expiresAt ?? 0) * 1000 > Date.now();
    /**
     * Approve, the card's first press (owner decision 2026-10-10 17:57 PT, Gyro-Assistant#425): it never runs anything. An
     * action that needs a preview gets a current one first (fetched here when the card holds none), so the second step shows
     * what the receiver will do. A write gyro-svc confirms twice sends its first confirm, which gyro-svc answers
     * `confirm_again` and changes nothing: gyro-svc's question becomes the second step. Any other write sends nothing and
     * shows the card's own second step.
     */
    const approveAction = async (action) => {
        if (resolved[action.id] && resolved[action.id] !== 'awaiting-approval')
            return;
        if (!actionParametersAreFullyDisplayable(action.input, confirmLimits(action)))
            return;
        if (!localActionFoldersConfirmable(action, localFilesSnapshot))
            return;
        let preview = previews[action.id];
        if (action.requiresPreview && !previewCurrent(preview)) {
            preview = await previewAction(action);
            // No current preview with a phrase (it failed, or the receiver gave none): the card shows why; nothing is approved.
            if (!previewCurrent(preview))
                return;
        }
        const approved = action.requiresPreview ? previewKey(preview) : null;
        if (approveSendsFirstConfirm(action)) {
            await resolveAction(action, 'confirm', { preview, approved, first: true });
            return;
        }
        // A call to a tool on one of the PC's own connections: this press is the confirm (approvalCard.ts singlePressConfirm).
        if (singlePressConfirm(action)) {
            await resolveAction(action, 'confirm', { preview, approved });
            return;
        }
        setApprovalStep(action.id, { kind: 'confirm', preview: approved });
    };
    /**
     * Confirm, the card's second press: the only press that can run the write. The preview the person approved must still be
     * the action's preview, and current; gyro-svc's ticket, when it gave one, goes back with it. Anything else sends the card
     * back to Approve and runs nothing.
     */
    const confirmAction = async (action) => {
        const step = currentApprovalStep(approvalSteps[action.id], Date.now());
        if (!atConfirmStep(step)) {
            setApprovalStep(action.id, step);
            return;
        }
        if (action.requiresPreview) {
            const preview = previews[action.id];
            if (!previewCurrent(preview)) {
                setPreviews((current) => {
                    const next = { ...current };
                    delete next[action.id];
                    return next;
                });
                setApprovalStep(action.id, { kind: 'approve', note: PREVIEW_EXPIRED_TEXT });
                await previewAction(action);
                return;
            }
            if (previewKey(preview) !== step.preview) {
                setApprovalStep(action.id, { kind: 'approve', note: PREVIEW_CHANGED_TEXT });
                return;
            }
        }
        await resolveAction(action, 'confirm', { approved: step.preview, ...(step.kind === 'server' ? { reconfirm: step.ticket } : {}) });
    };
    /** Cancel on the second step: back to Approve. Nothing is sent, and the action stays as it was. */
    const backToApprove = (action) => setApprovalStep(action.id, undefined);
    /**
     * Send a decision (POST /v1/action). Cancel on the first step declines the action. A confirm comes only from the card's
     * two presses (approveAction, confirmAction): it sends the exact phrase the action's preview gave, never one the person
     * typed, says it knows gyro-svc's second confirmation, and carries gyro-svc's ticket on the press that runs the write.
     * gyro-svc's `confirm_again` (nothing ran) becomes the card's second step in place of its own, never a third step.
     */
    const resolveAction = async (action, decision, options = {}) => {
        const destination = { id: sessionIdRef.current, owner: userKeyRef.current };
        if (resolved[action.id] && resolved[action.id] !== 'awaiting-approval')
            return;
        if (decision === 'confirm' && !actionParametersAreFullyDisplayable(action.input, confirmLimits(action)))
            return;
        // A local action whose card could not name every folder (one not connected now) is never confirmed.
        if (decision === 'confirm' && !localActionFoldersConfirmable(action, localFilesSnapshot))
            return;
        if (!beginActionOp(action.id, 'decision'))
            return;
        try {
            const preview = options.preview ?? previews[action.id];
            // The exact phrase the action's preview gave (a governed write's, a forwarding rule's): the card sends it itself.
            const phrase = action.requiresPreview ? preview?.confirmationPhrase : undefined;
            if (decision === 'confirm' && action.requiresPreview && (preview?.expiresAt ?? 0) * 1000 <= Date.now()) {
                setPreviews((current) => {
                    const next = { ...current };
                    delete next[action.id];
                    return next;
                });
                setApprovalStep(action.id, { kind: 'approve', note: PREVIEW_EXPIRED_TEXT });
                endActionOp(action.id);
                await previewAction(action);
                return;
            }
            // What is confirmed is exactly the preview the person approved.
            if (decision === 'confirm' && action.requiresPreview && (!phrase || previewKey(preview) !== options.approved))
                return;
            if (decision === 'confirm' && destination.id) {
                const task = localFileOriginalRequest(messages, action.id);
                if (task)
                    localFileContinuationsRef.current.bind(action, destination.owner, destination.id, task);
            }
            // A Lab filing with screenshots: remember exactly which images belong to it NOW (the last N the person
            // attached), so what uploads is what the card showed even if more images are attached meanwhile.
            if (decision === 'confirm' && action.app === 'the-lab' && LAB_UPLOAD_TOOLS.has(action.tool)) {
                const shots = selectReportScreenshots(messages, Number(action.input.screenshotCount ?? 0));
                if (shots.length)
                    pendingUploadsRef.current.set(action.id, shots);
            }
            // The action belongs to the chat it is decided in: its later outcomes (the inbox, the Office pane's runner) go there.
            actionChatsRef.current.decided(action.id, destination.owner, destination.id);
            // The Office pane: this page confirms it, so if the answer is lost the inbox may start the run here (and only here).
            if (decision === 'confirm')
                clientActionRunnerRef.current?.confirming(action.id);
            const res = await client.action(action.id, decision, preview?.token ?? action.token, decision === 'confirm' ? phrase : undefined, options.reconfirm !== undefined ? { reconfirm: options.reconfirm } : {});
            if (destination.owner !== userKeyRef.current)
                return;
            track('gyro_action', { app: appKey, tool: action.tool, result: decision });
            if (decision === 'confirm' && isConfirmAgain(res)) {
                // gyro-svc's second confirmation: nothing ran. Its question takes the place of the card's own second step. A ticket
                // that went back and drew another `confirm_again` had lapsed or was refused: the card starts again from Approve.
                const step = options.reconfirm === undefined ? serverConfirmStep(res, action.id, options.approved ?? null) : null;
                setApprovalStep(action.id, step ?? { kind: 'approve', note: options.reconfirm === undefined ? CONFIRM_AGAIN_UNUSABLE_TEXT : TICKET_LAPSED_TEXT });
                return;
            }
            if (res.status === 'done' && res.token) {
                claimedResultsRef.current.add(action.id);
                try {
                    const claimed = await client.claimActionResult(action.id, res.token);
                    recordActionOutcome(action, { ...res, ...claimed }, destination);
                    void finishLabWriteRef.current(action, { ...res, ...claimed });
                }
                catch {
                    // Dispatch already succeeded. Keep that outcome and let inbox polling retry the receipt claim.
                    claimedResultsRef.current.delete(action.id);
                    recordActionOutcome(action, res, destination);
                }
            }
            else {
                recordActionOutcome(action, res, destination);
                void finishLabWriteRef.current(action, res);
            }
            // A confirm that left the action running (client-executing) or waiting for an approver: its outcome arrives
            // through the inbox, so poll at the active cadence now rather than after an idle 30 s wait.
            if (isLiveActionStatus(classifyActionOutcome(res).state))
                inboxPollerRef.current?.markLive();
            // The Office pane runs a client-executing action itself, exactly once, then acknowledges it (contract §3.3). An
            // answer that is not for exactly the confirmed card is never run here: loud, and the card shows the error.
            const hostRun = clientExecutingFromResponse(res, action);
            if (hostRun)
                clientActionRunnerRef.current?.start(hostRun, action);
            else if (clientActionsRef.current) {
                const refusal = clientExecutingRefusal(res, action);
                if (refusal)
                    withholdHostRun(action, refusal, destination);
            }
            setApprovalStep(action.id, undefined);
            setPreviews((current) => {
                const next = { ...current };
                delete next[action.id];
                return next;
            });
        }
        catch (e) {
            if (destination.owner !== userKeyRef.current)
                return;
            if (options.first) {
                // The first of gyro-svc's two confirmations changes nothing, so its failure is never ambiguous: the card goes back
                // to Approve with the reason, the action stays as it was, and the next Approve fetches a fresh preview.
                setPreviews((current) => {
                    const next = { ...current };
                    delete next[action.id];
                    return next;
                });
                const reason = (e instanceof GyroActionResponseError ? e.message : 'Gyro could not be reached').replace(/[.\s]+$/, '');
                setApprovalStep(action.id, { kind: 'approve', note: `Nothing ran: ${reason}. Approve it again to retry.` });
                void client.reportError({ message: e instanceof Error ? e.message : 'action failed', stack: e instanceof Error ? e.stack : undefined, where: 'action:approve' });
                return;
            }
            const ambiguous = governedActionFailureRequiresReconciliation(e, decision, action.requiresPreview);
            resolveInConversation(destination, action.id, ambiguous ? 'reconciliation-required' : 'error');
            updateConversationMessages(destination, (prev) => [...prev, {
                    role: 'assistant',
                    content: ambiguous
                        ? `Outcome unknown - do not retry; administrator reconciliation required - ${actionSummary(action)}`
                        : (e instanceof GyroActionResponseError ? e.message : `Could not ${decision} the action.`),
                    error: !ambiguous,
                }]);
            void client.reportError({ message: e instanceof Error ? e.message : 'action failed', stack: e instanceof Error ? e.stack : undefined, where: `action:${decision}` });
        }
        finally {
            endActionOp(action.id);
        }
    };
    /**
     * The Preview button, and Approve when the action needs a current preview: fetches the action's preview and returns the
     * one the card now holds (undefined when there is none). A held preview that is still current is returned as it is.
     */
    const previewAction = async (action) => {
        if (!beginActionOp(action.id, 'preview'))
            return undefined;
        try {
            if (previews[action.id] && !action.requiresPreview) {
                setPreviews((p) => {
                    const n = { ...p };
                    delete n[action.id];
                    return n;
                });
                return undefined;
            }
            if (previews[action.id]?.confirmationPhrase && (previews[action.id]?.expiresAt ?? 0) * 1000 > Date.now())
                return previews[action.id];
            const res = await client.previewAction(action.id, previews[action.id]?.token ?? action.token);
            const text = !res.supported ? 'No preview available for this action.' : res.error ? `Preview error: ${res.error}` : `Dry run: ${JSON.stringify(res.preview)}`;
            const summary = res.supported && !res.error ? previewSummary(res.preview) : undefined;
            const fields = res.supported && !res.error ? previewFields(res.preview) : [];
            // A "not connected" refusal's connection to start: the card offers Connect (or Allow changes) instead of a sentence alone.
            const connect = res.supported ? previewConnect(res) : undefined;
            const fresh = {
                text,
                ...(summary ? { summary } : {}),
                ...(fields.length ? { fields } : {}),
                ...(connect ? { connect } : {}),
                confirmationPhrase: res.confirmationPhrase,
                expiresAt: res.expiresAt,
                implementationRevision: res.implementationRevision,
                token: res.token,
            };
            setPreviews((p) => ({ ...p, [action.id]: fresh }));
            return fresh;
        }
        catch (error) {
            // Preserve an already-good receipt/token if a delayed or failed preview response arrives later.
            if (!previews[action.id]?.confirmationPhrase) {
                setPreviews((current) => ({ ...current, [action.id]: { text: `Preview error: ${error instanceof Error ? error.message : 'preview failed'}`, token: action.token } }));
            }
            return undefined;
        }
        finally {
            endActionOp(action.id);
        }
    };
    /**
     * The approval card's buttons (approvalCard.ts). First step: Approve (File bug for a Lab report), Preview and Cancel;
     * Cancel declines the action. Second step: the card's own confirmation, or gyro-svc's question, with the action's
     * summary and the receiver's own preview summary, then Confirm and Cancel; that Cancel goes back to the first step and
     * sends nothing. Nothing on the card asks the person to type a phrase.
     */
    const renderApprovalControls = (a) => {
        const step = currentApprovalStep(approvalSteps[a.id], nowMs);
        if (atConfirmStep(step)) {
            const receiverSays = a.requiresPreview ? previews[a.id]?.summary : undefined;
            // The preview's own fields and, for a change in the person's own account, that it is done as them and how they
            // reverse it themselves (approvalCard.ts previewFields, personWriteNotes): read in full before Confirm.
            const receiverFields = a.requiresPreview ? previews[a.id]?.fields ?? [] : [];
            const personNotes = personWriteNotes(a.app, a.tool);
            return (_jsxs("div", { className: "gyro-action-confirm-step", role: "group", "aria-label": "Confirm this action", children: [_jsxs("div", { className: "gyro-action-confirm-text", id: `gyro-confirm-step-${a.id}`, children: [_jsx("strong", { children: step.kind === 'server' ? step.message : CLIENT_CONFIRM_TEXT }), _jsx("span", { children: actionSummary(a) }), receiverSays && _jsx("span", { children: `${a.app ? `${actionSystemName(a)} says` : 'The preview says'}: ${receiverSays}` }), _jsx(PreviewFieldList, { fields: receiverFields }), personNotes.map((note) => _jsx("span", { className: "gyro-action-person-note", children: note }, note))] }), _jsxs("div", { className: "gyro-action-btns", children: [_jsx("button", { type: "button", className: "gyro-btn", onClick: () => void confirmAction(a), "aria-describedby": `gyro-confirm-step-${a.id}`, disabled: !actionParametersAreFullyDisplayable(a.input, confirmLimits(a)) || !localActionFoldersConfirmable(a, localFilesSnapshot) || Boolean(actionOps[a.id]) || Boolean(a.contractChanged), children: "Confirm" }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => backToApprove(a), disabled: Boolean(actionOps[a.id]), children: "Cancel" })] })] }));
        }
        return (_jsxs(_Fragment, { children: [step?.kind === 'approve' && _jsx("div", { className: "gyro-action-note", role: "status", children: step.note }), _jsxs("div", { className: "gyro-action-btns", children: [_jsx("button", { type: "button", className: "gyro-btn", onClick: () => void approveAction(a), disabled: !actionParametersAreFullyDisplayable(a.input, confirmLimits(a)) || !localActionFoldersConfirmable(a, localFilesSnapshot) || Boolean(actionOps[a.id]) || Boolean(a.contractChanged), children: a.app === 'the-lab' && a.tool === LAB_FILE_TOOL ? 'File bug' : singlePressConfirm(a) ? 'Confirm' : 'Approve' }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => void previewAction(a), title: "Dry run", disabled: Boolean(actionOps[a.id]) || Boolean(a.requiresPreview && previews[a.id]?.confirmationPhrase && (previews[a.id]?.expiresAt ?? 0) * 1000 > nowMs), children: "Preview" }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => void resolveAction(a, 'cancel'), disabled: Boolean(actionOps[a.id]), children: "Cancel" })] })] }));
    };
    // The Office pane's confirmed actions (contract §3.3, clientActions.ts): run once on the host, then acknowledged; the
    // card records the outcome once gyro-svc took it. Inert without the clientActions prop. The runner belongs to the
    // effect that made it (mountClientActionRunner), so StrictMode's and Fast Refresh's extra cleanup leaves a live one.
    const clientActionSettledRef = useRef(() => undefined);
    clientActionSettledRef.current = (action, response) => {
        recordActionOutcome(action, response);
        inboxPollerRef.current?.markLive();
    };
    const clientActionRunnerRef = useRef(null);
    useEffect(() => mountClientActionRunner(clientActionRunnerRef, () => createClientActionRunner({
        actions: () => clientActionsRef.current,
        ack: (path, body) => client.ackClientAction(path, body),
        settled: (action, response) => clientActionSettledRef.current(action, response),
        // Claimed atomically in IndexedDB, shared by every pane of the add-in: two panes never both run one action.
        ledger: browserClientActionLedger(clientActionLedgerKey(appKey, userKey)),
    })), [client, appKey, userKey]);
    // Approver-released Office actions another page confirmed: their cards offer "Run it here" (the inbox refreshes the
    // list; clientActions.ts decides), so the person picks the document a write belongs to. Pressing it takes the action
    // exactly as a confirmed one is taken: the same atomic claim, execute and acknowledgement.
    const [runHereOffers, setRunHereOffers] = useState([]);
    const runApprovedHere = (action) => {
        setRunHereOffers((existing) => existing.filter((id) => id !== action.id));
        if (clientActionRunnerRef.current?.runHere(action.id))
            inboxPollerRef.current?.markLive();
    };
    // A client-executing answer that is not for exactly the confirmed card (clientExecutingRefusal) never runs here. It
    // is said loudly (the action id and the reason, never the input) and the card shows the error, not "Running in ...".
    const withholdHostRun = (action, reason, destination) => {
        clientActionRunnerRef.current?.withhold(action.id);
        console.error(clientActionRefusalMessage(action.id, reason));
        recordActionOutcome(action, { status: 'error', message: `Not run here: ${reason}. Nothing was changed.` }, destination);
        void client.reportError({ message: `client action not run: ${reason}`, where: 'action:client-executing' });
    };
    // Requester inbox (GET /v1/actions): how a result is folded in. Re-created every render so it always sees the
    // current client/state; the poller calls it through the ref. Each updater returns its previous value when
    // the poll changed nothing, so an idle inbox neither re-renders the panel nor re-saves the conversation.
    const applyInbox = async (actions, current) => {
        if (!current())
            return;
        inboxActionsRef.current = new Map(actions.map(action => [action.id, action]));
        // The Office pane: an acknowledgement that has not landed is sent again while the inbox lists the action executing or
        // awaiting reconciliation. A run starts on its own only in the page that pressed Confirm (a lost confirm answer, or
        // an approver's release), and only if no other pane or earlier page claimed it (clientActions.ts). Any other pane
        // only offers an approver-released action ("Run it here"); one that merely sees an action never runs it.
        const runner = clientActionRunnerRef.current;
        const offered = [];
        for (const action of actions) {
            const sighting = clientExecutingFromInbox(action);
            if (!sighting || !runner)
                continue;
            runner.sighted(sighting, action);
            if (runner.offered(sighting.actionId))
                offered.push(sighting.actionId);
        }
        setRunHereOffers((existing) => jsonEqual(existing, offered) ? existing : offered);
        setInboxSync({ loading: false, error: false, lastSyncedAt: Date.now() });
        // A live action whose card belongs to another chat stays there and is not surfaced in the open one. Any other live
        // action is shown in the open chat (its card is there, or the attention line adds it), so it belongs to that chat.
        const elsewhere = new Set(actions.filter((action) => belongsElsewhere(action.id)).map((action) => action.id));
        for (const action of actions) {
            if (isLiveActionStatus(action.status) && !elsewhere.has(action.id))
                actionChatsRef.current.shown(action.id, userKeyRef.current, sessionIdRef.current);
        }
        setMessages((previous) => mergeInboxActions(previous, actions, (discovered) => ({ role: 'assistant', content: 'A pending action requires your attention.', actions: discovered }), (actionId) => elsewhere.has(actionId)));
        setPreviews((existing) => {
            const next = { ...existing };
            for (const action of actions) {
                if (!action.providerPreview)
                    continue;
                const summary = previewSummary(action.providerPreview.preview);
                const fields = previewFields(action.providerPreview.preview);
                next[action.id] = {
                    text: `Provider preview: ${JSON.stringify(action.providerPreview.preview)}`,
                    ...(summary ? { summary } : {}),
                    ...(fields.length ? { fields } : {}),
                    confirmationPhrase: action.providerPreview.confirmationPhrase,
                    expiresAt: action.providerPreview.expiresAt,
                    implementationRevision: action.providerPreview.implementationRevision,
                    token: action.token,
                };
            }
            return jsonEqual(next, existing) ? existing : next;
        });
        setResolved((existing) => {
            const next = { ...existing };
            for (const action of actions) {
                const expiredAwaiting = action.status === 'awaiting-approval'
                    && Boolean(action.providerPreview)
                    && (action.providerPreview?.expiresAt ?? 0) * 1000 <= Date.now();
                if (action.contractChanged)
                    next[action.id] = 'error';
                else if (action.status === 'proposed' || action.status === 'previewed' || expiredAwaiting)
                    delete next[action.id];
                else if (action.status === 'awaiting-approval')
                    next[action.id] = 'awaiting-approval';
                // An Office action this page refused to run stays in error while gyro-svc waits out its lease (withholdHostRun).
                else if (action.status === 'executing')
                    next[action.id] = runner?.withheld(action.id) ? 'error' : 'executing';
                else if (action.status === 'done')
                    next[action.id] = 'done';
                else if (action.status === 'failed')
                    next[action.id] = 'error';
                else if (action.status === 'reconciliation-required')
                    next[action.id] = 'reconciliation-required';
                else if (action.status === 'cancelled' || action.status === 'expired')
                    next[action.id] = 'cancelled';
            }
            return jsonEqual(next, existing) ? existing : next;
        });
        for (const action of actions) {
            if (!action.resultReady && (action.status === 'done'
                || action.status === 'failed'
                || action.status === 'reconciliation-required'
                || action.status === 'cancelled')) {
                recordActionOutcome(action, {
                    status: action.status === 'done'
                        ? 'done'
                        : action.status === 'failed'
                            ? 'error'
                            : action.status,
                    message: action.errorMessage,
                });
                continue;
            }
            if (!action.resultReady || !action.token || claimedResultsRef.current.has(action.id))
                continue;
            if (action.status !== 'done' && action.status !== 'failed' && action.status !== 'reconciliation-required')
                continue;
            // A result is claimed where it can be shown: one whose chat is not loaded here waits until that chat is open, and one
            // no chat here knows (another tab's, or one from before a reload) is left alone (gyro-svc keeps it until it expires).
            if (!outcomeShowable(action.id))
                continue;
            if (!beginResultClaim(claimedResultsRef.current, action.id, () => beginActionOp(action.id, 'result')))
                continue;
            try {
                const result = await client.claimActionResult(action.id, action.token);
                // The inbox result of a Lab write carries no `result` body; keep the one already on screen. A result its chat could
                // not take (that chat stopped being loaded while the claim was in flight) is claimed again once the chat is open.
                if (current() && recordActionOutcome(action, result))
                    void finishLabWriteRef.current(action, result);
                else
                    claimedResultsRef.current.delete(action.id);
            }
            catch {
                claimedResultsRef.current.delete(action.id);
            }
            finally {
                endActionOp(action.id);
            }
        }
    };
    const applyInboxRef = useRef(applyInbox);
    applyInboxRef.current = applyInbox;
    // The poll itself (cadence, one request in flight, 10 s deadline, backoff honouring Retry-After) lives in
    // InboxPoller; see inboxPoll.ts for the load contract. One poller per client, driven by `onScreen`: it
    // polls once on show, never while the panel is closed or off screen. Polling is best-effort; a failure
    // only lengthens the wait before the next attempt.
    useEffect(() => {
        let retired = false;
        const poller = new InboxPoller({
            fetch: async (signal) => {
                try {
                    return await client.listActions({ signal });
                }
                catch (error) {
                    if (!retired)
                        setInboxSync(previous => ({ ...previous, loading: false, error: true }));
                    throw error;
                }
            },
            apply: (actions, current) => applyInboxRef.current(actions, current),
        });
        inboxPollerRef.current = poller;
        return () => {
            retired = true;
            poller.dispose();
            if (inboxPollerRef.current === poller)
                inboxPollerRef.current = null;
        };
    }, [client, userKey, inboxEpoch]);
    useEffect(() => {
        inboxPollerRef.current?.setVisible(onScreen);
    }, [client, onScreen, userKey, inboxEpoch]);
    const undoAction = async (action) => {
        const res = await client.undoAction(action.id);
        const withdraw = action.app === 'the-lab' && action.tool === LAB_FILE_TOOL;
        setMessages((prev) => [...prev, { role: 'assistant', content: res.status === 'undone' ? `↩️ ${withdraw ? 'Withdrawn' : 'Undone'} — ${actionSummary(action)}` : `⚠️ Could not ${withdraw ? 'withdraw' : 'undo'} — ${res.message ?? actionSummary(action)}`, error: res.status !== 'undone' }]);
    };
    const giveFeedback = async (traceId, rating, askId) => {
        if (!traceId || feedbackGiven[traceId])
            return;
        setFeedbackGiven((f) => ({ ...f, [traceId]: rating }));
        // Pulse on and the reply has its ask id (docs/CONTRACT-pulse.md 6.1, 10.3): the thumb goes to Pulse at once (the service
        // stamps the trace's rating too) and the one-line comment box opens under the actions row. Otherwise, as before.
        if (pulseOnRef.current?.cards && askId) {
            setPulseThumbBox({ traceId, askId, thumb: rating });
            await client.pulseFeedback({ askId, kind: 'thumb', thumb: rating });
            return;
        }
        await client.feedback(traceId, rating);
    };
    /**
     * Step 2 of the share flow (L-26): mint the link ONLY after the user confirmed in the dialog. The
     * result is shown in the dialog with a Copy button — nothing is written to the clipboard here. The
     * reducer drops a result that arrives after Cancel (or for another answer), so a slow POST can never
     * surface a link the user did not ask for.
     */
    const createShareLink = async (key, m) => {
        dispatchShare({ type: 'confirm' });
        // The chat's id goes with it, so the service can refuse a link to a chat that holds pay data (its line is shown).
        const outcome = await client.shareAnswer(m.question ?? 'Gyro answer', m.content, sessionIdRef.current || undefined);
        if (outcome && 'refused' in outcome) {
            dispatchShare({ type: 'refused', key, message: outcome.refused });
            return;
        }
        const url = outcome?.url ?? null;
        dispatchShare({ type: 'created', key, url });
        if (url)
            track('gyro_share', { app: appKey });
    };
    /** Explicit, user-initiated copy of a link that is already on screen (the Copy button). */
    const copyShareLink = (url) => {
        try {
            void navigator.clipboard.writeText(url).then(() => dispatchShare({ type: 'copied' }), () => dispatchShare({ type: 'copy-failed' }));
        }
        catch {
            dispatchShare({ type: 'copy-failed' }); // no clipboard API (insecure context) — the field is still selectable
        }
    };
    /** Start a fresh recognition session (resets any stale instance). Used by the mic + the hotkey. */
    const beginListening = () => {
        // A web page offers no voice input (voiceInputFor): nothing below runs there, so whatever asks, it never makes a
        // recognizer or opens the microphone.
        if (voiceInput === 'off') {
            setVoiceError(VOICE_INPUT_OFF);
            return;
        }
        // Do not capture Gyro's own spoken reply into the user's dictation.
        stopReplySpeech();
        const NativeSR = window.GyroSpeechRecognition;
        // The desktop shell dictates only through the local Windows recognizer. Without it (no English recognizer
        // installed), it says so: it never falls back to WebView2's browser recognizer or opens a getUserMedia meter.
        const desktopShell = isDesktopShell(window);
        const SR = NativeSR ?? (desktopShell ? undefined :
            window.SpeechRecognition ??
                window.webkitSpeechRecognition);
        recognitionRef.current?.cancel();
        const epoch = attachmentEpochRef.current;
        let nativeLevels = Array(VOICE_METER_BARS).fill(0);
        const recognition = new SpeechInput({
            createRecognition: SR ? () => new SR() : undefined,
            // The desktop's Windows online recognizer sends the final text of a long phrase after the stop, often later than 5 s.
            stopTimeoutMs: NativeSR ? NATIVE_DICTATION_STOP_TIMEOUT_MS : BROWSER_DICTATION_STOP_TIMEOUT_MS,
            unsupportedError: desktopShell && !NativeSR ? 'windows-recognizer-missing' : undefined,
            initialText: input,
            isCurrent: () => recognitionRef.current === recognition && attachmentEpochRef.current === epoch && userKeyRef.current === userKey,
            onState: state => {
                if (state === 'idle' || state === 'stopping') {
                    voiceMeterRef.current?.stop();
                    voiceMeterRef.current = null;
                    setVoiceMeter(undefined);
                }
                setSpeechInputState(state);
            },
            onError: setVoiceError,
            onDraft: setInput,
            onComplete: text => { setInput(text); requestAnimationFrame(() => inputRef.current?.focus()); },
            onAudioLevel: level => { nativeLevels = [...nativeLevels.slice(1), level]; setVoiceMeter({ status: 'live', levels: nativeLevels }); },
        });
        recognitionRef.current = recognition;
        voiceMeterRef.current?.stop();
        setVoiceMeter(undefined);
        if (SR && !NativeSR)
            voiceMeterRef.current = startVoiceMeter({ onUpdate: snapshot => {
                    if (recognitionRef.current === recognition && attachmentEpochRef.current === epoch && userKeyRef.current === userKey)
                        setVoiceMeter(snapshot);
                } });
        recognition.start();
    };
    const startListening = () => {
        if (recognitionRef.current?.isActive) {
            recognitionRef.current.stop();
            return;
        }
        track('gyro_voice_input', { app: appKey });
        beginListening();
    };
    /** Open Gyro and immediately start voice capture — the target of the global shortcut + gyro:listen. */
    const triggerVoice = () => {
        setShowPets(false);
        // Like the mic button, a second press while dictating finishes it (keeping the words) instead of restarting.
        if (recognitionRef.current?.isActive) {
            recognitionRef.current.stop();
            return;
        }
        setOpen(true);
        setShowSessions(false);
        setShowCapabilities(false);
        setShowNotifications(false);
        setShowMemory(false);
        // Keep speech startup in the click/hotkey gesture; a timer can lose browser permission context.
        beginListening();
    };
    triggerVoiceRef.current = triggerVoice;
    /**
     * Toggle voice capture exactly as if the mic button was clicked (stops an active session, starts one
     * otherwise), opening the widget first when it's launcher-collapsed. Target of the 'gyro:voice-toggle'
     * host hook (desktop global push-to-talk lands on this).
     */
    const toggleVoiceHook = () => {
        if (!open)
            triggerVoice();
        else
            startListening();
    };
    toggleVoiceHookRef.current = toggleVoiceHook;
    const copyAnswer = (text) => {
        track('gyro_copy', { app: appKey, kind: 'answer' });
        void navigator.clipboard.writeText(text).catch(() => undefined);
    };
    // Status check WITH auto-recovery. A failed check no longer LATCHES on `configured === null`: it schedules
    // a retry on a capped backoff while the panel is on screen, so a transient auth/network blip clears itself
    // instead of showing "unavailable" until a full page reload (#123). Keyed on `onScreen` (open AND the
    // surface visible), not `open`: a hidden desktop overlay or a background tab must not keep a 3–30 s retry
    // loop running while nobody can see the banner it feeds (0.3.9.3 review); it resumes on the next show.
    // The cleanup cancels a pending retry when the surface leaves the screen or the effect re-runs, so we
    // never stack timers or fire after unmount.
    useEffect(() => {
        if (!(onScreen && (configured === null || !petsCapabilityCurrent)))
            return;
        let cancelled = false;
        client
            .status()
            .then((s) => {
            if (cancelled)
                return;
            setConfigured(s.configured);
            setPetsCapability({ serviceKey: petServiceKey, userKey, epoch: petSessionEpoch, enabled: s.petsEnabled === true });
            attention.acceptCapability(s.attention);
            setUnreachable(false);
            setUnreachableReason(null);
            setCapabilities(s.tools ?? []);
            setAgentStatus(s.agent);
            setKnowledgeCapabilities(knowledgeStatus(s));
            setFileAttachmentsSupported(s.fileAttachments === true);
            setLookStatus(s.look === true);
            rememberLookStatus(s.look === true);
            setPppStatus(s.pppEnabled);
            setSkillsStatus(s.skills && typeof s.skills === 'object' ? { personal: s.skills.personal === true, company: s.skills.company === true } : undefined);
            setPulseOn(parsePulseStatus(s.pulse));
            // The model button: reconcile the saved per-app pick with the set THIS user may still use. A pick that disappeared
            // (disabled, role revoked, catalog edited) or names the High model (never a saved choice) falls back to the default.
            const visible = s.models ?? [];
            setModels(visible);
            let stored = null;
            try {
                stored = localStorage.getItem(modelKey);
                localStorage.removeItem(`${modelKey}-fast-carried`); // the retired depth chooser's one-time carry-over mark
            }
            catch {
                /* storage unavailable */
            }
            const choice = resolveModelChoice(visible, stored);
            setModelChoice(choice);
            if (stored && !choice) {
                try {
                    localStorage.removeItem(modelKey);
                }
                catch {
                    /* ignore */
                }
            }
            if (s.persona?.greeting)
                setPersonaGreeting(s.persona.greeting);
            applyDayGreeting(dayGreetingFromPersona(s.persona));
        })
            // A failed status call is an auth/connectivity problem for THIS app — not a missing model key.
            // Don't swallow it: capture WHY (HTTP status + the server's reason), show it in the banner, log it,
            // and forward it to the central error sink — so a "was working → unavailable" flip is diagnosable
            // instead of collapsing to a bare generic message with no cause.
            .catch((e) => {
            if (cancelled)
                return;
            setUnreachable(true);
            const msg = e instanceof Error ? e.message : 'status check failed';
            setUnreachableReason(msg);
            console.warn(`[gyro] status check failed for app '${appKey}': ${msg}`);
            void client.reportError({ message: msg, stack: e instanceof Error ? e.stack : undefined, where: 'status' });
            // Recover: retry with capped exponential backoff (3s → 6s → 12s → 24s → 30s cap) rather than
            // latching. `configured` stays null, so bumping statusAttempt re-runs this effect and re-checks.
            const delay = Math.min(30_000, 3_000 * 2 ** Math.min(statusAttempt, 3));
            statusRetryRef.current = setTimeout(() => setStatusAttempt((n) => n + 1), delay);
        });
        return () => {
            cancelled = true;
            if (statusRetryRef.current) {
                clearTimeout(statusRetryRef.current);
                statusRetryRef.current = undefined;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onScreen, configured, statusAttempt, userKey, petServiceKey, petSessionEpoch]);
    // Panel open: (re)load local sessions, sync server history, focus the input. Separate from the status
    // check above so a status retry doesn't re-sync history or steal focus every few seconds.
    useEffect(() => {
        if (!open)
            return;
        const saved = loadSessions();
        for (const session of saved) {
            conversationTitlesRef.current.remember(session);
            // A chat's token total from the local cache, for chats this session has not touched yet.
            if (session.usage)
                conversationUsageRef.current.seed(session.id, session.usage);
        }
        setSessions(saved);
        void syncRemoteHistory();
        const t = setTimeout(() => {
            // Do not steal a selection made while the panel was finishing its first render.
            const active = document.activeElement;
            if (active instanceof HTMLElement && active.closest('.gyro-panel') && active.matches('button,input,textarea,select,[tabindex]'))
                return;
            if (webTouchScreen())
                return;
            inputRef.current?.focus();
        }, 50);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userKey]);
    // The desktop shell keeps the panel open for the life of the page and only hides and shows its window, so the effect
    // above reads the list once per Gyro start: each time the host shows Gyro again, the list is read again.
    useEffect(() => {
        if (!open || hostVisibility !== 'shown' || Date.now() - historySyncAtRef.current < HISTORY_RESYNC_MS)
            return;
        void syncRemoteHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hostVisibility]);
    // Opening the panel, or switching saved sessions, jumps to the newest message.
    useEffect(() => {
        if (workspace && !floatingWorkspace)
            return;
        // The floating panel unmounts when closed: reopening (or loading a saved chat) shows the newest message once, never follows.
        if (floatingWorkspace && messages.every(m => m.welcome))
            return;
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: floatingWorkspace ? 'instant' : 'smooth' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, showSessions, loadedSessionRevision, workspace, floatingWorkspace]);
    // A new message only pulls the view down if the reader is ALREADY at the
    // bottom. Streaming rewrites `messages` on every token, so scrolling
    // unconditionally here drags a reader who scrolled up to re-read something
    // back down several times a second — they cannot read the answer they were
    // just given. Scrolled away means left alone. The workspace layouts follow
    // too (owner, 2026-10-08: the chat did not scroll down as Gyro replied): a
    // send puts the reader at the bottom (useSendScroll), and this keeps them
    // there while the answer grows, instantly there so the stream never lags.
    useEffect(() => {
        const el = listRef.current;
        if (!el)
            return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom <= 120)
            el.scrollTo({ top: el.scrollHeight, behavior: workspace ? 'instant' : 'smooth' });
    }, [messages, workspace]);
    // The floating panel's keys follow the person's last pointer press or focus move (floatingPanelHasKeys): in Gyro (the panel
    // or its launcher), or on the host page. Escape from a host control hidden under the panel closes it and leaves focus there.
    const lastMoveInsidePanelRef = useRef(false);
    const keepFocusOnCloseRef = useRef(false);
    useEffect(() => {
        openRef.current = open;
        if (open) {
            hasOpenedRef.current = true;
            setUnseen(0); // opening the widget marks every finished answer as seen
        }
        else {
            setPaletteOpen(false);
            stopAudio();
            // Return focus to the launcher only on a genuine close — one that follows an open. On the
            // initial mount `open` is already false, so focusing here would steal focus from the host
            // page and break its skip link / keyboard navigation (a11y regression reported by Meridian;
            // it affects every app that embeds the widget). The sibling focus-trap effect is likewise
            // gated on `open`; this one previously was not. Escape from a host control hidden under the floating panel closes
            // it to reveal that control, so focus stays there.
            if (hasOpenedRef.current && !keepFocusOnCloseRef.current)
                launcherRef.current?.focus();
            keepFocusOnCloseRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Returning to the tab with the widget already open also clears the unread count.
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === 'visible' && openRef.current)
                setUnseen(0);
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);
    // The floating header keeps its title readable (headerOverflow.ts): the buttons that do not fit beside it wait in a "More
    // actions" menu, measured when the panel opens or its layout changes and whenever the header is resized. Only a floating
    // panel does this; the desktop app's and the Office pane's headers are as they were.
    const headerRef = useRef(null);
    const [headerOverflowIds, setHeaderOverflowIds] = useState([]);
    const headerOverflowOn = floatingPanel && open;
    useClientLayoutEffect(() => {
        const keep = (next) => setHeaderOverflowIds((current) => current.join(' ') === next.join(' ') ? current : next);
        if (!headerOverflowOn) {
            keep([]);
            return undefined;
        }
        const measure = () => keep(measureHeaderOverflow(headerRef.current));
        measure();
        const header = headerRef.current;
        const observer = header && typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        if (header)
            observer?.observe(header);
        return () => observer?.disconnect();
    }, [headerOverflowOn, workspace, expanded, floatingFullScreen]);
    /** A floating header button's overflow marks: its id, and hidden while it waits in the More menu. Nothing elsewhere. */
    const headerActionProps = (id) => {
        if (!floatingPanel)
            return {};
        return headerOverflowIds.includes(id) ? { 'data-header-action': id, 'data-header-overflowed': '', hidden: true } : { 'data-header-action': id };
    };
    const headerOverflowItems = headerOverflowIds.map((id) => {
        if (id === 'new-chat')
            return { id, label: 'New chat', icon: _jsx(Plus, { size: 15, "aria-hidden": "true" }) };
        if (id === 'fork-chat')
            return { id, label: 'Fork chat', icon: _jsx(GitFork, { size: 15, "aria-hidden": "true" }) };
        if (id === 'commands')
            return { id, label: 'Commands (Ctrl+K)', icon: _jsx(Command, { size: 15, "aria-hidden": "true" }) };
        if (id === 'expand')
            return { id, label: expanded ? 'Shrink' : 'Expand', icon: expanded ? _jsx(Minimize2, { size: 15, "aria-hidden": "true" }) : _jsx(Maximize2, { size: 15, "aria-hidden": "true" }) };
        if (id === 'capabilities')
            return { id, label: 'What can Gyro do here?', icon: _jsx(Compass, { size: 15, "aria-hidden": "true" }) };
        if (id === 'memory')
            return { id, label: 'Your memory', icon: _jsx(Brain, { size: 15, "aria-hidden": "true" }) };
        if (id === 'history')
            return { id, label: 'Chat history', icon: _jsx(Clock, { size: 15, "aria-hidden": "true" }) };
        return null;
    }).filter((item) => item !== null);
    // An item of the More menu does exactly what its header button does: it presses that button, hidden in the header.
    const lastHeaderActionRef = useRef(null);
    const selectHeaderAction = (id) => {
        lastHeaderActionRef.current = id;
        headerRef.current?.querySelector(`:scope > .gyro-row > [data-header-action="${id}"]`)?.click();
    };
    // The More button went away while it had focus (Expand made room for every button): focus goes to the button of the action
    // just run, now in the header, else the header's first button, never to the page's body.
    const focusHeaderAfterMenu = () => {
        if (!isPageBody(document.activeElement))
            return;
        const row = headerRef.current?.querySelector(':scope > .gyro-row');
        const shown = (id) => id ? row?.querySelector(`:scope > [data-header-action="${id}"]:not([data-header-overflowed])`) : null;
        (shown(lastHeaderActionRef.current) ?? row?.querySelector(':scope > [data-header-action]:not([data-header-overflowed])') ?? inputRef.current)?.focus();
    };
    // gyro:close (the host closes Gyro, e.g. Star Command on sign-out): as Escape or the close button would, where the layout and
    // the window allow it (not a pane, not the desktop window). Focus goes back to the launcher only when it was in Gyro (the
    // panel, or the page's body after a press in it); a host control that has it keeps it.
    const closeFromHostRef = useRef(() => undefined);
    closeFromHostRef.current = () => {
        if (!openRef.current || !escapeCloses || !layoutChrome.escapeCloses)
            return;
        const active = document.activeElement;
        const focusInGyro = Boolean(panelRef.current?.contains(active) || launcherRef.current?.contains(active))
            || (isPageBody(active) && lastMoveInsidePanelRef.current);
        keepFocusOnCloseRef.current = !focusInGyro;
        closeAssistant();
    };
    // Where the person's last pointer press or focus move was while the floating panel is open; it opens with the composer focused.
    useEffect(() => {
        if (!floatingPanel || !open)
            return undefined;
        lastMoveInsidePanelRef.current = true;
        const note = (event) => {
            const target = event.target instanceof Node ? event.target : null;
            lastMoveInsidePanelRef.current = Boolean(target && (panelRef.current?.contains(target) || launcherRef.current?.contains(target)));
        };
        document.addEventListener('pointerdown', note, true);
        document.addEventListener('focusin', note, true);
        return () => {
            document.removeEventListener('pointerdown', note, true);
            document.removeEventListener('focusin', note, true);
        };
    }, [floatingPanel, open]);
    // Focus trap + Escape + Ctrl/Cmd+K (command palette) within the dialog.
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (chatMenu)
                return;
            // M-10: a key from a field of the page outside Gyro is that field's, so none of the keys below act on it, Escape while
            // Gyro listens included. One exception: Escape on such a field hidden under the open floating panel still reveals it.
            const fromHostField = keyFromHostField(e, [panelRef.current, launcherRef.current]);
            if (e.key === 'Escape' && recognitionRef.current?.isActive && !fromHostField) {
                e.preventDefault();
                recognitionRef.current.cancel();
                requestAnimationFrame(() => inputRef.current?.focus());
                return;
            }
            // The floating panel is one part of the host's page (aria-modal false): its keys act only while it has the keyboard
            // (floatingPanelHasKeys), so the host keeps its own Escape and Ctrl/Cmd+K. Escape still closes it when focus is on a host
            // control hidden under it, so the person can see where they are (WCAG 2.4.11); focus stays on that control.
            if (floatingPanel && !floatingPanelHasKeys(panelRef.current, launcherRef.current, lastMoveInsidePanelRef.current)) {
                if (e.key === 'Escape' && !e.defaultPrevented && escapeCloses && layoutChrome.escapeCloses && focusHiddenUnderPanel(panelRef.current, launcherRef.current)) {
                    e.preventDefault();
                    keepFocusOnCloseRef.current = true;
                    closeAssistant();
                }
                return;
            }
            // A pane, or the desktop app: the panel's keys act from anywhere on the page but a field of the page outside Gyro.
            if (fromHostField)
                return;
            if (desktopWorkspace && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key === ',') {
                e.preventDefault();
                if (settingsSection)
                    closeSettings();
                else
                    openSettings();
                return;
            }
            if (settingsSection) {
                // Recover if a click on non-focusable content moved focus outside the modal.
                if (e.key === 'Escape') {
                    e.preventDefault();
                    closeSettings();
                }
                else if (e.key === 'Tab') {
                    const dialog = panelRef.current?.querySelector('.gyro-settings-dialog');
                    if (dialog && !dialog.contains(document.activeElement)) {
                        e.preventDefault();
                        dialog.querySelector('input, button:not([disabled])')?.focus();
                    }
                }
                return;
            }
            if (outfitsOpen) {
                // Gyro on the web's Outfits window is modal, as Settings is: Escape closes it and Tab never leaves it.
                if (e.key === 'Escape') {
                    e.preventDefault();
                    closeOutfits();
                }
                else if (e.key === 'Tab') {
                    const dialog = panelRef.current?.querySelector('.gyro-web-outfits');
                    if (dialog && !dialog.contains(document.activeElement)) {
                        e.preventDefault();
                        dialog.querySelector('button:not([disabled])')?.focus();
                    }
                }
                return;
            }
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (taskDrawerOpen)
                    setTaskPanelOpen(false);
                if (desktopDrawerOpen)
                    setSidebarOpen(false);
                setPaletteOpen((p) => !p);
                return;
            }
            if (e.key === 'Escape') {
                if (paletteOpen) {
                    e.preventDefault();
                    setPaletteOpen(false);
                    setPaletteQuery('');
                    setPaletteIndex(0);
                }
                else if (sidePanelFullScreen) {
                    // Full screen fills Gyro's window with the side panel; Esc returns to the chat beside it.
                    e.preventDefault();
                    sidePanel.setFullScreen(false);
                }
                else if (taskPanelOpen) {
                    e.preventDefault();
                    closeTaskPanel();
                }
                else if (desktopDrawerOpen) {
                    e.preventDefault();
                    setSidebarOpen(false);
                }
                else if (showPets && !props.petContext) {
                    e.preventDefault();
                    setShowPets(false);
                }
                else if (showNotifications) {
                    e.preventDefault();
                    setShowNotifications(false);
                }
                else if (showSessions) {
                    e.preventDefault();
                    setShowSessions(false);
                }
                else if (workspace && (showMemory || showCapabilities)) {
                    e.preventDefault();
                    setShowMemory(false);
                    setShowCapabilities(false);
                }
                // Esc closes only where both the window style (not the desktop window, contract §7) and the layout (not a pane)
                // let it; the overlay closes as before.
                else if (escapeCloses && layoutChrome.escapeCloses) {
                    e.preventDefault();
                    closeAssistant();
                }
                return;
            }
            // A pane (layoutChrome.trapsFocus false) cannot be closed, so its panel never wraps Tab: focus moves on to the host. Nor
            // does a floating panel on a host page, which is non-modal (aria-modal false): Tab moves on to the page, except from its
            // open drawer or command palette.
            const panelTraps = layoutChrome.trapsFocus && (!floatingPanel || paletteOpen);
            const focusRoot = taskDrawerOpen && !paletteOpen ? taskPanelRef.current : desktopDrawerOpen && !paletteOpen ? sidebarRef.current : panelTraps ? panelRef.current : null;
            if (e.key === 'Tab' && focusRoot) {
                const focusable = Array.from(focusRoot.querySelectorAll('button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'))
                    .filter((element) => element.tabIndex >= 0 && !element.hasAttribute('disabled') && !element.closest('[inert]') && element.getClientRects().length > 0);
                if (!focusable.length)
                    return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if ((taskDrawerOpen || desktopDrawerOpen) && !focusRoot.contains(document.activeElement)) {
                    e.preventDefault();
                    (e.shiftKey ? last : first).focus();
                }
                else if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, chatMenu, showPets, props.petContext, showNotifications, showSessions, showMemory, showCapabilities, paletteOpen, outfitsOpen, closeOutfits, desktopWorkspace, desktopDrawerOpen, settingsSection, closeSettings, openSettings, taskDrawerOpen, taskPanelOpen, floatingPanel, sidePanelFullScreen, sidePanel.setFullScreen, closeTaskPanel, closeAssistant, escapeCloses, layoutChrome.escapeCloses, layoutChrome.trapsFocus]);
    useEffect(() => {
        const onKey = (e) => {
            // Ctrl/Cmd+Shift+V (or +J): open Gyro and start talking, in the desktop app and the Office pane. A web page offers no
            // voice input (voiceInputFor, 0.16.3), so there both keys stay the page's. These page-wide
            // shortcuts (and Ctrl/Cmd+J below) act wherever focus is on the page, unless the host turned them off (pageShortcuts
            // false: its own pages use these keys, Ctrl+Shift+V being paste as plain text), and never in a field of the page
            // outside Gyro (M-10): an input that takes text, a textarea, a select or an editable region keeps its own keys there,
            // and so does whatever has the focus inside a closed shadow root, which Gyro cannot see into. Gyro's composer and
            // fields keep them, also when Gyro renders inside a shadow root, open or closed (keyFromHostField).
            if (!pageShortcutsRef.current)
                return;
            const key = e.key.toLowerCase();
            const voice = voiceInputRef.current !== 'off' && (e.ctrlKey || e.metaKey) && e.shiftKey && (key === 'v' || key === 'j');
            const toggle = (e.ctrlKey || e.metaKey) && !e.shiftKey && key === 'j';
            if (!voice && !toggle)
                return;
            if (keyFromHostField(e, [panelRef.current, launcherRef.current]))
                return;
            e.preventDefault();
            if (voice)
                triggerVoiceRef.current();
            else
                setOpen((o) => !o);
        };
        const onOpen = () => setOpen(true);
        const onClose = () => closeFromHostRef.current(); // gyro:close, the mirror of gyro:open (0.15.0)
        // gyro:listen and gyro:voice-toggle on a web page (voiceInputFor 'off', 0.16.3) start nothing and open nothing. They only
        // say that voice input is not available here: on the console for the host's developers and, while the panel is open,
        // in it for the person.
        const voiceInputOff = (hook) => {
            if (voiceInputRef.current !== 'off')
                return false;
            console.warn(`[gyro] ${hook}: voice input is not available here, so nothing was started. On a web page Gyro takes typed messages only.`);
            if (openRef.current)
                setVoiceError(VOICE_INPUT_OFF);
            return true;
        };
        // dispatch window 'gyro:listen' (e.g. from an OS hotkey daemon)
        const onListen = () => { if (!voiceInputOff('gyro:listen'))
            triggerVoiceRef.current(); };
        // gyro:situation — validated detail, dispatched through the ref so the CURRENT send/busy guard is
        // used, not the stale first-render closure this deps-[] effect captured (L-28).
        const onSituation = (e) => {
            const prompt = parseSituationPrompt(e.detail);
            if (!prompt)
                return;
            openSituationRef.current(prompt);
        };
        // ---- Host integration hooks (see docs/EMBED.md → "Host integration hooks") ----
        // A host page or desktop shell can drive the widget programmatically, two equivalent ways:
        //   window.postMessage({ type: 'gyro:voice-toggle' })   → open (if collapsed) + toggle the mic,
        //                                                         exactly like clicking the mic button
        //   window.postMessage({ type: 'gyro:open' })           → just open the widget
        //   window.postMessage({ type: 'gyro:close' })          → close it, as Escape or its close button would
        //   new CustomEvent('gyro:voice-toggle') on window      → same as the message form
        // Messages can arrive from ANY window and ANY origin. Two gates (L-29): the EVENT must come from
        // this very window (`e.source === window`) and, if the host configured `hostOrigins`, from an
        // allowlisted origin — anything else is dropped silently, before the payload is even looked at.
        // Then only the `type` string of the payload is inspected — no other payload data is trusted or
        // read (parseHostHookMessage). The hook gate drops voice re-triggers while a prior toggle is still
        // settling (panel opening / mic warm-up), so a double-fire can't leave the session half-toggled.
        const voiceGate = createHookGate();
        const onVoiceToggle = () => {
            if (voiceInputOff('gyro:voice-toggle'))
                return;
            if (voiceGate.tryEnter())
                toggleVoiceHookRef.current();
        };
        const onSignOut = () => signOutRef.current(); // host sign-out hook (M-09) — wipes this identity's caches
        // gyro:support-card — the host's whole card list, validated field by field (supportCards.ts); the panel opens
        // when a request this person has not seen yet arrives (the desktop overlay also shows its window natively).
        const acceptSupportCards = (detail) => {
            const cards = parseSupportCardsDetail(detail);
            if (!cards)
                return;
            const { openIds, fresh } = acceptHostSupportCards(openSupportIdsRef.current, cards);
            openSupportIdsRef.current = openIds;
            setSupportCards(cards);
            if (fresh)
                setOpen(true);
        };
        const onSupportCards = (e) => acceptSupportCards(e.detail);
        // gyro:compose — the host puts one line in the composer WITHOUT sending it (the "Create my PPP" reminder's prompt).
        // Validated field by field (hostHooks.ts parseComposeText); a draft the person already started is never replaced.
        // Not gated on the starter (pppOn): the desktop posts the reminder's prompt as it shows a window that may not have been
        // on screen yet, and /v1/status and /v1/ppp/me are read only once it is, so a gate would drop the prompt it exists for.
        const acceptCompose = (data) => {
            const text = parseComposeText(data);
            if (!text)
                return;
            setOpen(true);
            setInput((current) => (current.trim() ? current : text));
            requestAnimationFrame(() => { const field = inputRef.current; if (field) {
                field.focus();
                field.setSelectionRange(field.value.length, field.value.length);
            } });
        };
        const onCompose = (e) => acceptCompose(e.detail);
        const onHookMessage = (e) => {
            if (!isTrustedHostHookEvent(e, { window, allowedOrigins: hostOriginsRef.current }))
                return;
            // Bug-report hooks from the desktop shell (same-window, like every hook): a screen capture to attach, the
            // record of the window that was in front before the overlay opened, and a capture that failed.
            const data = e.data && typeof e.data === 'object' ? e.data : null;
            if (data?.type === 'gyro:attach') {
                if (captureTimerRef.current)
                    clearTimeout(captureTimerRef.current);
                setCapturing(false);
                const images = parseAttachImages(data.images);
                if (!images) {
                    setAttachError('Screenshot not attached: the image from the desktop agent was not usable.');
                    return;
                }
                setOpen(true);
                setReportMode(true);
                setAttachments((prev) => [...prev, ...images].slice(0, ATTACHMENT_MAX_COUNT));
                return;
            }
            if (data?.type === 'gyro:report-context') {
                hostReportContextRef.current = parseReportContext(data.context);
                return;
            }
            if (data?.type === 'gyro:capture-failed') {
                if (captureTimerRef.current)
                    clearTimeout(captureTimerRef.current);
                setCapturing(false);
                setAttachError(`Screenshot not taken: ${typeof data.reason === 'string' && data.reason ? data.reason.slice(0, 200) : 'the desktop agent could not capture the screen'}.`);
                return;
            }
            const action = parseHostHookMessage(e.data);
            // The message form goes on as the window event, so one path serves both: an open StarScribe page that has the focus takes
            // the voice shortcut for its own dictation (it listens for the event alone, and stops it), and the chat has it otherwise.
            if (action === 'voice-toggle')
                window.dispatchEvent(new CustomEvent('gyro:voice-toggle', { cancelable: true }));
            else if (action === 'open')
                setOpen(true);
            else if (action === 'close')
                onClose();
            else if (action === 'sign-out')
                onSignOut();
            else if (action === 'support-card')
                acceptSupportCards(e.data);
            else if (action === 'compose')
                acceptCompose(e.data);
        };
        document.addEventListener('keydown', onKey);
        window.addEventListener('gyro:open', onOpen);
        window.addEventListener('gyro:close', onClose);
        window.addEventListener('gyro:listen', onListen);
        window.addEventListener('gyro:situation', onSituation);
        window.addEventListener('gyro:voice-toggle', onVoiceToggle);
        window.addEventListener('gyro:sign-out', onSignOut);
        window.addEventListener('gyro:support-card', onSupportCards);
        window.addEventListener('gyro:compose', onCompose);
        window.addEventListener('message', onHookMessage);
        // A host that already holds cards (the desktop overlay posts its list as soon as the page loads, which can land
        // before this effect registered the listener above) answers this with a fresh 'gyro:support-card'.
        try {
            window.dispatchEvent(new CustomEvent('gyro:support-card-request'));
        }
        catch {
            /* ignore */
        }
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('gyro:open', onOpen);
            window.removeEventListener('gyro:close', onClose);
            window.removeEventListener('gyro:listen', onListen);
            window.removeEventListener('gyro:situation', onSituation);
            window.removeEventListener('gyro:voice-toggle', onVoiceToggle);
            window.removeEventListener('gyro:sign-out', onSignOut);
            window.removeEventListener('gyro:support-card', onSupportCards);
            window.removeEventListener('gyro:compose', onCompose);
            window.removeEventListener('message', onHookMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (loadingRef.current) {
            loadingRef.current = false;
            return;
        }
        // Never autosave over a conversation whose saved transcript has not been read (loading, or the load failed).
        if (!transcriptLoadRef.current.canWrite(sessionIdRef.current))
            return;
        const real = messages.filter((m) => !m.pending && !m.streaming && !m.note);
        if (real.length <= 1)
            return;
        setForkNotice(null);
        const t = setTimeout(() => {
            if (!transcriptLoadRef.current.canWrite(sessionIdRef.current))
                return;
            const slim = savedTurns(real);
            const now = Date.now();
            let id = sessionIdRef.current;
            if (!id) {
                id = `s-${now}-${Math.floor(now % 100000)}`;
                sessionIdRef.current = id;
            }
            const { title, createdAt } = conversationTitlesRef.current.describe(id, deriveTitle(real), now);
            conversationTitlesRef.current.remember({ id, title, createdAt });
            // Save only what changed: a re-render that leaves the saved transcript as it was (an action card the
            // inbox refreshed, a re-minted binding token) must not rewrite the cache and PUT the conversation again.
            // It counts as saved only once gyro-svc answered 2xx (HistorySaveTracker), so a refused save is retried. The
            // tracker keeps one record per conversation: a chat reopened from the desktop's own copy is compared with its
            // own last save, not with whichever chat was saved last.
            const fingerprint = transcriptFingerprint(id, title, slim);
            const tracker = historySaveRef.current;
            if (!tracker.needsSave(id, fingerprint))
                return;
            setSessions((prev) => {
                let next = [...prev];
                if (!next.some(session => session.id === id))
                    next.unshift({ id: id, title, messages: slim, createdAt, updatedAt: now, usage: conversationUsageRef.current.saved(id) });
                else
                    next = next.map((s) => (s.id === id ? { ...s, messages: slim, title, updatedAt: now, remote: false, turnCount: undefined, usage: conversationUsageRef.current.saved(id) ?? s.usage } : s));
                persistSessions(next.filter((s) => !s.remote));
                return next;
            });
            // Mirror to the server so the chat is available cross-device + searchable (a forked chat keeps where it came from).
            const forkedFrom = sessionsRef.current.find(session => session.id === id)?.forkedFrom;
            void saveChatHistory({ id: id, title, turns: slim, createdAt, updatedAt: now, ...(forkedFrom ? { forkedFrom } : {}) });
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, userKey]);
    // Each bug-report card shows The Lab's own preview (filed as, the person's quota, who sees the screenshot) before
    // anything is pressed, so a limit that is already hit is visible up front (§1.5). Fetched once per card.
    useEffect(() => {
        for (const m of messages) {
            for (const a of m.actions ?? []) {
                if (a.app !== 'the-lab' || a.tool !== LAB_FILE_TOOL || resolved[a.id] || m.undoable)
                    continue;
                if (labPreviewRequestedRef.current.has(a.id))
                    continue;
                labPreviewRequestedRef.current.add(a.id);
                void client.previewAction(a.id, a.token)
                    .then((res) => setLabPreviews((current) => ({ ...current, [a.id]: res.supported && !res.error ? parseLabPreview(res.preview) : { error: res.error ?? 'no preview' } })))
                    .catch((e) => setLabPreviews((current) => ({ ...current, [a.id]: { error: e instanceof Error ? e.message : 'preview failed' } })));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, client]);
    const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant' && !m.streaming && !m.pending && !m.note)?.content ?? '';
    // What "Save as skill" starts from when nothing is typed: the last answer, unless it came from a chat that holds pay data.
    const lastSkillSeed = [...messages].reverse().find((m) => m.role === 'assistant' && !m.streaming && !m.pending && !m.note)?.classified ? '' : lastAnswer;
    // What a screen reader hears when an answer lands: its words, not its Markdown (no ** or list bullets), up to 400 characters.
    const liveText = useMemo(() => clipAtWord(markdownPlainText(lastAnswer), 400), [lastAnswer]);
    // ---- Command palette (Ctrl/Cmd+K): fuzzy-filterable actions + suggestion chips + follow-ups. ----
    const closePalette = () => {
        setPaletteOpen(false);
        setPaletteQuery('');
        setPaletteIndex(0);
    };
    const showChat = () => {
        setShowPets(false);
        setShowNotifications(false);
        setShowSessions(false);
        setShowCapabilities(false);
        setShowMemory(false);
        starscribe.closeList();
    };
    const closeMobileSidebar = () => {
        if (floatingWorkspace ? compactWorkspace : window.innerWidth <= 760)
            setSidebarOpen(false);
    };
    const openPets = (route) => {
        if (!petsEnabled)
            return;
        setShowNotifications(false);
        setShowSessions(false);
        setShowCapabilities(false);
        setShowMemory(false);
        setTaskPanelOpen(false);
        setPetRoute(route);
        setShowPets(true);
        closeMobileSidebar();
    };
    const searchConversations = () => {
        setShowNotifications(false);
        setShowCapabilities(false);
        setShowMemory(false);
        setShowSessions(true);
        closeMobileSidebar();
        setTimeout(() => historySearchRef.current?.focus(), 30);
    };
    // The device notification history page (local notices and requested checks). The nav's Notifications entry and the
    // command palette open the notification center instead while it is on (openNotifications below); the center's
    // "Device notification history" button is this page's way in then.
    const openDeviceHistory = () => {
        setShowNotifications(true);
        setShowSessions(false);
        setShowCapabilities(false);
        setShowMemory(false);
        setTaskPanelOpen(false);
        closeMobileSidebar();
    };
    // Notifications, as the person understands them: the center the mascot's bell opens (Star Command items, questions),
    // when the desktop's notification capability is on; the device history page otherwise (owner, 2026-10-26: the bell
    // counted items while the Notifications page listed none, on every PC).
    const openNotifications = () => {
        if (attention.openCenter()) {
            closeMobileSidebar();
            return;
        }
        openDeviceHistory();
    };
    const openNotification = (entry) => {
        notificationCenter.markRead(entry.id, true);
        if (entry.actionId) {
            const action = inboxActionsRef.current.get(entry.actionId);
            showChat();
            const existing = messages.some(message => message.actions?.some(item => item.id === entry.actionId));
            if (!existing)
                setMessages(previous => [...previous, action
                        ? { role: 'assistant', content: 'Review the action below.', actions: [action] }
                        : { role: 'assistant', content: 'This action is no longer in your current inbox. Its notification is kept for reference.' }]);
            requestAnimationFrame(() => {
                const target = Array.from(panelRef.current?.querySelectorAll('[data-notification-action]') ?? []).find(element => element.dataset.notificationAction === entry.actionId);
                target?.scrollIntoView({ block: 'center', behavior: 'instant' });
                target?.focus();
            });
        }
        else if (entry.conversationId) {
            if (entry.conversationId === sessionIdRef.current) {
                showChat();
                return;
            }
            if (busy)
                return;
            const session = sessions.find(item => item.id === entry.conversationId);
            if (session) {
                showChat();
                void loadSession(session);
            }
            else {
                searchConversations();
                setHistorySearch('');
            }
        }
    };
    // StarScribe's list or its page in place of the chat, and its narrow tabs over the chat, when no other view covers the chat.
    const chatCovered = showPets || showNotifications || showMemory || showSessions || showCapabilities;
    // The screen on view, for the usage record (usage/widgetScreens.ts): reported once per change; the one left is credited
    // with its time on screen.
    const usageScreen = widgetScreenFor({
        open, desktopWorkspace, settingsSection, showPets, petRoute, showNotifications, showMemory, showSessions, showCapabilities,
        sidePanelTab: sidePanelShown ? sidePanel.activeTab?.id ?? null : null, composerWorkspace,
    });
    const usageScreenKey = widgetScreenKey(usageScreen);
    // A host with screens of its own (Gyro Admin's pages) gets its screen back when the panel closes; a page without one
    // (a web host's launcher) is simply left.
    const usageHostScreenRef = useRef(null);
    const usageOwnScreenRef = useRef(null);
    useEffect(() => {
        const usage = usageCapture();
        if (usageScreen) {
            if (usageOwnScreenRef.current === null)
                usageHostScreenRef.current = usage.snapshot().currentScreen;
            usageOwnScreenRef.current = usageScreen.name;
            usage.screen(usageScreen.name, usageScreen.params);
            return;
        }
        const own = usageOwnScreenRef.current;
        usageOwnScreenRef.current = null;
        if (own === null || usage.snapshot().currentScreen !== own)
            return; // the host moved on meanwhile
        if (usageHostScreenRef.current)
            usage.screen(usageHostScreenRef.current);
        else
            usage.leaveScreen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usageScreenKey]);
    const starscribeMain = chatCovered ? null : starscribe.main;
    const starscribeListShown = Boolean(starscribeMain) && starscribe.listOpen;
    const desktopTitle = showPets ? PETS_TITLE : showNotifications ? 'Notifications' : showMemory ? 'Memory' : showSessions ? 'Conversations' : showCapabilities ? 'Capabilities'
        : starscribeListShown ? 'StarScribe' : sessions.find((session) => session.id === sessionIdRef.current)?.title ?? deriveTitle(messages);
    const recentSessions = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 12);
    /** The chat view is showing and the chat has no content yet: the welcome (with its large mascot) is on screen. */
    const welcomeOnScreen = !showPets && !showNotifications && !showMemory && !showSessions && !showCapabilities && messages.every((m) => m.welcome);
    /** Send from the palette: jump back to the chat view; queue (steer) instead if a turn is running. */
    const paletteSend = (text) => {
        showChat();
        if (busy)
            setQueued((q) => [...q, text]);
        else
            void send(text);
    };
    const lastFollowups = [...messages].reverse().find((m) => m.role === 'assistant' && !m.pending && !m.streaming && !m.note)?.followups ?? [];
    const { replyQuestions, ...attention } = useAttentionIntegration({ client, desktopWorkspace, workspace, serviceKey: petServiceKey, userKey, sessionEpoch: petSessionEpoch, petsEnabled: Boolean(petsEnabled), missionSession: petMissionSession, messages, sessions, busy, input, staticAvatar, notificationEntries: notificationCenter.entries, parts, sessionIdRef, taskRunRef, inputRef, attachmentPendingRef, attachmentsRef, conversationsRef, transcriptLoadRef, loadSession, setInput, showChat, openPets, openNotifications: openDeviceHistory, setShowPets, disablePets });
    // The pending question belongs to the latest response. Earlier cards remain transcript history;
    // a user reply immediately removes the dock, including free-form replies sent from the composer.
    const dockedQuestionIndex = workspace && replyQuestions[messages.length - 1]?.length ? messages.length - 1 : -1;
    const dockedQuestionMessage = dockedQuestionIndex >= 0 ? messages[dockedQuestionIndex] : undefined;
    // "Create my PPP" chips (ppp.ts): under the latest reply whose tools drafted, exported or gathered, as suggestion buttons.
    const pppChipsFor = (m, i) => {
        if (!m.ppp || m.role !== 'assistant' || i !== messages.length - 1 || replyQuestions[i])
            return [];
        return pppChipIds({ marks: m.ppp, content: m.content, question: m.question, emailOffered: capabilities.some((t) => t.name === PPP_EMAIL_TOOL), chatGathered: messages.some((x) => x.ppp?.gathered) })
            .filter((id) => id !== 'open_pdf' || m.artifacts?.some((artifact) => artifact.mediaType === 'application/pdf'))
            // While the widget continues the run itself (pppShouldContinue), no "Continue my PPP" chip: it would send the same.
            .filter((id) => id !== 'continue' || !(pppAutoRef.current || (busy && m.ppp?.remaining?.calls === 0)));
    };
    /**
     * What Open PDF falls back to when the reply's 24-hour PDF is gone: the stored report's PDF, read fresh from /v1/ppp/me,
     * when its lastReport is this very file (pppDurablePdfPath). A client without the methods (a host's own stand-in) or a
     * /v1/ppp/me read that fails gives null, so the 404's own sentence stays; a stored file that fails to download says why.
     */
    const pppDurablePdf = async (artifact, options = {}) => {
        if (typeof client.pppMe !== 'function' || typeof client.downloadPppReport !== 'function')
            return null;
        let me;
        try {
            me = parsePppMe(await client.pppMe(options));
        }
        catch {
            return null;
        }
        const path = pppDurablePdfPath(me, artifact.name);
        return path ? client.downloadPppReport(path, options) : null;
    };
    const runPppChip = (id, m, i) => {
        const reply = pppReplyKey(m, i);
        if (id === 'copy_text') {
            const block = pppCopyBlock(m.content);
            if (block) {
                // A page without the clipboard API (an insecure origin, an old webview) gets the same note as a refused write.
                const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
                if (!clipboard || typeof clipboard.writeText !== 'function') {
                    setAttachError('Copy was blocked: select the text in the answer and copy it manually.');
                    return;
                }
                clipboard.writeText(block).then(() => { setPppCopied(reply); setTimeout(() => setPppCopied((current) => (current === reply ? null : current)), 2000); }, () => setAttachError('Copy was blocked: select the text in the answer and copy it manually.'));
                return;
            }
        }
        else if (id === 'open_pdf') {
            setPppPdfOpen((current) => ({ ...current, [reply]: (current[reply] ?? 0) + 1 }));
            return;
        }
        else if (id === 'where_from') {
            // The person edits the number: it goes into the composer instead of being sent, with the number selected. Like
            // gyro:compose (acceptCompose), it fills only an empty composer: a draft the person already started stays as it is.
            const sentence = pppChipSentence('where_from');
            const start = sentence.indexOf('item ') + 5;
            setInput((current) => (current.trim() ? current : sentence));
            requestAnimationFrame(() => {
                const field = inputRef.current;
                if (!field)
                    return;
                field.focus();
                if (field.value === sentence)
                    field.setSelectionRange(start, start + 1);
                else
                    field.setSelectionRange(field.value.length, field.value.length);
            });
            return;
        }
        const sentence = pppChipSentence(id);
        if (sentence)
            void send(sentence);
    };
    // Pulse's quick feedback card for a message that earned one (contract 10.2), until it is sent, dismissed or gone.
    const renderPulseCard = (m) => {
        const mark = m.pulse;
        if (!mark || pulseClosed[mark.key])
            return null;
        const close = () => setPulseClosed((current) => (current[mark.key] ? current : { ...current, [mark.key]: true }));
        return (_jsx(PulseCard, { askId: mark.askId, actionId: mark.actionId, summary: mark.summary, tool: mark.tool, chips: pulseChips[mark.chipKey] ?? defaultPulseChips(mark.chipKey), perDayLeft: mark.perDayLeft, onSend: (feedback) => client.pulseFeedback(feedback), onDismiss: () => { pulseStore.dismissed(mark.key); close(); }, onDone: close }, mark.key));
    };
    const renderPppChips = (m, i) => {
        const ids = pppChipsFor(m, i);
        return ids.length > 0 ? (_jsx("div", { className: "gyro-followups", role: "group", "aria-label": "Your PPP", "data-ppp-chips": "true", children: ids.map((id) => (_jsx("button", { type: "button", className: "gyro-chip small", onClick: () => runPppChip(id, m, i), disabled: busy && id !== 'copy_text' && id !== 'open_pdf', children: id === 'copy_text' && pppCopied === pppReplyKey(m, i) ? 'Copied ✓' : PPP_CHIP_LABELS[id] }, id))) })) : null;
    };
    const paletteEntries = [
        ...(desktopWorkspace ? [{ id: 'cmd-notification-center', label: 'Open notification center', section: 'Commands', keywords: ['inbox', 'alerts', 'requests', 'updates'], run: openNotifications }] : []),
        ...(desktopWorkspace ? [
            { id: 'cmd-settings', label: 'Open settings', section: 'Commands', keywords: ['appearance', 'account', 'preferences', 'theme'], run: () => openSettings() },
            { id: 'cmd-guide', label: 'Open user guide', section: 'Commands', keywords: ['help', 'instructions', 'learn', 'manual'], run: openGuide },
        ] : []),
        { id: 'cmd-new-chat', label: 'New chat', section: 'Commands', keywords: ['clear', 'reset', 'start over'], run: () => { showChat(); newSession(); } },
        { id: 'cmd-fork-chat', label: 'Fork chat', section: 'Commands', keywords: ['fork', 'branch', 'copy chat', 'duplicate'], run: () => { showChat(); void forkCurrentChat(); } },
        ...(labAvailable
            ? [{ id: 'cmd-report-bug', label: 'Report a bug', section: 'Commands', hint: 'to The Lab', keywords: ['bug', 'broken', 'issue', 'defect', 'qa', 'finding', 'lab', 'problem'], run: startBugReport }]
            : []),
        ...(pppOn
            ? [{ id: 'cmd-create-ppp', label: PPP_STARTER, section: 'Commands', keywords: ['ppp', 'progress', 'planned', 'pinch points', 'weekly', 'biweekly', 'report', 'status'], run: () => paletteSend(PPP_STARTER) }]
            : []),
        {
            id: 'cmd-search-chats',
            label: 'Search conversations',
            section: 'Commands',
            keywords: ['history', 'chats', 'sessions', 'find'],
            run: () => {
                setShowCapabilities(false);
                setShowMemory(false);
                setShowSessions(true);
                setTimeout(() => historySearchRef.current?.focus(), 60);
            },
        },
        { id: 'cmd-toggle-voice', label: 'Toggle voice mode', section: 'Commands', hint: voiceOn ? 'now: voice' : 'now: detailed', keywords: ['voice', 'detailed', 'spoken', 'audio', 'mode'], run: () => switchMode(voiceOn ? 'detailed' : 'voice') },
        // Dictation, which a web page does not offer (voiceInputFor).
        ...(voiceInput !== 'off'
            ? [{ id: 'cmd-talk', label: 'Talk to Gyro', section: 'Commands', keywords: ['mic', 'microphone', 'speak', 'listen', 'dictate'], run: () => { showChat(); startListening(); } }]
            : []),
        { id: 'cmd-memory', label: 'Open memory', section: 'Commands', keywords: ['remember', 'profile', 'brain'], run: () => { if (desktopWorkspace)
                openSettings('memory');
            else if (!showMemory)
                void toggleMemory(); } },
        { id: 'cmd-capabilities', label: 'Open capabilities', section: 'Commands', keywords: ['tools', 'actions', 'discover', 'help'], run: () => { setShowSessions(false); setShowMemory(false); setShowCapabilities(true); } },
        // A pane has no mode toggle (layoutChrome): it fills the host's pane, so there is nothing to expand or collapse; the
        // desktop window hands its layout and window controls to the title bar (windowChrome).
        ...(layoutChrome.modeToggle && windowChrome.layoutToggle && !(floatingWorkspace && floatingFullScreen)
            ? [{ id: 'cmd-expand', label: desktopWorkspace ? expanded ? 'Return to vertical view' : 'Expand to comfy view' : expanded ? 'Collapse widget' : 'Expand widget', section: 'Commands', keywords: ['resize', 'bigger', 'smaller', 'enlarge', 'shrink', 'vertical', 'comfy'], run: toggleExpanded }]
            : []),
        ...(layoutChrome.windowControls && windowChrome.windowToggle ? [{ id: 'cmd-window-mode', label: windowSwitch.label, section: 'Commands', keywords: ['window', 'desktop', 'overlay', 'taskbar', 'title bar', 'floating'], run: requestWindowSwitch }] : []),
        // Palette entries run from a click / Enter — a user gesture — so this is a valid place to ask (L-27).
        ...(notificationsEnabled && !props.nativeNotifications && notifPerm === 'default'
            ? [{ id: 'cmd-notifications', label: 'Enable desktop notifications', section: 'Commands', hint: 'when an answer finishes while you are away', keywords: ['notify', 'alert', 'toast', 'bell', 'push'], run: enableNotifications }]
            : []),
        ...suggestions.map((s, i) => ({ id: `sug-${i}`, label: s, section: 'Suggestions', run: () => paletteSend(s) })),
        ...lastFollowups.map((f, i) => ({ id: `fu-${i}`, label: f, section: 'Follow-ups', run: () => paletteSend(f) })),
    ];
    const rankedPalette = paletteOpen ? rankPalette(paletteEntries, paletteQuery) : [];
    const activeEntry = rankedPalette[Math.min(paletteIndex, Math.max(0, rankedPalette.length - 1))];
    const runPaletteEntry = (entry) => {
        closePalette();
        track('gyro_palette', { app: appKey, kind: 'run', tool: entry.id.startsWith('cmd-') ? entry.id : entry.section });
        entry.run();
    };
    // ---- Slash commands (slashCommands.ts): "/" at the start of the composer opens a menu of commands above the message box
    // (owner, 2026-10-31). Every command but /goal and /help is a spelling of a palette command above: the same handler. ----
    /** A local note in the chat (a slash command's answer): shown like a quiet reply, never sent as history, never saved. */
    const addNote = (content) => {
        showChat();
        setMessages((previous) => [...previous, { role: 'assistant', content, note: true }]);
    };
    /** /goal: the person's goals, kept as "Goal:" lines of their memory note by POST /v1/memory/goals (memory.ts "Goals"). */
    const runGoal = async (args) => {
        const request = parseGoalArgs(args);
        if (request.op === 'help') {
            addNote(GOAL_USAGE);
            return;
        }
        if (typeof client.memoryGoals !== 'function') {
            addNote('Goals are not available in this app.');
            return;
        }
        try {
            const { status, body } = request.op === 'add' ? await client.memoryGoals('add', { text: request.text })
                : request.op === 'remove' ? await client.memoryGoals('remove', { n: request.n })
                    : await client.memoryGoals('list');
            addNote(goalResultNote(request, status, body));
        }
        catch {
            addNote('Gyro could not be reached just now; nothing was changed. Try again in a minute.');
        }
    };
    const slashCommands = slashCommandsFor(paletteEntries.filter((entry) => entry.section === 'Commands'), [{ id: 'slash-goal', name: 'goal', aliases: ['goals'], description: 'Goals you want Gyro to keep in mind', usage: GOAL_USAGE_SHORT, run: (args) => { void runGoal(args); } }], [{ id: 'slash-help', name: 'help', aliases: ['commands'], description: 'List the slash commands', run: () => addNote(slashHelpNote(slashCommands)) }]);
    const slash = slashInput(input);
    const slashOpen = Boolean(slash) && !slashDismissed && !paletteOpen;
    const rankedSlash = slashOpen && slash ? rankSlashCommands(slashCommands, slash.word) : [];
    const activeSlash = rankedSlash[Math.min(slashIndex, Math.max(0, rankedSlash.length - 1))];
    /** Run a slash command from the composer (Enter, Send or a click on a row): the composer is cleared and the model never sees it. */
    const runSlash = (command, args) => {
        setInput('');
        setSlashDismissed(false);
        setSlashIndex(0);
        track('gyro_slash', { app: appKey, kind: 'run', tool: command.id });
        command.run(args);
    };
    /** The arguments a row gets when it is picked: what follows the word only when the word IS that command (`/goal set x`), else nothing. */
    const slashArgsFor = (command) => (slash && (slash.word === command.name || command.aliases.includes(slash.word)) ? slash.args : '');
    /**
     * What Enter or Send runs: the command the text names exactly, with what follows it; else, while the menu is open, its
     * highlighted row with no arguments ("/go" + Enter runs /goal, as the palette runs its highlighted row); else null, and
     * the text is an ordinary message ("/etc/hosts", or a word no command matches).
     */
    const slashToRun = () => {
        if (!slash)
            return null;
        const exact = matchSlashCommand(slashCommands, slash.word);
        if (exact)
            return { command: exact, args: slash.args };
        return slashOpen && activeSlash ? { command: activeSlash, args: '' } : null;
    };
    const onPaletteKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setPaletteIndex((i) => (rankedPalette.length ? (Math.min(i, rankedPalette.length - 1) + 1) % rankedPalette.length : 0));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setPaletteIndex((i) => (rankedPalette.length ? (Math.min(i, rankedPalette.length - 1) - 1 + rankedPalette.length) % rankedPalette.length : 0));
        }
        else if (e.key === 'Home') {
            e.preventDefault();
            setPaletteIndex(0);
        }
        else if (e.key === 'End') {
            e.preventDefault();
            setPaletteIndex(Math.max(0, rankedPalette.length - 1));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeEntry)
                runPaletteEntry(activeEntry);
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // the panel's document-level handler must not also close the widget
            closePalette();
        }
    };
    // Once the host reports a card claimed/resolved, this person's own click result has done its job.
    useEffect(() => {
        setSupportOutcomes((current) => retainSupportOutcomes(current, supportCards));
    }, [supportCards]);
    /** Claim an IT-support request (desktop: forwarded natively under device custody; the server decides who won). */
    const claimSupportRequest = async (card) => {
        if (supportClaiming)
            return;
        setSupportClaiming(card.requestId);
        try {
            const { status, body } = await client.claimSupportRequest(card.requestId);
            const outcome = claimOutcomeFromResponse(status, body);
            setSupportOutcomes((o) => ({ ...o, [card.requestId]: outcome }));
            track('gyro_support_claim', { app: appKey, result: outcome.result });
        }
        catch {
            setSupportOutcomes((o) => ({ ...o, [card.requestId]: { result: 'error', message: 'Could not reach Gyro. Try again.' } }));
        }
        finally {
            setSupportClaiming(null);
        }
    };
    /** Close a card that needs nothing more from this person; the host is told so its list agrees. */
    const dismissSupportCard = (card) => {
        setSupportCards((cards) => cards.filter((c) => c.requestId !== card.requestId));
        try {
            window.dispatchEvent(new CustomEvent('gyro:support-card-dismiss', { detail: { requestId: card.requestId } }));
        }
        catch {
            /* ignore */
        }
    };
    useEffect(() => {
        if (paletteOpen) {
            track('gyro_palette', { app: appKey, kind: 'open' });
            setTimeout(() => paletteInputRef.current?.focus(), 30);
        }
        else if (open && !webTouchScreen()) {
            setTimeout(() => inputRef.current?.focus(), 30);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paletteOpen]);
    /** The composer's two buttons (ComposerModelControls): the model button only when there is a choice, the thinking button only when the model has levels. */
    const modelControls = (held) => _jsxs(_Fragment, { children: [menuEntries.length > 1 && _jsx(ModelMenuButton, { entries: menuEntries, currentId: currentModel?.id, high: modelTier === 'high', onPick: pickModel, disabled: busy || held }), _jsx(ThinkingButton, { levels: levels, value: effort, defaultLevel: defaultThinkingLevel(levels), onChange: level => { setEffort(level === defaultThinkingLevel(levels) ? '' : level); setTierNote(''); }, locked: modelTier === 'high', disabled: busy || held })] });
    // The desktop's copies of generated files (desktopAutoSave.tsx): the chat on screen names the folder its Export outputs and
    // code-block downloads are copied to. Null on every other host, where the components under a chat change nothing.
    const autoSaveChatId = sessionIdRef.current ?? '';
    const autoSaveChat = sessions.find((session) => session.id === autoSaveChatId)?.title ?? deriveTitle(messages);
    // A StarScribe page beside a chat with nothing in it yet exports to Documents\Gyro\StarScribe\<page title> instead: the
    // welcome greeting and a message still on its way are not the chat yet.
    const autoSaveBesideChat = chatHasMessages(messages);
    const autoSaveBinding = useMemo(() => (autoSave ? autoSave.bind(autoSaveChat, autoSaveChatId, { besideChat: autoSaveBesideChat }) : null), [autoSave, autoSaveChat, autoSaveChatId, autoSaveBesideChat]);
    return (_jsx(SidePanelContext.Provider, { value: sidePanelApi, children: _jsxs(AutoSaveContext.Provider, { value: autoSaveBinding, children: [layoutChrome.launcher && _jsxs("button", { ref: launcherRef, className: `gyro-launcher${open ? ' open' : ''}${floatingWorkspace ? ' gyro-floating' : ''}`, "data-theme": floatingWorkspace && open ? resolvedTheme : undefined, onClick: () => setOpen((o) => !o), title: `${props.title ?? 'Gyro'} — AI assistant`, "aria-label": open ? 'Close Gyro assistant' : unseen > 0 ? `Open Gyro assistant — ${unseen} unread answer${unseen > 1 ? 's' : ''}` : 'Open Gyro assistant', "aria-expanded": open, children: [open ? _jsx(X, { size: 22 }) : _jsx("img", { src: launcherAvatar, alt: "", className: `gyro-launcher-img${outfitLook ? ' gyro-look-img' : ''}`, ...lookImg }), !open && unseen > 0 && (_jsx("span", { className: "gyro-unread", "aria-hidden": "true", children: unseen > 9 ? '9+' : unseen }))] }), open && (_jsxs("section", { ref: panelRef, ...(desktopWorkspace ? desktopDrop.handlers : {}), className: `gyro-panel${expanded ? ' expanded' : ''}${workspace ? ' gyro-desktop-workspace' : ''}${layoutChrome.alwaysOpen ? ' gyro-pane' : ''}${floatingWorkspace ? ' gyro-floating' : ''}`, "data-sidebar": workspace ? sidebarOpen ? 'open' : 'closed' : undefined, "data-task-panel": desktopWorkspace ? taskPanelOpen ? 'open' : 'closed' : undefined, "data-theme": workspace ? resolvedTheme : undefined, "data-chat-font": workspace ? preferences.chatFont : undefined, "data-motion": workspace ? preferences.motion : undefined, "data-accent": workspace ? preferences.accent : undefined, "data-surface": workspace && !desktopWorkspace ? appKey : undefined, "data-gyro-look": outfitLook ? 'outfit' : undefined, "data-side-panel": sidePanelShown ? sidePanelFullScreen ? 'full-screen' : 'open' : undefined, role: "dialog", "aria-label": "Gyro assistant", "aria-modal": "false", children: [desktopWorkspace && desktopDrop.state && _jsx(DesktopDropOverlay, { ...desktopDrop.state }), _jsx("span", { className: "gyro-sr-only", "aria-live": "polite", children: liveText }), _jsxs(DesktopBackdrop, { workspace: workspace, disabled: Boolean(settingsSection) || (webPage && outfitsOpen), children: [workspace && (_jsxs(_Fragment, { children: [sidebarOpen && _jsx("button", { className: "gyro-desktop-sidebar-dismiss", onClick: () => setSidebarOpen(false), tabIndex: -1, "aria-label": "Close navigation" }), _jsxs("aside", { ref: sidebarRef, id: `gyro-desktop-sidebar-${appKey}`, className: "gyro-desktop-sidebar", "aria-label": "Gyro navigation", hidden: !sidebarOpen, ...inertProps(taskDrawerOpen || sidePanelFullScreen), children: [_jsxs("div", { className: "gyro-desktop-brand-row", children: [_jsxs("button", { className: "gyro-desktop-brand", onClick: () => { showChat(); closeMobileSidebar(); }, "aria-label": `${props.title ?? 'Gyro'} EXANTE360, return to your conversation`, children: [_jsx("img", { src: staticAvatar, alt: "", ...lookImg }), _jsxs("span", { children: [_jsx("span", { className: "gyro-desktop-brand-name", children: props.title ?? 'Gyro' }), ' ', _jsx("span", { className: "gyro-desktop-brand-org", children: "EXANTE360" })] })] }), _jsx("button", { ref: sidebarCloseRef, className: "gyro-btn ghost gyro-desktop-sidebar-close", onClick: () => setSidebarOpen(false), "aria-label": "Close navigation", children: _jsx(PanelLeft, { size: 17 }) })] }), _jsxs("button", { className: "gyro-desktop-new", onClick: () => { showChat(); newSession(); closeMobileSidebar(); inputRef.current?.focus(); }, children: [_jsx(Plus, { size: 17 }), _jsx("span", { children: "New chat" })] }), _jsxs("nav", { className: "gyro-desktop-nav", "aria-label": "Assistant tools", children: [desktopWorkspace && _jsxs("button", { className: `gyro-desktop-nav-item${showNotifications ? ' active' : ''}`, onClick: openNotifications, "aria-current": showNotifications ? 'page' : undefined, children: [_jsx(Bell, { size: 17 }), _jsx("span", { children: "Notifications" }), notificationCenter.entries.some(entry => !entry.read) && _jsx("span", { className: "gyro-desktop-notification-count", "aria-label": `${notificationCenter.entries.filter(entry => !entry.read).length} unread notifications`, children: notificationCenter.entries.filter(entry => !entry.read).length })] }), _jsxs("button", { className: `gyro-desktop-nav-item${showSessions ? ' active' : ''}`, onClick: searchConversations, "aria-current": showSessions ? 'page' : undefined, children: [_jsx(Search, { size: 17 }), _jsx("span", { children: "Search conversations" })] }), _jsxs("button", { className: `gyro-desktop-nav-item${showCapabilities ? ' active' : ''}`, onClick: () => { setShowNotifications(false); setShowCapabilities(true); setShowSessions(false); setShowMemory(false); closeMobileSidebar(); }, "aria-current": showCapabilities ? 'page' : undefined, children: [_jsx(Compass, { size: 17 }), _jsx("span", { children: "Capabilities" })] }), starscribe.navItem(starscribeListShown, () => { showChat(); starscribe.openList(); closeMobileSidebar(); }), _jsxs("button", { className: `gyro-desktop-nav-item${!desktopWorkspace && showMemory ? ' active' : ''}`, onClick: () => { if (desktopWorkspace) {
                                                                openSettings('memory');
                                                                return;
                                                            } if (!showMemory)
                                                                void toggleMemory(); closeMobileSidebar(); }, "aria-current": !desktopWorkspace && showMemory ? 'page' : undefined, children: [_jsx(Brain, { size: 17 }), _jsx("span", { children: "Memory" })] }), webPage && _jsxs("button", { ref: outfitsButtonRef, className: "gyro-desktop-nav-item", onClick: () => { setOutfitsOpen(true); closeMobileSidebar(); }, "aria-haspopup": "dialog", "aria-expanded": outfitsOpen, children: [_jsx(Shirt, { size: 17 }), _jsx("span", { children: "Outfits" })] }), _jsxs("button", { className: "gyro-desktop-nav-item", onClick: () => { setPaletteOpen(true); closeMobileSidebar(); }, "aria-haspopup": "dialog", "aria-expanded": paletteOpen, children: [_jsx(Command, { size: 17 }), _jsx("span", { children: "Commands" }), _jsx("kbd", { children: "Ctrl K" })] })] }), petsEnabled && companionSessions.ready && _jsx(PetsSidebar, { sessionKey: `${petServiceKey}:${userKey}:${petSessionEpoch}`, missionSession: petMissionSession, client: props.client, checkStatusSessions: petCheckStatusSessions, visible: open && hostVisibility !== 'hidden', active: showPets, selectedId: showPets && petRoute.view === 'pet' ? petRoute.petId : undefined, nativeAvailable: props.nativePets === true, onNavigate: openPets, onDisabled: disablePets }, `pet-sidebar:${userKey}:${petSessionEpoch}`), petsEnabled && !companionSessions.ready && _jsxs("p", { role: companionSessions.failed ? 'alert' : 'status', children: [_jsx("strong", { children: PETS_TITLE }), " \u00B7 ", companionSessions.failed ? 'This feature could not load. Reload Gyro to try again.' : 'Loading…'] }), _jsx("div", { className: "gyro-desktop-section-label", children: "Recent conversations" }), _jsx("div", { className: "gyro-desktop-recent", children: recentSessions.length === 0 ? (_jsx("p", { className: "gyro-desktop-session-empty", children: "Your conversations will appear here. Start with a question below." })) : recentSessions.map((session) => (_jsxs("div", { className: "gyro-desktop-session-row", onContextMenu: event => { event.preventDefault(); openChatMenu(session, event.currentTarget.querySelector('button'), { x: event.clientX, y: event.clientY }); }, children: [_jsxs("button", { className: `gyro-desktop-session${!showPets && !showNotifications && !showSessions && !showCapabilities && !showMemory && !starscribeListShown && session.id === sessionIdRef.current ? ' active' : ''}`, title: session.title, "aria-current": !showPets && !showNotifications && !showSessions && !showCapabilities && !showMemory && !starscribeListShown && session.id === sessionIdRef.current ? 'page' : undefined, onClick: () => { setChatMenu(null); showChat(); void loadSession(session); closeMobileSidebar(); }, onKeyDown: event => chatMenuKey(event, session), children: [conversationsRef.current.get(session.id)?.state === 'running' ? _jsx(LoaderCircle, { size: 15, className: "gyro-file-spinner", role: "status", "aria-label": "Chat is working" }) : _jsx(MessageSquare, { size: 15 }), _jsx("span", { children: session.title }), chatOriginLabel(session.app, appKey) && _jsx("span", { className: "gyro-session-origin", title: "Where this chat was started", children: chatOriginLabel(session.app, appKey) })] }), _jsx("button", { type: "button", className: "gyro-chat-options gyro-btn ghost", "aria-label": `Chat options: ${session.title}`, title: "Chat options", "aria-haspopup": "menu", "aria-expanded": chatMenu?.session.id === session.id, onClick: event => openChatMenu(session, event.currentTarget), children: _jsx(MoreHorizontal, { size: 16 }) })] }, session.id))) }), _jsxs("div", { className: "gyro-desktop-footer", children: [floatingWorkspace && notificationsEnabled && !props.nativeNotifications && notifPerm !== 'unsupported' && (_jsxs("button", { type: "button", className: "gyro-desktop-notify", onClick: enableNotifications, "aria-disabled": notifPerm !== 'default' || undefined, "data-permission": notifPerm, title: notifPerm === 'denied' ? 'Notifications are blocked in this browser’s settings for this site' : notifPerm === 'default' ? 'Notify me when an answer finishes while this tab is hidden or Gyro is closed' : undefined, children: [notifPerm === 'denied' ? _jsx(BellOff, { size: 14, "aria-hidden": "true" }) : _jsx(Bell, { size: 14, "aria-hidden": "true" }), _jsx("span", { children: notifPerm === 'granted' ? 'Notifications are on' : notifPerm === 'denied' ? 'Notifications are blocked' : 'Notify me when Gyro answers' })] })), webPage && (_jsxs("button", { type: "button", className: "gyro-desktop-signout", onClick: () => { if (webSignOut !== 'busy')
                                                                void signOutOfWeb(); }, "aria-disabled": webSignOut === 'busy' || undefined, children: [_jsx(LogOut, { size: 14, "aria-hidden": "true" }), _jsx("span", { children: webSignOut === 'busy' ? 'Signing out…' : 'Sign out' })] })), webPage && webSignOut === 'failed' && _jsx("p", { className: "gyro-desktop-signout-failed", role: "alert", children: "Sign-out did not finish. Check your connection and try again." }), _jsxs("span", { className: "gyro-status", children: [_jsx("span", { className: "gyro-dot" }), unreachable ? 'Reconnecting...' : configured === false ? 'Setup needed' : 'Here to help'] }), desktopWorkspace && _jsx(DesktopAccountMenu, { name: preferences.displayName || props.user?.name || 'Your account', email: props.user?.email, avatar: staticAvatar, onOpenSettings: openSettings, onOpenGuide: openGuide })] })] })] })), _jsxs(AssistantSurface, { workspace: workspace, drawerOpen: desktopDrawerOpen || taskDrawerOpen || sidePanelFullScreen, children: [_jsxs("header", { className: "gyro-head", ref: headerRef, children: [workspace ? (_jsxs(_Fragment, { children: [_jsx("button", { ref: sidebarToggleRef, className: "gyro-btn ghost gyro-desktop-sidebar-toggle", onClick: () => { if (taskCompact)
                                                                setTaskPanelOpen(false); setSidebarOpen((current) => !current); }, title: sidebarOpen ? 'Hide navigation' : 'Show navigation', "aria-label": sidebarOpen ? 'Hide navigation' : 'Show navigation', "aria-expanded": sidebarOpen, "aria-controls": `gyro-desktop-sidebar-${appKey}`, children: _jsx(PanelLeft, { size: 18 }) }), officePane && !welcomeOnScreen && _jsx("img", { src: staticAvatar, alt: "", className: "gyro-pane-head-mascot", ...lookImg }), _jsxs("div", { className: "gyro-desktop-heading", children: [_jsx("span", { className: "gyro-desktop-heading-eyebrow", children: props.surfaceLabel ?? (desktopWorkspace ? 'Gyro Desktop' : props.title ?? 'Gyro') }), showPets || showNotifications || showMemory || showSessions || showCapabilities || starscribeListShown
                                                                    ? _jsx("h2", { className: `gyro-desktop-heading-title${showPets ? ' gyro-pets-heading' : ''}`, title: desktopTitle, children: desktopTitle })
                                                                    : _jsx(ChatTitleEditor, { title: desktopTitle, onRename: renameCurrentChat, editRequest: renameRequest?.id === sessionIdRef.current ? renameRequest.revision : 0, onEditRequestHandled: renameRequestHandled }, `${userKey}:${taskContextEpoch}`)] })] })) : _jsxs("div", { className: "gyro-title", children: [_jsx("img", { src: staticAvatar, alt: "", className: "gyro-head-avatar", ...lookImg }), _jsxs("div", { className: "gyro-stack", children: [showNotifications || showMemory || showSessions || showCapabilities ? _jsx("span", { children: props.title ?? 'Gyro' }) : _jsx(ChatTitleEditor, { title: sessions.find(session => session.id === sessionIdRef.current)?.title ?? props.title ?? 'Gyro', onRename: renameCurrentChat, editRequest: renameRequest?.id === sessionIdRef.current ? renameRequest.revision : 0, onEditRequestHandled: renameRequestHandled }, `${userKey}:${taskContextEpoch}`), _jsxs("span", { className: "gyro-status", children: [_jsx("span", { className: "gyro-dot" }), " ", unreachable ? 'reconnecting…' : configured === false ? 'no key configured' : 'Here to help'] })] })] }), _jsxs("span", { className: "gyro-row", children: [workspace && !desktopWorkspace && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-header-fork-chat", ...headerActionProps('fork-chat'), onClick: () => void forkCurrentChat(), disabled: busy || forking, title: "Fork chat", "aria-label": "Fork chat", children: _jsx(GitFork, { size: 17 }) }), workspace && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-header-new-chat", ...headerActionProps('new-chat'), onClick: () => { showChat(); newSession(); closeMobileSidebar(); requestAnimationFrame(() => inputRef.current?.focus()); }, title: "New chat", "aria-label": "New chat", children: _jsx(Plus, { size: 17 }) }), attention.bell, _jsx("button", { className: `gyro-btn ghost${paletteOpen ? ' active' : ''}`, ...headerActionProps('commands'), onClick: () => setPaletteOpen((p) => !p), title: "Commands (Ctrl+K)", "aria-label": "Open command palette (Ctrl+K)", "aria-haspopup": "dialog", "aria-expanded": paletteOpen, children: _jsx(Command, { size: 15 }) }), !workspace && _jsxs(_Fragment, { children: [_jsx("button", { className: `gyro-btn ghost${showCapabilities ? ' active' : ''}`, ...headerActionProps('capabilities'), onClick: () => { setShowCapabilities((c) => !c); setShowSessions(false); setShowMemory(false); }, title: "What can Gyro do here?", "aria-label": "What can Gyro do here?", children: _jsx(Compass, { size: 15 }) }), _jsx("button", { className: `gyro-btn ghost${showMemory ? ' active' : ''}`, ...headerActionProps('memory'), onClick: () => void toggleMemory(), title: "What Gyro remembers about you", "aria-label": "Your memory", children: _jsx(Brain, { size: 15 }) }), _jsx("button", { className: "gyro-btn ghost", ...headerActionProps('history'), onClick: () => { setShowSessions((s) => !s); setShowCapabilities(false); setShowMemory(false); }, title: "Chat history", "aria-label": "Chat history", children: _jsx(Clock, { size: 15 }) }), _jsx("button", { className: "gyro-btn ghost gyro-header-fork-chat", ...headerActionProps('fork-chat'), onClick: () => void forkCurrentChat(), disabled: busy || forking, title: "Fork chat", "aria-label": "Fork chat", children: _jsx(GitFork, { size: 15 }) }), _jsx("button", { className: "gyro-btn ghost", ...headerActionProps('new-chat'), onClick: newSession, title: "New chat", "aria-label": "New chat", children: _jsx(Plus, { size: 15 }) })] }), windowChrome.minimize && layoutChrome.windowControls && _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => window.dispatchEvent(new CustomEvent('gyro:window-minimize')), title: "Minimize to Gyro logo", "aria-label": "Minimize to Gyro logo", children: _jsx(Minus, { size: 15 }) }), windowChrome.layoutToggle && layoutChrome.modeToggle && _jsxs("button", { className: `gyro-btn ghost${desktopWorkspace ? ' gyro-window-mode-toggle' : ''}`, onClick: toggleExpanded, ...headerActionProps('expand'), "data-expand-toggle": floatingWorkspace ? '' : undefined, title: desktopWorkspace ? expanded ? 'Return to vertical view' : 'Expand to comfy view' : expanded ? 'Shrink' : 'Expand', "aria-label": desktopWorkspace ? expanded ? 'Return to vertical view' : 'Expand to comfy view' : expanded ? 'Shrink' : 'Expand', children: [expanded ? _jsx(Minimize2, { size: 15 }) : _jsx(Maximize2, { size: 15 }), desktopWorkspace && _jsx("span", { children: expanded ? 'Vertical' : 'Comfy' })] }), windowChrome.windowToggle && layoutChrome.windowControls && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-window-style-toggle", onClick: requestWindowSwitch, title: windowSwitch.label, "aria-label": windowSwitch.label, children: windowed ? _jsx(PictureInPicture2, { size: 15 }) : _jsx(AppWindow, { size: 15 }) }), windowChrome.maximize && layoutChrome.windowControls && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-window-maximize", onClick: () => window.dispatchEvent(new CustomEvent('gyro:window-maximize')), title: windowMaximized ? 'Restore window' : 'Maximize window', "aria-label": windowMaximized ? 'Restore window' : 'Maximize window', children: windowMaximized ? _jsx(Copy, { size: 14 }) : _jsx(Square, { size: 14 }) }), floatingPanel && headerOverflowItems.length > 0 && _jsx(HeaderOverflowMenu, { items: headerOverflowItems, onSelect: selectHeaderAction, onFocusLost: focusHeaderAfterMenu }), windowChrome.close && layoutChrome.closeButton && _jsx("button", { className: "gyro-btn ghost", ...headerActionProps('close'), onClick: closeAssistant, "aria-label": "Close", children: _jsx(X, { size: 15 }) })] })] }), desktopWorkspace && !settingsSection && !showPets && _jsx(DesktopKnowledgeBar, { mode: knowledgeMode, onChange: next => { if (!busy && (next === 'work' || knowledgeCapabilities.worldEnabled)) {
                                                updatePreferences({ knowledgeMode: next });
                                                setAttachError('');
                                            } }, disabled: busy, worldEnabled: knowledgeCapabilities.worldEnabled, webAvailable: knowledgeCapabilities.webAvailable, protection: knowledgeCapabilities.protection, demo: knowledgeCapabilities.demo }), speechState !== 'idle' && _jsxs("div", { className: "gyro-speech-bar", "aria-label": "Voice playback", children: [_jsxs("span", { className: "gyro-speech-status", role: "status", children: [_jsx(Volume2, { size: 15, "aria-hidden": "true" }), speechState === 'loading' ? 'Preparing voice…' : 'Speaking…'] }), _jsxs("button", { type: "button", className: "gyro-btn gyro-speech-stop", onClick: stopReplySpeech, "aria-label": "Stop speaking", children: [_jsx(Square, { size: 13, "aria-hidden": "true" }), "Stop speaking"] })] }), !workspace && !showNotifications && !showSessions && !showCapabilities && !showMemory && (_jsxs("div", { className: "gyro-modebar", children: [_jsx("span", { className: "gyro-modebar-label", children: "Mode" }), _jsxs("span", { className: "gyro-mode-seg", role: "group", "aria-label": "Response mode", children: [_jsxs("button", { className: `gyro-mode-btn${mode === 'voice' ? ' active' : ''}`, onClick: () => switchMode('voice'), "aria-pressed": mode === 'voice', children: [_jsx(Volume2, { size: 13 }), " Voice"] }), _jsxs("button", { className: `gyro-mode-btn${mode === 'detailed' ? ' active' : ''}`, onClick: () => switchMode('detailed'), "aria-pressed": mode === 'detailed', children: [_jsx(VolumeX, { size: 13 }), " Detailed"] })] }), _jsx("span", { className: "gyro-modebar-hint", children: mode === 'voice' ? 'Short spoken replies' : 'Full written answers' }), notificationsEnabled && !props.nativeNotifications && notifPerm !== 'unsupported' && (_jsx("button", { type: "button", className: `gyro-btn ghost gyro-notify${notifPerm === 'granted' ? ' active' : ''}`, onClick: enableNotifications, disabled: notifPerm !== 'default', "aria-pressed": notifPerm === 'granted', title: notifPerm === 'granted' ? 'Desktop notifications are on (manage in your browser settings)' : notifPerm === 'denied' ? 'Notifications are blocked in your browser settings' : 'Notify me when an answer finishes while this tab is hidden', "aria-label": notifPerm === 'granted' ? 'Desktop notifications are on' : notifPerm === 'denied' ? 'Notifications are blocked in your browser settings' : 'Enable desktop notifications', children: notifPerm === 'denied' ? _jsx(BellOff, { size: 13 }) : _jsx(Bell, { size: 13 }) }))] })), !chatCovered && starscribe.strip, attention.center, desktopWorkspace && showPets ? (companionSessions.ready && petSetup.ready ? _jsx(PetsWorkspace, { draftSession: petSetup.drafts, renderPetUpdates: attention.renderPetUpdates, client: props.client, missionSession: petMissionSession, checkStatusSessions: petCheckStatusSessions, briefRemovalSession: petBriefRemovalSession, lifecycleSession: petLifecycleSession, resolutionSession: petResolutionSession, viewZoneSession: petViewZoneSession, actionReviewSession: petActionReviewSession, route: petRoute, onNavigate: openPets, nativeAvailable: props.nativePets === true, onConnections: () => openSettings('connectors'), onNotifications: () => openSettings('general'), onDisabled: disablePets }, `pet-workspace:${userKey}:${petSessionEpoch}`)
                                            : _jsxs("p", { role: companionSessions.failed || petSetup.failed ? 'alert' : 'status', children: [companionSessions.failed || petSetup.failed ? 'This feature could not load. Reload Gyro if retry does not help.' : 'Loading…', petSetup.failed && _jsx("button", { className: "gyro-btn ghost", onClick: petSetup.retry, children: "Retry setup" })] })) : desktopWorkspace && showNotifications ? (_jsx(DesktopNotifications, { entries: notificationCenter.entries, onRead: notificationCenter.markRead, onReadAll: notificationCenter.markAllRead, onOpen: openNotification, onSettings: () => openSettings('general'), busyConversationId: busy ? sessionIdRef.current ?? undefined : undefined })) : showMemory ? (_jsxs("div", { className: "gyro-body gyro-memory", "aria-busy": memoryLoading, children: [_jsxs("p", { className: "gyro-cap-intro", children: ["What ", _jsx("strong", { children: "Gyro" }), " remembers about you \u2014 it adapts to this everywhere you use Gyro. Edit or clear anything; it's your profile."] }), memoryLoading ? (_jsx("p", { className: "gyro-muted", role: "status", children: "Loading your memory\u2026" })) : memoryLoadFailed ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "gyro-warn", role: "alert", children: memoryError }), _jsxs("div", { className: "gyro-action-btns", style: { marginTop: 8 }, children: [_jsx("button", { className: "gyro-btn", onClick: () => void loadMemory(), children: "Try again" }), _jsx("button", { className: "gyro-btn ghost", onClick: () => setShowMemory(false), children: "Close" })] })] })) : (_jsxs(_Fragment, { children: [_jsx("textarea", { ref: memoryTextRef, className: "gyro-textinput gyro-memory-text", value: memoryText, maxLength: MEMORY_MAX_CHARS, onChange: (e) => { setMemoryText(e.target.value.slice(0, MEMORY_MAX_CHARS)); setMemorySaved(false); }, placeholder: "Nothing learned yet. As you work with Gyro it notes stable preferences and context here (e.g. 'prefers concise answers', 'works on the Tower-A project'). You can add or edit anything yourself.", rows: 11, "aria-label": "Your Gyro memory" }), _jsxs("div", { className: "gyro-memory-meta", children: [memoryError && _jsx("span", { className: "gyro-warn", role: "alert", children: memoryError }), _jsxs("span", { className: `gyro-memory-count${memoryText.length >= MEMORY_MAX_CHARS ? ' full' : ''}`, "aria-label": `${memoryText.length} of ${MEMORY_MAX_CHARS} characters used`, children: [memoryText.length.toLocaleString(), " / ", MEMORY_MAX_CHARS.toLocaleString()] })] }), _jsxs("div", { className: "gyro-action-btns", style: { marginTop: 8 }, children: [_jsx("button", { className: "gyro-btn", onClick: () => void saveMemory(), disabled: memorySaving, children: memorySaving ? 'Saving…' : memorySaved ? 'Saved ✓' : 'Save' }), _jsx("button", { className: "gyro-btn ghost", onClick: () => { setMemoryText(''); setMemorySaved(false); }, disabled: memorySaving, children: "Clear" }), _jsx("button", { className: "gyro-btn ghost", onClick: () => setShowMemory(false), children: "Close" })] })] }))] })) : showSessions ? (_jsxs("div", { className: "gyro-body gyro-sessions", children: [_jsx("input", { ref: historySearchRef, className: "gyro-textinput", style: { marginBottom: 8 }, placeholder: "Search chats\u2026", value: historySearch, onChange: (e) => setHistorySearch(e.target.value), onKeyDown: (e) => {
                                                        if (e.key === 'Enter')
                                                            void syncRemoteHistory(historySearch);
                                                    }, "aria-label": "Search chat history" }), sessions.length === 0 && _jsx("p", { className: "gyro-muted", children: "No saved chats yet." }), sessions
                                                    .filter((s) => !historySearch || s.title.toLowerCase().includes(historySearch.toLowerCase()))
                                                    .map((s) => (_jsxs("div", { className: "gyro-session-row", onContextMenu: event => { event.preventDefault(); openChatMenu(s, event.currentTarget.querySelector('button'), { x: event.clientX, y: event.clientY }); }, children: [_jsxs("button", { className: "gyro-session-open", onClick: () => { setChatMenu(null); void loadSession(s); }, onKeyDown: event => chatMenuKey(event, s), children: [_jsx("span", { className: "gyro-session-title", children: s.title }), _jsxs("span", { className: "gyro-faint", children: [s.remote ? `${s.turnCount ?? 0} messages · synced` : `${s.messages.length} messages`, chatOriginLabel(s.app, appKey) && _jsx("span", { className: "gyro-session-origin", title: "Where this chat was started", children: chatOriginLabel(s.app, appKey) })] })] }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: event => openChatMenu(s, event.currentTarget), title: "Chat options", "aria-label": `Chat options: ${s.title}`, "aria-haspopup": "menu", children: _jsx(MoreHorizontal, { size: 14 }) })] }, s.id)))] })) : showCapabilities ? (_jsxs("div", { className: "gyro-body gyro-capabilities", children: [_jsxs("p", { className: "gyro-cap-intro", children: ["Here's what I can do on ", _jsx("strong", { children: "this" }), " screen. Just ask in plain language \u2014 I'll confirm before any change."] }), desktopWorkspace && _jsxs("div", { className: "gyro-cap-group", children: [_jsx("div", { className: "gyro-cap-head", children: "Create documents" }), _jsxs("div", { className: "gyro-cap-item", children: [_jsx("span", { className: "gyro-cap-name", children: "Office, PDF, Markdown, text/data, ZIP, and skills" }), _jsx("span", { className: "gyro-cap-desc", children: "Attach common documents and archives. Ask Gyro to create a document, workbook, presentation, data file, or download bundle. Download the finished file from the answer, or use Export on an existing response." })] })] }), capabilities.length === 0 ? (_jsx("p", { className: "gyro-muted", children: "No app actions are wired here yet \u2014 but I can still answer questions, explain what's on screen, and help you set things up." })) : (_jsxs(_Fragment, { children: [capabilities.some((t) => t.mode === 'read') && (_jsxs("div", { className: "gyro-cap-group", children: [_jsx("div", { className: "gyro-cap-head", children: "I can look things up" }), capabilities.filter((t) => t.mode === 'read').map((t) => (_jsxs("div", { className: "gyro-cap-item", children: [_jsx("span", { className: "gyro-cap-name", children: t.name.replace(/_/g, ' ') }), t.description && _jsx("span", { className: "gyro-cap-desc", children: t.description })] }, t.name)))] })), capabilities.some((t) => t.mode === 'write') && (_jsxs("div", { className: "gyro-cap-group", children: [_jsx("div", { className: "gyro-cap-head", children: "I can take actions (you confirm first)" }), capabilities.filter((t) => t.mode === 'write').map((t) => (_jsxs("div", { className: "gyro-cap-item", children: [_jsxs("span", { className: "gyro-cap-name", children: [t.name.replace(/_/g, ' '), t.riskTier && t.riskTier !== 'standard' && _jsx("span", { className: `gyro-risk risk-${t.riskTier}`, children: t.riskTier })] }), t.description && _jsx("span", { className: "gyro-cap-desc", children: t.description })] }, t.name)))] }))] })), _jsx("button", { className: "gyro-btn", style: { marginTop: 10 }, onClick: () => setShowCapabilities(false), children: "Got it" })] })) : starscribeMain ? starscribeMain : (_jsxs("div", { className: "gyro-body", ref: listRef, style: workspace ? { overflowAnchor: 'none' } : undefined, children: [supportCards.length > 0 && (_jsx("div", { className: "gyro-support-cards", "aria-label": "IT support requests", children: supportCards.map((c) => (_jsx(SupportCard, { card: applyClaimOutcome(c, supportOutcomes[c.requestId]), outcome: supportOutcomes[c.requestId], claiming: supportClaiming === c.requestId, onClaim: (card) => void claimSupportRequest(card), onDismiss: dismissSupportCard }, c.requestId))) })), messages.every(m => m.welcome) && (_jsxs(WelcomeSurface, { workspace: workspace, avatar: largeAvatar, onAvatarError: lookImg.onError, heading: welcomeHeading(dayGreeting, Date.now()), headingLevel: floatingWorkspace ? 2 : 1, greeting: greeting.content, nickname: preferences.nickname, children: [suggestions.map((s) => (_jsx("button", { className: "gyro-chip", onClick: (event) => {
                                                                // "Write a formula to…" is a start the person finishes: it goes into the composer instead of being sent.
                                                                const draft = suggestionDraft(s);
                                                                if (draft === null) {
                                                                    void send(s);
                                                                    // A starter chosen from the keyboard goes away with the welcome: focus moves to the composer, not the page's
                                                                    // body (a click or a tap leaves it, so no on-screen keyboard opens).
                                                                    if (event.detail === 0)
                                                                        requestAnimationFrame(() => inputRef.current?.focus());
                                                                    return;
                                                                }
                                                                setInput(draft);
                                                                requestAnimationFrame(() => { const field = inputRef.current; if (field) {
                                                                    field.focus();
                                                                    field.setSelectionRange(draft.length, draft.length);
                                                                } });
                                                            }, children: s }, s))), pppOn && !suggestions.includes(PPP_STARTER) && (_jsx("button", { className: "gyro-chip", onClick: (event) => {
                                                                void send(PPP_STARTER);
                                                                if (event.detail === 0)
                                                                    requestAnimationFrame(() => inputRef.current?.focus());
                                                            }, children: PPP_STARTER })), labAvailable && (_jsxs("button", { className: "gyro-chip gyro-chip-discover", onClick: startBugReport, children: [_jsx(Bug, { size: 13 }), " Report a bug"] })), _jsxs("button", { className: "gyro-chip gyro-chip-discover", onClick: () => setShowCapabilities(true), children: [_jsx(Compass, { size: 13 }), " What can Gyro do here?"] }), unreachable && _jsxs("p", { className: "gyro-warn", children: ["Gyro can't reach the service for this app right now \u2014 retrying automatically. If this persists, the app likely needs its Gyro auth wired up (backend proxy + app key / signed assertion).", unreachableReason ? _jsxs("span", { className: "gyro-warn-detail", children: [" (", unreachableReason, ")"] }) : null] }), !unreachable && configured === false && _jsx("p", { className: "gyro-warn", children: "No model key configured on the service yet." })] })), messages.map((m, i) => workspace && m.welcome ? null : (_jsxs("div", { children: [_jsxs("div", { className: `gyro-msg ${m.role}${m.auto ? ' auto' : ''}${m.error ? ' error' : ''}${m.note ? ' note' : ''}`, role: m.note ? 'status' : undefined, children: [m.role === 'assistant' && !m.note && _jsx("img", { src: staticAvatar, alt: "", className: "gyro-msg-avatar", ...lookImg }), _jsxs("div", { className: "gyro-bubble", children: [m.attachments && m.attachments.length > 0 && (_jsx("div", { className: "gyro-msg-attachments", children: m.attachments.map((a, ai) => (a.kind !== 'image' ? _jsxs("span", { className: "gyro-document-chip", "data-preview": htmlPreview.has(attachmentSourceId(i, ai)) ? 'true' : undefined, children: [_jsx(FileText, { size: 19, "aria-hidden": "true" }), _jsxs("span", { children: [a.name || 'File attachment', _jsx("small", { children: fileTypeLabel(a.name, a.mediaType) })] }), htmlPreview.has(attachmentSourceId(i, ai)) && _jsx("button", { type: "button", className: "gyro-preview-button", "aria-label": `Preview ${a.name || 'the attached page'} in the side panel`, title: "Preview", onClick: () => htmlPreview.openSource(attachmentSourceId(i, ai)), children: "Preview" })] }, ai)
                                                                                : _jsx("img", { src: `data:${a.mediaType};base64,${a.data}`, alt: a.name || 'attachment', className: "gyro-msg-thumb" }, ai))) })), !desktopWorkspace && m.pending && !m.content ? (_jsxs("span", { className: "gyro-typing", children: ["Thinking\u2026", m.workingForMs && m.workingForMs >= 8000 ? ` still working (${Math.round(m.workingForMs / 1000)}s)` : '', preferences.showTokenCounter && m.liveUsage && _jsx(LiveTokenCounter, { usage: m.liveUsage })] })) : m.auto ? _jsx("span", { className: "gyro-msg-auto", role: "status", children: "Continuing my PPP\u2026" })
                                                                            : m.content ? _jsx(Markdown, { text: m.content, guideBaseUrl: client.baseUrl, onPetNavigate: appKey === 'desktop' && petsEnabled && !props.petContext && m.role === 'assistant' ? openPets : undefined, onPreviewHtml: htmlPreview.enabled && m.role === 'assistant' && !m.pending && !m.streaming && !m.error ? (ordinal) => htmlPreview.openSource(codeSourceId(pppReplyKey(m, i), ordinal)) : undefined }) : null, previousUserKeyRef.current === userKey && m.role === 'assistant' && !m.streaming && !m.pending && (documentOutputs && !m.error && m.content && i > 0 && shouldOfferExport(m.requestedDocuments ?? [], (m.artifacts?.length ?? 0) + documentStore.forReply(`${m.startedAt ?? i}`).length)
                                                                            ? _jsx(DocumentOutputs, { replyId: `${m.startedAt ?? i}`, question: m.documentPrompt ?? m.question ?? '', markdown: m.content, attachedName: messages[i - 1]?.role === 'user' ? messages[i - 1].attachments?.find(file => file.kind !== 'image' && file.name)?.name : undefined, requested: m.requestedDocuments ?? [], autoCreate: Boolean(m.documentReady && !m.actions?.length && !questionsForReply(m.content, m.followups, m.questions)?.length), artifacts: m.artifacts, client: client, store: documentStore, openPdf: pppPdfOpen[pppReplyKey(m, i)], pdfFallback: m.ppp?.exported ? pppDurablePdf : undefined, webPages: htmlPreview.enabled ? { isWebPage: htmlPreview.isWebPage, preview: (artifact) => htmlPreview.openFile(artifact, i) } : undefined, onFiles: files => {
                                                                                    if (documentStoreRef.current === documentStore && m.startedAt === taskActivity?.startedAt)
                                                                                        setTaskArtifacts(files);
                                                                                } }, `${userKey}:${taskContextEpoch}:${m.startedAt ?? i}`) : m.artifacts ? (_jsx(GeneratedFiles, { artifacts: m.artifacts, client: client, openPdf: pppPdfOpen[pppReplyKey(m, i)], pdfFallback: m.ppp?.exported ? pppDurablePdf : undefined, webPages: htmlPreview.enabled ? { isWebPage: htmlPreview.isWebPage, preview: (artifact) => htmlPreview.openFile(artifact, i) } : undefined }, pppReplyKey(m, i))) : null), m.role === 'assistant' && starscribe.cards(`${sessionIdRef.current}:${m.startedAt ?? i}`), m.actionReceipt && (_jsxs("details", { className: "gyro-action-receipt", children: [_jsx("summary", { children: "Execution receipt" }), (m.actionReceipt.runId || m.actionReceipt.correlationId || m.actionReceipt.implementationRevision) && (_jsxs("div", { className: "gyro-action-receipt-meta", children: [m.actionReceipt.runId && _jsxs("span", { children: ["Run ", m.actionReceipt.runId] }), m.actionReceipt.correlationId && _jsxs("span", { children: ["Correlation ", m.actionReceipt.correlationId] }), m.actionReceipt.implementationRevision && _jsxs("span", { children: ["Release ", m.actionReceipt.implementationRevision] })] })), _jsx("pre", { children: m.actionReceipt.text })] })), showsLookPicker(appKey, m) && _jsx(ChatMascotLookPicker, { client: client, builtInSrc: builtInAvatar }), showsPetSetupReview(Boolean(petsEnabled), petSetup.handoffs, sessionIdRef.current ?? '', m) && _jsxs("div", { className: "gyro-pet-setup-handoff", children: [_jsx("button", { className: "gyro-btn ghost", disabled: !petSetup.ready || Boolean(petSetup.checking), onClick: () => void petSetup.open(m.petSetupReview, sessionIdRef.current ?? '', openPets), children: petSetup.label() }), _jsx("p", { children: "This opens a setup for your review. Nothing is created, tested or activated automatically." }), petSetup.failed && _jsxs("p", { role: "alert", children: ["Setup could not load. ", _jsx("button", { className: "gyro-btn ghost", onClick: petSetup.retry, children: "Retry setup" })] }), petSetup.notice && petSetup.notice.token === m.petSetupReview && _jsx("p", { role: "status", children: petSetup.notice.text })] }), desktopWorkspace && m.localFileResult && (!m.error || m.localFileResult.kind === 'mcp') && (_jsx(LocalFileContinueCard, { result: m.localFileResult, blocked: { busy, worldMode: knowledgeMode !== 'work', dictating: speechInputState !== 'idle', draft: Boolean(input.trim()), attachments: attachments.length > 0 || attachmentReads > 0 }, onContinue: result => void continueFileTask(result) })), m.link && /^https:\/\//.test(m.link.href) && (_jsxs("a", { className: "gyro-msg-link", href: m.link.href, target: "_blank", rel: "noreferrer noopener", referrerPolicy: "no-referrer", children: [_jsx(ExternalLink, { size: 11 }), " ", m.link.label] })), m.vaultDownload && (_jsxs("div", { className: "gyro-vault-download", children: [_jsxs("span", { children: [_jsx("strong", { children: m.vaultDownload.modelName }), _jsxs("small", { children: ["Single use; expires ", new Date(m.vaultDownload.expiresAt * 1000).toLocaleTimeString()] })] }), native.vaultDownload && relayableVaultUrl(m.vaultDownload.url)
                                                                                    ? (_jsx(VaultDownloadControl, { id: m.vaultDownload.id, result: vaultResults[m.vaultDownload.id], requested: vaultRequested[m.vaultDownload.id] ?? 'none', onDownload: () => requestVaultDownload(m.vaultDownload), onOpen: () => requestVaultOpen(m.vaultDownload.id) }))
                                                                                    : _jsx("a", { className: "gyro-btn", href: m.vaultDownload.url, download: true, rel: "noreferrer", referrerPolicy: "no-referrer", children: "Download now" })] })), m.bluebeamUpload && native.bluebeamUpload && (_jsx(BluebeamUploadLegCard, { leg: m.bluebeamUpload, result: uploadResults[m.bluebeamUpload.runId], onSend: () => requestBluebeamUpload(m.bluebeamUpload) })), desktopWorkspace && m.role === 'assistant' && _jsx(StreamingActivity, { active: Boolean(m.streaming || m.pending), task: taskActivity, hasContent: Boolean(m.content), startedAt: m.startedAt, latestTextAt: m.latestTextAt, usage: m.liveUsage, showUsage: preferences.showTokenCounter, personConnect: personConnect }), !desktopWorkspace && m.streaming && (_jsx(CompactActivityRow, { steps: m.toolActivity, usage: compactRowUsage(m, preferences.showTokenCounter) }))] })] }), m.actionOutcomeId && renderPulseCard(m), m.role === 'assistant' && !m.pending && !m.streaming && !m.error && !m.note && i > 0 && (_jsxs(_Fragment, { children: [m.advisory && (_jsxs("div", { className: "gyro-advisory", children: ["\u26A0 ", m.advisory] })), m.grounded === false && (_jsx("div", { className: "gyro-ungrounded", children: "Not grounded in live data \u2014 verify before acting." })), m.sources && m.sources.length > 0 && (_jsxs("details", { className: "gyro-sources", children: [_jsxs("summary", { children: [m.sources.length, " source", m.sources.length > 1 ? 's' : '', " consulted"] }), m.sources.map((s, si) => (_jsxs("div", { className: "gyro-source", children: [_jsx("span", { className: `gyro-source-kind ${s.kind}`, children: s.kind }), _jsx("code", { children: s.label || s.ref }), s.snippet && _jsxs("span", { className: "gyro-faint", children: [" \u2014 ", s.snippet.slice(0, 120)] })] }, si)))] })), _jsxs("div", { className: "gyro-msg-actions", children: [!m.classified && starscribe.editButton(`${sessionIdRef.current}:${m.startedAt ?? i}`, m.content, sessionIdRef.current), _jsxs("button", { className: "gyro-link", onClick: () => copyAnswer(m.content), children: [_jsx(Copy, { size: 11 }), " Copy"] }), _jsxs("button", { className: "gyro-link gyro-fork-from-here", onClick: () => void forkFromTurn(i), disabled: busy || forking, title: "Start a new chat that keeps everything up to this reply", "aria-label": "Fork from here", children: [_jsx(GitFork, { size: 11 }), " Fork from here"] }), m.question && (_jsxs("button", { className: "gyro-link", onClick: () => void send(m.question), disabled: busy, children: [_jsx(RefreshCw, { size: 11 }), " Regenerate"] })), allowShare && (_jsxs("button", { className: `gyro-link${share?.key === `m${i}` ? ' active' : ''}`, onClick: () => dispatchShare({ type: 'request', key: `m${i}` }), title: "Create a link to this answer", "aria-expanded": share?.key === `m${i}`, children: [_jsx(Share2, { size: 11 }), " Share"] })), saveSkillOn && m.content && !m.classified && (_jsx(SaveSkillLink, { active: skillBox?.key === `m${i}`, onClick: () => setSkillBox((current) => (current?.key === `m${i}` ? null : { key: `m${i}`, body: m.content })) })), m.traceId && (_jsxs("span", { className: "gyro-feedback", children: [_jsx("button", { className: `gyro-link${feedbackGiven[m.traceId] === 'up' ? ' active' : ''}`, onClick: () => void giveFeedback(m.traceId, 'up', m.askId), "aria-label": "Good answer", title: "Good answer", children: _jsx(ThumbsUp, { size: 11 }) }), _jsx("button", { className: `gyro-link${feedbackGiven[m.traceId] === 'down' ? ' active' : ''}`, onClick: () => void giveFeedback(m.traceId, 'down', m.askId), "aria-label": "Bad answer", title: "Bad answer", children: _jsx(ThumbsDown, { size: 11 }) })] })), m.model && (_jsxs("span", { className: "gyro-model-chip", children: [_jsx(Sparkles, { size: 10 }), " ", m.model] }))] }), pulseThumbBox && m.traceId && pulseThumbBox.traceId === m.traceId && (_jsx(PulseThumbBox, { askId: pulseThumbBox.askId, thumb: pulseThumbBox.thumb, onSend: (feedback) => client.pulseFeedback(feedback), onClose: () => setPulseThumbBox((current) => (current?.traceId === m.traceId ? null : current)) }, `${pulseThumbBox.traceId}:${pulseThumbBox.thumb}`)), allowShare && share?.key === `m${i}` && (_jsxs("div", { className: "gyro-share-box", role: "group", "aria-label": "Share this answer", children: [share.step === 'confirm' && m.classified && (_jsxs(_Fragment, { children: [_jsx("p", { className: "gyro-warn", role: "alert", children: PAY_DATA_NOT_SHAREABLE_TEXT }), _jsx("div", { className: "gyro-action-btns", children: _jsx("button", { className: "gyro-btn ghost", onClick: () => dispatchShare({ type: 'cancel' }), children: "Close" }) })] })), share.step === 'confirm' && !m.classified && (_jsxs(_Fragment, { children: [_jsx("p", { className: "gyro-share-text", children: SHARE_CONFIRM_TEXT }), _jsxs("div", { className: "gyro-action-btns", children: [_jsx("button", { className: "gyro-btn", onClick: () => void createShareLink(`m${i}`, m), children: "Create link" }), _jsx("button", { className: "gyro-btn ghost", onClick: () => dispatchShare({ type: 'cancel' }), children: "Cancel" })] })] })), share.step === 'creating' && _jsx("p", { className: "gyro-share-text", role: "status", children: "Creating link\u2026" }), share.step === 'ready' && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "gyro-share-text", children: ["Link ready \u2014 anyone with it can read this answer for ", SHARE_TTL_DAYS, " days."] }), _jsxs("div", { className: "gyro-share-link", children: [_jsx("input", { className: "gyro-textinput", readOnly: true, value: share.url, onFocus: (e) => e.currentTarget.select(), "aria-label": "Share link" }), _jsx("button", { className: "gyro-btn", onClick: () => copyShareLink(share.url), "aria-live": "polite", children: share.copy === 'copied' ? 'Copied ✓' : 'Copy' }), _jsx("button", { className: "gyro-btn ghost", onClick: () => dispatchShare({ type: 'cancel' }), children: "Done" })] }), share.copy === 'failed' && _jsx("p", { className: "gyro-warn", role: "alert", children: "Copy was blocked \u2014 select the link and copy it manually." })] })), share.step === 'refused' && (_jsxs(_Fragment, { children: [_jsx("p", { className: "gyro-warn", role: "alert", children: share.message }), _jsx("div", { className: "gyro-action-btns", children: _jsx("button", { className: "gyro-btn ghost", onClick: () => dispatchShare({ type: 'cancel' }), children: "Close" }) })] })), share.step === 'failed' && (_jsxs(_Fragment, { children: [_jsx("p", { className: "gyro-warn", role: "alert", children: "Could not create a share link. Try again in a moment." }), _jsxs("div", { className: "gyro-action-btns", children: [_jsx("button", { className: "gyro-btn", onClick: () => dispatchShare({ type: 'request', key: `m${i}` }), children: "Try again" }), _jsx("button", { className: "gyro-btn ghost", onClick: () => dispatchShare({ type: 'cancel' }), children: "Close" })] })] }))] })), skillBox?.key === `m${i}` && (_jsx(SaveSkillBox, { skills: gyroSkills, body: skillBox.body, onClose: () => setSkillBox(null) }, `skill:${i}`)), attention.renderMessage(m), replyQuestions[i] && i !== dockedQuestionIndex && _jsx(ClarificationCard, { questions: replyQuestions[i], disabled: busy || attachmentPendingRef.current > 0, answered: messages.slice(i + 1).some(message => message.role === 'user'), onAnswer: reply => {
                                                                        if (busy || attachmentPendingRef.current > 0)
                                                                            return false;
                                                                        void send(reply, [], m.requestedDocuments);
                                                                        return true;
                                                                    } }), !replyQuestions[i] && m.followups && m.followups.length > 0 && (_jsx("div", { className: "gyro-followups", children: m.followups.map((f, fi) => (_jsx("button", { className: "gyro-chip small", onClick: () => void send(f), disabled: busy, children: f }, fi))) })), renderPppChips(m, i)] })), m.autoActions?.map((a, ai) => (_jsxs("div", { className: `gyro-action resolved ${a.result === 'success' ? 'done' : 'error'}`, children: [_jsxs("div", { className: "gyro-action-head", children: [_jsx("span", { children: a.result === 'success' ? '⚡ Done autonomously' : '⚠ Autonomous attempt failed' }), a.app && _jsxs("span", { className: "gyro-sys-badge", children: ["in ", actionSystemName(a)] }), a.riskTier && _jsx("span", { className: `gyro-risk risk-${a.riskTier}`, children: a.riskTier })] }), _jsx("div", { className: "gyro-action-summary", children: a.summary }), _jsx(ActionToolLine, { tool: a.tool, app: a.app, appLabel: knownSystemName(a.app), suffix: `${a.reversible ? ' · reversible' : ''}${a.message ? ` · ${a.message}` : ''}` })] }, `auto-${ai}`))), !m.actionOutcomeId && renderPulseCard(m), m.actions?.map((a) => (_jsxs("div", { "data-notification-action": a.id, tabIndex: -1, className: `gyro-action${resolved[a.id] ? ` resolved ${resolved[a.id]}` : ''}`, children: [_jsxs("div", { className: "gyro-action-head", children: [_jsx("span", { children: m.undoable ? 'Completed action' : 'Confirm action' }), a.app && _jsxs("span", { className: "gyro-sys-badge", children: ["\u26A0 in ", actionSystemName(a)] }), a.riskTier && _jsx("span", { className: `gyro-risk risk-${a.riskTier}`, children: a.riskTier }), a.reversible && _jsx("span", { className: "gyro-risk risk-standard", children: "reversible" })] }), a.app && _jsxs("div", { className: "gyro-action-system", children: ["This runs in ", _jsx("strong", { children: actionSystemName(a) }), a.app === PERSON_SURFACE_APP ? ', as you' : '', ", not this app."] }), _jsx("div", { className: "gyro-action-summary", children: actionSummary(a) }), a.app === 'the-lab' && a.tool === LAB_FILE_TOOL && (_jsx(LabReportCard, { input: a.input, preview: labPreviews[a.id], screenshots: selectReportScreenshots(messages, Number(a.input.screenshotCount ?? 0)) })), a.app === 'the-lab' && a.tool !== LAB_FILE_TOOL && LAB_UPLOAD_TOOLS.has(a.tool) && (_jsx(LabScreenshotRow, { screenshots: selectReportScreenshots(messages, Number(a.input.screenshotCount ?? 0)) })), _jsx(ActionToolLine, { tool: a.tool, app: a.app, appLabel: knownSystemName(a.app) }), (localActionFolders(a, localFilesSnapshot) ?? []).map((folder) => (_jsxs("div", { className: "gyro-action-folder", children: [folder.role === 'from' ? 'In folder' : folder.role === 'to' ? 'Into folder' : 'Runs in', ":", ' ', folder.documents
                                                                            ? _jsx("strong", { children: "Documents" })
                                                                            : folder.root
                                                                                ? _jsxs(_Fragment, { children: [_jsx("strong", { children: folder.root.name }), " ", _jsx("small", { children: folder.root.path })] })
                                                                                : _jsx("strong", { children: "no longer connected, so Gyro has to propose this again once it is connected (Cancel this one)" })] }, `${folder.role}:${folder.id}`))), _jsx(ActionParameterDetails, { tool: a.tool, input: a.input, limits: confirmLimits(a), open: !(a.app === 'the-lab' && a.tool === LAB_FILE_TOOL) }), previews[a.id] && (_jsxs("div", { className: "gyro-action-preview", children: [previews[a.id].fields?.length
                                                                            ? _jsxs(_Fragment, { children: [_jsx(PreviewFieldList, { fields: previews[a.id].fields }), _jsxs("details", { children: [_jsx("summary", { children: "Show the exact preview" }), _jsx("div", { children: previews[a.id].text })] })] })
                                                                            : _jsx("div", { children: previews[a.id].text }), previews[a.id].connect && _jsx(PersonConnectControl, { connect: previews[a.id].connect, flow: personConnect, compact: true }), previews[a.id].implementationRevision && _jsxs("small", { children: ["Release ", previews[a.id].implementationRevision] }), previews[a.id].confirmationPhrase && (previews[a.id].expiresAt ?? 0) * 1000 <= nowMs && _jsx("small", { children: "Preview expired. Approve gets a fresh one." })] })), m.undoable ? (_jsx("div", { className: "gyro-action-btns", children: _jsxs("button", { className: "gyro-btn ghost", onClick: () => void undoAction(a), children: [_jsx(Undo2, { size: 13 }), " ", a.app === 'the-lab' && a.tool === LAB_FILE_TOOL ? 'Withdraw report' : 'Undo'] }) })) : resolved[a.id] === 'executing' && clientActions ? (runHereOffers.includes(a.id) ? (_jsxs("div", { className: "gyro-action-run-here", role: "group", "aria-label": "Approved action", children: [_jsx("div", { className: "gyro-action-state", id: `gyro-run-here-${a.id}`, children: "Approved and not run yet. Choose Run it here in the document it is for." }), _jsx("div", { className: "gyro-action-btns", children: _jsx("button", { type: "button", className: "gyro-btn", onClick: () => runApprovedHere(a), "aria-describedby": `gyro-run-here-${a.id}`, children: "Run it here" }) })] })) : _jsx("div", { className: "gyro-action-state", children: executingLabelFor(clientActions) })) : resolved[a.id] ? (_jsx("div", { className: "gyro-action-state", children: resolved[a.id] === 'done' ? 'Confirmed' : resolved[a.id] === 'awaiting-approval' ? 'Awaiting approval' : resolved[a.id] === 'executing' ? 'Running on your PC' : resolved[a.id] === 'reconciliation-required' ? 'Outcome unknown - do not retry; administrator reconciliation required' : resolved[a.id] === 'cancelled' ? 'Cancelled' : failedStateLine(messages.find((message) => message.actionOutcomeId === a.id && message.error)?.failureReason) })) : renderApprovalControls(a)] }, a.id)))] }, i)))] })), !chatCovered && !starscribeMain && (_jsxs(_Fragment, { children: [attention.composerQuestions, dockedQuestionMessage && _jsx("div", { className: "gyro-question-dock", "aria-label": "Pending question", children: _jsx(ClarificationCard, { questions: replyQuestions[dockedQuestionIndex], disabled: busy || attachmentPendingRef.current > 0, answered: false, docked: true, onAnswer: reply => {
                                                            if (busy || attachmentPendingRef.current > 0)
                                                                return false;
                                                            void send(reply, [], dockedQuestionMessage.requestedDocuments);
                                                            return true;
                                                        } }, `${sessionIdRef.current ?? 'new'}:${dockedQuestionIndex}:${replyQuestions[dockedQuestionIndex].map(question => question.id + question.question).join('|')}`) }), !desktopWorkspace && skillsPanelOpen && skillsStatus && (_jsx("div", { className: "gyro-composer-skill-box-wrap", children: _jsx(GyroSkillsPanel, { status: skillsStatus, skills: gyroSkills, onUse: name => { setInput(current => `run ${name} ${current}`); setSkillsPanelOpen(false); requestAnimationFrame(() => inputRef.current?.focus()); }, onSave: saveSkillOn ? () => { setSkillsPanelOpen(false); setSkillBox({ key: 'composer', body: input.trim() || lastSkillSeed }); } : undefined, onClose: () => setSkillsPanelOpen(false) }) })), skillBox?.key === 'composer' && (_jsx("div", { className: "gyro-composer-skill-box-wrap", children: _jsx(SaveSkillBox, { skills: gyroSkills, body: skillBox.body, onClose: () => setSkillBox(null) }, "skill:composer") })), _jsxs("div", { className: "gyro-composer", "data-high": modelTier === 'high' || undefined, children: [desktopWorkspace && workingFolder && knowledgeMode === 'work' && _jsxs("button", { type: "button", className: "gyro-working-context", onClick: () => { if (compactDesktop)
                                                                setSidebarOpen(false); setTaskPanelOpen(true); }, title: "Review the text files included with your next message", children: [_jsx(Paperclip, { size: 13, "aria-hidden": "true" }), _jsx("span", { children: workingFolder.name }), _jsxs("small", { children: [workingFolder.files.length, " ", workingFolder.files.length === 1 ? 'file' : 'files', " in context"] })] }), !workspace && (menuEntries.length > 1 || levels.length > 1) && (_jsx("div", { className: "gyro-model-picker", children: modelControls(false) })), tierNote && _jsx("p", { className: "gyro-model-note", role: "status", children: tierNote }), labAvailable && reportMode && (_jsxs("div", { className: "gyro-report-bar", role: "group", "aria-label": "Bug report", children: [_jsxs("span", { className: "gyro-report-label", children: [_jsx(Bug, { size: 13 }), " Reporting a bug to The Lab"] }), (captureMode === 'host' || canCaptureTab()) && (_jsxs("button", { type: "button", className: "gyro-btn ghost", onClick: () => void captureScreenshot(), disabled: capturing || busy || attachments.length >= ATTACHMENT_MAX_COUNT, children: [captureMode === 'host' ? _jsx(Monitor, { size: 13 }) : _jsx(Camera, { size: 13 }), " ", capturing ? 'Capturing…' : captureMode === 'host' ? 'Capture screen' : 'Capture this page'] })), _jsxs("button", { type: "button", className: "gyro-btn ghost", onClick: () => fileInputRef.current?.click(), disabled: busy || attachments.length >= ATTACHMENT_MAX_COUNT, children: [_jsx(Paperclip, { size: 13 }), " Add a screenshot"] }), _jsx("button", { type: "button", className: "gyro-btn ghost gyro-report-x", onClick: () => setReportMode(false), "aria-label": "Stop reporting a bug", title: "Stop reporting a bug", children: _jsx(X, { size: 12 }) })] })), (attachments.length > 0 || attachError || attachmentReads > 0) && (_jsxs("div", { className: "gyro-attach-tray", children: [attachments.map((a, i) => (_jsxs("div", { className: `gyro-attach-chip${a.kind !== 'image' ? ' gyro-attach-document' : ''}`, title: a.name, children: [a.kind !== 'image' ? _jsxs(_Fragment, { children: [_jsx(FileText, { size: 20, "aria-hidden": "true" }), _jsxs("span", { children: [a.name || 'File attachment', _jsx("small", { children: fileTypeLabel(a.name, a.mediaType) })] })] }) : _jsx("img", { src: `data:${a.mediaType};base64,${a.data}`, alt: a.name || 'attachment' }), _jsx("button", { type: "button", className: "gyro-attach-x", onClick: () => removeAttachment(i), "aria-label": `Remove ${a.name || 'file'}`, title: "Remove", children: _jsx(X, { size: 11 }) })] }, i))), attachmentReads > 0 && _jsx("span", { className: "gyro-attach-loading", role: "status", children: "Adding files\u2026" }), attachError && _jsx("span", { className: "gyro-attach-err", role: "alert", children: attachError })] })), queued.length > 0 && (_jsxs("div", { className: "gyro-queue", "aria-label": "Queued messages", children: [_jsx("span", { className: "gyro-queue-label", children: "Queued" }), queued.map((q, qi) => (_jsxs("span", { className: "gyro-queue-item", children: [_jsx("button", { type: "button", className: "gyro-queue-edit", title: "Edit this queued message", onClick: () => {
                                                                                setInput(q);
                                                                                setQueued((qq) => qq.filter((_, j) => j !== qi));
                                                                                inputRef.current?.focus();
                                                                            }, children: q.length > 42 ? q.slice(0, 41) + '…' : q }), _jsx("button", { type: "button", className: "gyro-queue-x", "aria-label": "Remove queued message", onClick: () => setQueued((qq) => qq.filter((_, j) => j !== qi)), children: "\u00D7" })] }, qi))), busy && (_jsx("button", { type: "button", className: "gyro-queue-send", title: "Stop the current reply and send your queued messages now", onClick: stopTurn, children: "Send now \u2192" }))] })), transcriptLoad.state !== 'ready' && transcriptLoad.sessionId === sessionIdRef.current && (_jsxs("div", { className: "gyro-transcript-load", role: transcriptLoad.state === 'failed' ? 'alert' : 'status', "data-state": transcriptLoad.state, children: [_jsx("span", { children: transcriptLoad.state === 'failed' ? 'This conversation could not be loaded. Nothing can be added to it until it loads.' : 'Loading this conversation…' }), transcriptLoad.state === 'failed' && _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => { const current = sessions.find(item => item.id === transcriptLoad.sessionId); if (current)
                                                                                void loadSession(current, { fresh: true }); }, children: "Retry" }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: newSession, children: "New chat" })] })] })), historySync && historySync.sessionId === sessionIdRef.current && transcriptLoad.state === 'ready' && (_jsxs("div", { className: "gyro-transcript-load", role: historySync.state === 'refused' ? 'alert' : 'status', "data-state": historySync.state === 'refused' ? 'failed' : 'unsaved', children: [_jsx("span", { children: historySync.message }), newChatHelps(historySync) && _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: newSession, children: "New chat" })] })), forkNotice && (forkNotice.sessionId === '' || forkNotice.sessionId === sessionIdRef.current) && (_jsxs("div", { className: "gyro-transcript-load gyro-fork-notice", role: "alert", "data-state": "failed", children: [_jsx("span", { children: forkNotice.message }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => setForkNotice(null), children: "Dismiss" })] })), speechInputState !== 'idle' && _jsx(VoiceRecordingBar, { state: speechInputState, meter: voiceMeter, onCancel: () => { recognitionRef.current?.cancel(); requestAnimationFrame(() => inputRef.current?.focus()); }, onFinish: () => recognitionRef.current?.stop() }), _jsxs("form", { hidden: !workspace && speechInputState !== 'idle', className: `gyro-input${dragOver ? ' drag-over' : ''}`, onSubmit: (e) => {
                                                                e.preventDefault();
                                                                if (speechInputState !== 'idle')
                                                                    return;
                                                                // A slash command (slashCommands.ts) runs here and never reaches the model, busy or not.
                                                                const slashHit = slashToRun();
                                                                if (slashHit) {
                                                                    runSlash(slashHit.command, slashHit.args);
                                                                    return;
                                                                }
                                                                clearVoiceInputOff(); // also when the message is only queued below, which does not go through send
                                                                recognitionRef.current?.cancel();
                                                                if (attachmentPendingRef.current > 0) {
                                                                    setAttachError('Wait for your files to finish loading before sending.');
                                                                    return;
                                                                }
                                                                // While Gyro is responding, Enter QUEUES the message (steer) instead of sending.
                                                                if (busy) {
                                                                    const text = input.trim();
                                                                    if (text) {
                                                                        setQueued((q) => [...q, text]);
                                                                        setInput('');
                                                                    }
                                                                    return;
                                                                }
                                                                const a = attachmentsRef.current;
                                                                attachmentsRef.current = [];
                                                                setAttachments([]);
                                                                setAttachError('');
                                                                // Host opted into priming on the first Send: the submit is a user gesture, so asking
                                                                // here is legitimate (L-27). Once per mount, and only while the decision is pending.
                                                                if (notificationsMode === 'prompt-on-send' && notifPerm === 'default' && !primedNotificationsRef.current) {
                                                                    primedNotificationsRef.current = true;
                                                                    enableNotifications();
                                                                }
                                                                void send(input, a);
                                                            }, onDragOver: (e) => {
                                                                if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
                                                                    e.preventDefault();
                                                                    setDragOver(true);
                                                                }
                                                            }, onDragLeave: () => setDragOver(false), onDrop: (e) => {
                                                                e.preventDefault();
                                                                setDragOver(false);
                                                                const files = Array.from(e.dataTransfer?.files || []);
                                                                if (files.length)
                                                                    void addFiles(files);
                                                            }, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ATTACHMENT_ACCEPT.join(','), multiple: true, hidden: true, onChange: (e) => {
                                                                        const files = Array.from(e.target.files || []);
                                                                        if (files.length)
                                                                            void addFiles(files);
                                                                        e.target.value = '';
                                                                    } }), _jsxs("div", { className: "gyro-composer-file-controls", children: [desktopWorkspace ? _jsxs(_Fragment, { children: [_jsx(LocalFilesMenu, { ref: localFilesMenuRef, showTrigger: false, onSnapshot: receiveLocalFiles, prompt: input, disabled: busy || speechInputState !== 'idle' || attachmentReads > 0 }, userKey), _jsx(ComposerAddMenu, { disabled: speechInputState !== 'idle' || hostVisibility === 'hidden', attachDisabled: busy || attachmentReads > 0 || attachments.length >= ATTACHMENT_MAX_COUNT, onAttach: () => fileInputRef.current?.click(), onOpenChange: setComposerMenuOpen, onOpenLocalFiles: localFilesSnapshot && !busy && attachmentReads === 0 ? returnFocus => localFilesMenuRef.current?.open(returnFocus) : undefined, localFilesPaused: localFilesSnapshot?.paused === true, report: labAvailable ? { active: reportMode, disabled: (busy && !reportMode) || (knowledgeMode === 'world' && !reportMode), title: reportMode ? 'Stop reporting a bug' : knowledgeMode === 'world' ? 'Choose Work IQ to report a bug' : 'Report a bug to The Lab', onToggle: () => (reportMode ? setReportMode(false) : startBugReport()) } : undefined, onOpenSettings: openSettings, accent: preferences.accent, onAccentChange: accent => updatePreferences({ accent }), skills: (desktopSkills.snapshot?.skills ?? []).filter(skill => skill.status === 'available' && skill.userInvocable), onUseSkill: name => { setInput(current => `/${name} ${current}`); requestAnimationFrame(() => inputRef.current?.focus()); }, gyroSkills: skillsStatus ? { state: gyroSkills.state, error: gyroSkills.error, showPersonal: skillsStatus.personal && gyroSkills.verified === true, showCompany: skillsStatus.company, personal: gyroSkills.personal, company: gyroSkills.company } : undefined, onUseGyroSkill: skillsStatus ? name => { setInput(current => `run ${name} ${current}`); requestAnimationFrame(() => inputRef.current?.focus()); } : undefined, onSaveSkill: saveSkillOn ? () => setSkillBox({ key: 'composer', body: input.trim() || lastSkillSeed }) : undefined, connectors: [...taskConnectors, ...(desktopMcp.snapshot?.servers ?? []).map(server => ({ id: `desktop:${server.name}`, displayName: server.displayName ?? server.name, connected: currentMcpStatus(server, desktopMcp.now) === 'healthy' }))], connectorsState: taskConnectorsState }, `add:${userKey}`), _jsxs("div", { className: "gyro-composer-workspace-switch", role: "group", "aria-label": "Workspace layout", children: [_jsx("button", { type: "button", "aria-pressed": composerWorkspace === 'chat', onClick: () => { setComposerWorkspace('chat'); setTaskPanelOpen(false); }, title: "Conversation workspace", children: "Chat" }), _jsx("button", { type: "button", "aria-pressed": composerWorkspace === 'cowork', onClick: () => setComposerWorkspace('cowork'), title: "Folders and task progress inside Gyro", children: "Cowork" })] })] }) : _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "gyro-btn ghost", onClick: () => fileInputRef.current?.click(), disabled: busy || speechInputState !== 'idle' || attachments.length >= ATTACHMENT_MAX_COUNT, title: "Add files or photos", "aria-label": "Add files or photos", children: _jsx(Plus, { size: 18 }) }), skillsListable(skillsStatus, gyroSkills) && (_jsx("button", { type: "button", className: `gyro-btn ghost${skillsPanelOpen ? ' active' : ''}`, onClick: () => setSkillsPanelOpen(current => !current), title: "Skills", "aria-label": "Skills", "aria-expanded": skillsPanelOpen, children: _jsx(Bookmark, { size: 15 }) })), labAvailable && (_jsx("button", { type: "button", className: `gyro-btn ghost${reportMode ? ' active' : ''}`, onClick: () => (reportMode ? setReportMode(false) : startBugReport()), disabled: (busy && !reportMode) || speechInputState !== 'idle' || (knowledgeMode === 'world' && !reportMode), title: reportMode ? 'Stop reporting a bug' : knowledgeMode === 'world' ? 'Choose Work IQ to report a bug' : 'Report a bug to The Lab', "aria-label": reportMode ? 'Stop reporting a bug' : 'Report a bug', "aria-pressed": reportMode, children: _jsx(Bug, { size: 15 }) }))] }), workspace && modelControls(speechInputState !== 'idle'), workspace && waitingOn.length > 0 && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-stop-waiting", onClick: () => stopWaiting(waitingOn[0].actionId, 'person'), title: `Stop waiting for the result of ${shownMcpName(waitingOn[0].tool)} on ${mcpServerLabel(waitingOn[0].server, mcpServersRef.current)}; nothing is run again`, children: "Stop waiting" })] }), !workspace && voiceInput !== 'off' && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-mic", onClick: startListening, title: "Talk to Gyro (Ctrl/Cmd+Shift+V)", "aria-label": "Talk to Gyro", children: _jsx(Mic, { size: 15 }) }), _jsxs("div", { className: "gyro-textinput-wrap", "data-stop": busy || undefined, children: [slashOpen && slash && (_jsxs("div", { className: "gyro-slash-menu", id: `gyro-slash-list-${appKey}`, role: "listbox", "aria-label": "Slash commands", children: [rankedSlash.length === 0 && _jsxs("p", { className: "gyro-palette-empty", children: ["No command matches \"/", slash.word, "\". Enter sends it as a message."] }), rankedSlash.map((command, i) => (_jsxs("button", { id: `gyro-slash-opt-${command.id}`, type: "button", role: "option", "aria-selected": command === activeSlash, tabIndex: -1, className: `gyro-palette-item${command === activeSlash ? ' active' : ''}`, onMouseEnter: () => setSlashIndex(i), onMouseDown: (e) => e.preventDefault(), onClick: () => runSlash(command, slashArgsFor(command)), children: [_jsxs("span", { className: "gyro-palette-label", children: ["/", command.name, command.usage && _jsxs("span", { className: "gyro-slash-usage", children: [" ", command.usage] })] }), _jsx("span", { className: "gyro-palette-hint", children: command.description })] }, command.id))), _jsx("div", { className: "gyro-palette-foot", "aria-hidden": "true", children: "\u2191\u2193 navigate \u00B7 Enter run \u00B7 Tab complete \u00B7 Esc close" })] })), _jsx("textarea", { ref: inputRef, rows: 1, className: "gyro-textinput", placeholder: busy ? 'Queue a message to steer… (Enter)' : 'Ask Gyro…', value: input, disabled: speechInputState !== 'idle', onChange: (e) => { recognitionRef.current?.cancel(); clearVoiceInputOff(); setInput(e.target.value); setSlashDismissed(false); setSlashIndex(0); }, onFocus: () => setComposerFocused(true), onBlur: () => setComposerFocused(false), "aria-autocomplete": slashOpen ? 'list' : undefined, "aria-controls": slashOpen ? `gyro-slash-list-${appKey}` : undefined, "aria-activedescendant": slashOpen && activeSlash ? `gyro-slash-opt-${activeSlash.id}` : undefined, onKeyDown: (e) => {
                                                                                if (slashOpen && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                                                                                    // The menu's keys, as the palette has them: up/down move, Tab completes the word, Esc closes the menu (the text stays).
                                                                                    if (e.key === 'ArrowDown' && rankedSlash.length) {
                                                                                        e.preventDefault();
                                                                                        setSlashIndex((i) => (Math.min(i, rankedSlash.length - 1) + 1) % rankedSlash.length);
                                                                                        return;
                                                                                    }
                                                                                    if (e.key === 'ArrowUp' && rankedSlash.length) {
                                                                                        e.preventDefault();
                                                                                        setSlashIndex((i) => (Math.min(i, rankedSlash.length - 1) - 1 + rankedSlash.length) % rankedSlash.length);
                                                                                        return;
                                                                                    }
                                                                                    if (e.key === 'Tab' && !e.shiftKey && activeSlash) {
                                                                                        e.preventDefault();
                                                                                        setInput(`/${activeSlash.name} `);
                                                                                        setSlashIndex(0);
                                                                                        return;
                                                                                    }
                                                                                    if (e.key === 'Escape') {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        setSlashDismissed(true);
                                                                                        return;
                                                                                    } // the panel's own Escape must not also close the widget
                                                                                }
                                                                                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                                                                                    e.preventDefault();
                                                                                    e.currentTarget.form?.requestSubmit();
                                                                                }
                                                                            }, onPaste: (e) => {
                                                                                const files = Array.from(e.clipboardData?.items || [])
                                                                                    .filter((it) => it.kind === 'file')
                                                                                    .map((it) => it.getAsFile())
                                                                                    .filter((f) => Boolean(f));
                                                                                if (files.length) {
                                                                                    e.preventDefault();
                                                                                    void addFiles(files);
                                                                                }
                                                                            }, "aria-label": "Message Gyro" }), busy && _jsx("button", { type: "button", className: "gyro-btn gyro-stop", onClick: stopTurn, "aria-label": "Stop", title: "Stop", children: _jsx(Square, { size: 16, "aria-hidden": "true" }) })] }), workspace ? _jsxs("div", { className: "gyro-composer-voice-controls", children: [voiceInput !== 'off' && _jsx("button", { type: "button", className: "gyro-btn ghost gyro-mic", onClick: startListening, disabled: speechInputState === 'starting' || speechInputState === 'stopping', title: "Dictate a message (Ctrl/Cmd+Shift+V)", "aria-label": "Dictate a message", children: _jsx(Mic, { size: 20, "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: "gyro-btn gyro-voice-mode-button", disabled: speechInputState !== 'idle', "aria-label": "Voice mode", "aria-pressed": voiceOn, "data-active": voiceOn || undefined, "data-speaking": speechState === 'playing' || undefined, "data-listening": speechInputState === 'listening' || undefined, title: voiceOn ? 'Turn off spoken replies' : 'Turn on spoken replies', onClick: () => switchMode(voiceOn ? 'detailed' : 'voice'), children: _jsx(AudioLines, { size: 23, "aria-hidden": "true" }) }), _jsx("button", { className: "gyro-btn gyro-composer-send", type: "submit", disabled: speechInputState !== 'idle' || attachmentReads > 0 || (!input.trim() && (busy || attachments.length === 0)), "aria-label": "Send", title: busy ? 'Queue message' : 'Send message', children: _jsx(Send, { size: 17, "aria-hidden": "true" }) })] }) : !busy && (_jsx("button", { className: "gyro-btn", type: "submit", disabled: attachmentReads > 0 || (!input.trim() && attachments.length === 0), "aria-label": "Send", children: _jsx(Send, { size: 15 }) }))] }), desktopWorkspace && composerWorkspace === 'cowork' && _jsx(DesktopCoworkBar, { localFiles: localFilesSnapshot, workingFolder: workingFolder, disabled: busy || speechInputState !== 'idle' || attachmentReads > 0, onConnect: scope => localFilesMenuRef.current?.connect(scope), onDisconnect: id => localFilesMenuRef.current?.revoke(id), onManageFolders: () => localFilesMenuRef.current?.open(), onOpenTaskContext: () => { if (compactDesktop)
                                                                setSidebarOpen(false); setFolderContextRequested(true); setTaskPanelOpen(true); }, onOpenProgress: () => { if (compactDesktop)
                                                                setSidebarOpen(false); setTaskPanelOpen(true); } }), voiceError && _jsx("div", { className: "gyro-attachments", role: "alert", children: _jsx("span", { className: "gyro-attach-err", children: voiceError }) }), workspace && (speechInputState !== 'idle' || (composerFocused && !input.trim())) && _jsx("p", { className: "gyro-desktop-composer-note", children: speechInputState !== 'idle' ? _jsxs(_Fragment, { children: ["Stop to transcribe ", _jsx("span", { "aria-hidden": "true", children: "\u00B7" }), " Escape to cancel"] }) : _jsxs(_Fragment, { children: [voiceOn && _jsxs(_Fragment, { children: ["Spoken replies on ", _jsx("span", { "aria-hidden": "true", children: "\u00B7" }), " "] }), "Enter to send ", _jsx("span", { "aria-hidden": "true", children: "\u00B7" }), " Shift+Enter for a new line ", _jsx("span", { "aria-hidden": "true", children: "\u00B7" }), " Ctrl+K for commands"] }) }), preferences.showTokenCounter && sessionUsage.turns > 0 && _jsx(SessionUsageLine, { usage: sessionUsage, live: messages.find(message => (message.streaming || message.pending) && message.liveUsage)?.liveUsage })] })] }))] }), sidePanelShown && sidePanel.activeTab && _jsx(GyroSidePanel, { id: `gyro-side-panel-${appKey}`, tabs: sidePanel.tabs, activeTab: sidePanel.activeTab, onSelectTab: (id) => sidePanelApi.open(id), fullScreen: sidePanelFullScreen, onFullScreen: sidePanel.setFullScreen, onClose: sidePanelApi.close, fraction: sidePanel.fraction, onFraction: sidePanel.setFraction }), desktopWorkspace && !showPets && !showNotifications && !sidePanelShown && _jsxs("div", { ref: taskPanelRef, style: { display: 'contents' }, children: [_jsx("button", { type: "button", ref: taskToggleRef, className: "gyro-task-edge-toggle", "aria-label": taskPanelOpen ? 'Hide task details' : 'Show task details', title: taskPanelOpen ? 'Collapse task details' : 'Progress, working folder, and context', "aria-expanded": taskPanelOpen, "aria-controls": `gyro-task-panel-${appKey}`, onClick: () => { if (compactDesktop)
                                                setSidebarOpen(false); setTaskPanelOpen(value => !value); }, children: taskPanelOpen ? _jsx(ChevronRight, { "aria-hidden": "true" }) : _jsx(ChevronLeft, { "aria-hidden": "true" }) }), taskDrawerOpen && _jsx("button", { className: "gyro-task-dismiss", tabIndex: -1, "aria-label": "Dismiss task details", onClick: closeTaskPanel }), taskPanelOpen && _jsx(DesktopTaskPanel, { id: `gyro-task-panel-${appKey}`, activity: taskActivity ? reconcileTaskActions(taskActivity, taskActions.map((action) => { const outcome = resolved[action.id]; return { ...action, status: outcome === 'error' ? 'failed' : outcome ?? action.status }; })) : null, busy: busy, folder: workingFolder, knowledgeMode: knowledgeMode, showFolderContext: folderContextRequested, onFolderContextShown: folderContextShown, onFolderChange: setWorkingFolder, connectors: [...taskConnectors, ...(desktopMcp.snapshot?.servers ?? []).map(server => ({ id: `desktop:${server.name}`, displayName: server.displayName ?? server.name, configured: true, connected: currentMcpStatus(server, desktopMcp.now) === 'healthy' }))], connectorsState: taskConnectorsState, onRefreshConnectors: () => setTaskConnectorsRevision(value => value + 1), sources: taskSources, client: client, artifacts: taskArtifacts, resolveLocalArtifact: documentStore.resolve, runtime: taskRuntime, agent: agentStatus, attachments: [...new Set([...taskAttachments, ...attachments.map(attachment => attachment.name || (attachment.kind === 'image' ? 'Image attachment' : 'File attachment'))])], onOpenConnectors: () => openSettings('connectors'), personConnect: personConnect }, `${userKey}:${taskContextEpoch}`)] })] }), chatMenu && _jsx(ConversationContextMenu, { position: chatMenu, chatTitle: chatMenu.session.title, returnFocus: chatMenu.opener, onRename: () => renameSessionFromMenu(chatMenu.session), onOpenNewSession: () => openFreshSession(chatMenu.session), onDelete: () => removeChatFromMenu(chatMenu.session.id), onClose: () => setChatMenu(null), topLayer: floatingWorkspace }, `${chatMenu.session.id}:${chatMenu.key}`), desktopWorkspace && settingsSection && _jsx(DesktopSettings, { initialSection: settingsSection, onClose: closeSettings, client: client, preferences: preferences, localMcp: desktopMcp, localSkills: desktopSkills, onPreferencesChange: updatePreferences, onResetPreferences: () => updatePreferences({ ...DEFAULT_DESKTOP_PREFERENCES }), preferenceStorageError: preferenceStorageError, user: props.user, organizationName: "Exante360", conversationCount: sessions.length, capabilities: capabilities, models: models, configured: configured, unreachable: unreachable, appKey: appKey, responseMode: mode, onResponseModeChange: switchMode, personConnect: personConnect, notificationPermission: props.nativeNotifications ? 'granted' : notifPerm, notificationsEnabled: notificationsMode !== false, onRequestNotifications: enableNotifications, allowShare: props.allowShare !== false, mascotSrc: builtInAvatar, mascotAnimations: mascotAnimations, pets: petsEnabled ? { ownerKey: `${userKey}:${petSessionEpoch}` } : undefined, attention: attention.settings, mascotShareHide: mascotShareHide, skillsStatus: skillsStatus, gyroSkills: gyroSkills }, `${userKey}:${hostSettingsRequests}`), webPage && outfitsOpen && _jsx(WebOutfitsWindow, { Gallery: DesktopMascotLooks, client: client, builtInSrc: builtInAvatar, onClose: closeOutfits }), paletteOpen && (_jsx("div", { className: "gyro-palette-overlay", onClick: closePalette, children: _jsxs("div", { className: "gyro-palette", role: "dialog", "aria-label": "Gyro command palette", onClick: (e) => e.stopPropagation(), children: [_jsx("input", { ref: paletteInputRef, className: "gyro-textinput gyro-palette-input", role: "combobox", "aria-expanded": "true", "aria-controls": `gyro-palette-list-${appKey}`, "aria-activedescendant": activeEntry ? `gyro-pal-opt-${activeEntry.id}` : undefined, "aria-autocomplete": "list", "aria-label": "Search commands and suggestions", placeholder: "Type a command or question\u2026", value: paletteQuery, onChange: (e) => {
                                            setPaletteQuery(e.target.value);
                                            setPaletteIndex(0);
                                        }, onKeyDown: onPaletteKeyDown }), _jsxs("div", { className: "gyro-palette-list", id: `gyro-palette-list-${appKey}`, role: "listbox", "aria-label": "Commands and suggestions", children: [rankedPalette.length === 0 && _jsxs("p", { className: "gyro-palette-empty", children: ["Nothing matches \"", paletteQuery, "\"."] }), rankedPalette.map((entry, i) => (_jsxs(Fragment, { children: [(i === 0 || rankedPalette[i - 1].section !== entry.section) && (_jsx("div", { className: "gyro-palette-section", role: "presentation", children: entry.section })), _jsxs("button", { id: `gyro-pal-opt-${entry.id}`, type: "button", role: "option", "aria-selected": entry === activeEntry, tabIndex: -1, className: `gyro-palette-item${entry === activeEntry ? ' active' : ''}`, onMouseEnter: () => setPaletteIndex(i), onClick: () => runPaletteEntry(entry), ref: entry === activeEntry ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined, children: [_jsx("span", { className: "gyro-palette-label", children: entry.label }), entry.hint && _jsx("span", { className: "gyro-palette-hint", children: entry.hint })] })] }, entry.id)))] }), _jsx("div", { className: "gyro-palette-foot", "aria-hidden": "true", children: "\u2191\u2193 navigate \u00B7 Enter run \u00B7 Esc close" })] }) }))] }))] }) }));
}
/** The exact images a Lab write will upload after confirm — shown on every card that carries screenshots. */
function LabScreenshotRow({ screenshots }) {
    return (_jsxs("div", { className: "gyro-lab-card", children: [_jsxs("div", { className: "gyro-lab-card-title", children: [_jsx(Camera, { size: 12 }), " Screenshots that will be attached"] }), screenshots.length ? (_jsxs("span", { className: "gyro-lab-shots", children: [screenshots.map((sh, i) => _jsx("img", { src: `data:${sh.mediaType};base64,${sh.data}`, alt: sh.name || 'screenshot', className: "gyro-msg-thumb" }, i)), _jsxs("small", { children: [screenshots.length, " image", screenshots.length === 1 ? '' : 's'] })] })) : _jsx("small", { className: "gyro-faint", children: "none" })] }));
}
// ---------------- The bug-report card body (§1.5): what will be filed, where, and as whom ----------------
function LabReportCard({ input, preview, screenshots }) {
    const text = (v, max = 600) => (typeof v === 'string' && v.trim() ? (v.length > max ? `${v.slice(0, max)}…` : v) : undefined);
    const context = input.context && typeof input.context === 'object' && !Array.isArray(input.context) ? input.context : {};
    const where = [text(input.route, 200), text(input.appVersion, 80), text(context.host, 120), text(context.process, 80), [text(context.browser, 40), text(context.os, 60)].filter(Boolean).join(' / ') || undefined]
        .filter(Boolean)
        .join(' · ');
    const steps = Array.isArray(input.steps) ? input.steps.filter((x) => typeof x === 'string' && x.trim().length > 0).slice(0, 10) : [];
    const q = preview?.quota;
    const full = q ? q.openUsed >= q.openMax || q.dayUsed >= q.dayMax : false;
    const rows = [
        ['App', `${preview?.appTitle ? `${preview.appTitle} ` : ''}(${text(input.appId, 80) ?? '?'})`],
        ['Summary', text(input.summary, 200) ?? '—'],
        ['What happened', text(input.whatHappened) ?? '—'],
    ];
    if (steps.length)
        rows.push(['Steps', _jsx("ol", { className: "gyro-list", children: steps.map((st, i) => _jsx("li", { children: st.slice(0, 300) }, i)) }, "steps")]);
    if (text(input.expected))
        rows.push(['Expected', text(input.expected)]);
    if (text(input.kind, 20) && input.kind !== 'bug')
        rows.push(['Kind', text(input.kind, 20)]);
    if (text(input.severityHint, 20))
        rows.push(['Severity hint', text(input.severityHint, 20)]);
    if (where)
        rows.push(['Context', where]);
    rows.push(['Screenshot', screenshots.length
            ? (_jsxs("span", { className: "gyro-lab-shots", children: [screenshots.map((sh, i) => _jsx("img", { src: `data:${sh.mediaType};base64,${sh.data}`, alt: sh.name || 'screenshot', className: "gyro-msg-thumb" }, i)), _jsxs("small", { children: [screenshots.length, " image", screenshots.length === 1 ? '' : 's', preview?.evidenceAudience ? ` · seen by ${preview.evidenceAudience}` : ''] })] }, "shots"))
            : 'none']);
    if (preview?.filedAs)
        rows.push(['Filed as', preview.filedAs]);
    if (q)
        rows.push(['Your reports', _jsxs("span", { className: full ? 'gyro-warn' : undefined, children: [q.openUsed, " of ", q.openMax, " waiting for review \u00B7 ", q.dayUsed, " of ", q.dayMax, " today", full ? ' — limit reached, The Lab will refuse this one' : ''] }, "quota")]);
    return (_jsxs("div", { className: "gyro-lab-card", children: [_jsxs("div", { className: "gyro-lab-card-title", children: [_jsx(Bug, { size: 12 }), " File a bug in The Lab"] }), _jsx("dl", { children: rows.map(([label, value]) => (_jsxs(Fragment, { children: [_jsx("dt", { children: label }), _jsx("dd", { children: value })] }, label))) }), preview?.error && _jsxs("small", { className: "gyro-faint", children: ["The Lab's preview is unavailable (", preview.error.slice(0, 120), ")."] })] }));
}
/** Keep the original widget DOM unchanged unless the desktop host opts into the workspace. */
function DesktopBackdrop({ workspace, disabled, children }) {
    return workspace ? _jsx("div", { className: "gyro-desktop-content", ...inertProps(disabled), style: { display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }, children: children }) : _jsx(_Fragment, { children: children });
}
function desktopStorage() {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    }
    catch {
        return null;
    }
}
function AssistantSurface({ workspace, drawerOpen, children }) {
    return workspace ? _jsx("div", { className: "gyro-desktop-main", ...inertProps(drawerOpen), children: children }) : _jsx(_Fragment, { children: children });
}
/**
 * The welcome. Its title (dayGreeting.ts welcomeHeading) is plain text, never Markdown: the usual title, or on a day
 * outfit's day that day's greeting. `headingLevel` 2 on a host page, which owns its own h1 (the floating panel). The
 * compact layout has no title, so a day's greeting shows as one line above its starters.
 */
function WelcomeSurface({ workspace, avatar, onAvatarError, heading, headingLevel = 1, greeting, nickname, children }) {
    if (!workspace)
        return _jsxs(_Fragment, { children: [heading.day && _jsx("p", { className: "gyro-compact-day-greeting", children: heading.text }), _jsx("div", { className: "gyro-suggest", children: children })] });
    const Title = headingLevel === 2 ? 'h2' : 'h1';
    return (_jsxs("div", { className: "gyro-desktop-welcome", children: [_jsx("img", { src: avatar, alt: "", onError: onAvatarError }), _jsx("span", { className: "gyro-desktop-welcome-eyebrow", children: nickname?.trim() ? `Welcome, ${nickname.trim()}` : 'Your Exante360 assistant' }), _jsx(Title, { className: "gyro-desktop-welcome-title", "data-day": heading.day ?? undefined, children: heading.text }), _jsx("div", { className: "gyro-desktop-welcome-description", children: _jsx(Markdown, { text: greeting }) }), _jsx("div", { className: "gyro-suggest", children: children })] }));
}
//# sourceMappingURL=GyroAssistant.js.map